import React from "react";
import ModuleHeaderMenu from "../components/ModuleHeaderMenu";
import { FeedBagIcon, GenericRationBagIcon, BovineRationBagIcon, SheepHeadIcon, BovineHeadIcon, HorseIcon, CattleIcon, ChickenHeadIcon, ChickenLotsIcon } from "../lib/icons";
import { buildNextRecordNumber } from "../lib/recordNumberUtils";
import { matchesRecordNumberSearch } from "../lib/recordNumberSearch";
import { appendProvisionalSuffix, appendUpdatePendingDisplaySuffix, markOperationalCreatePending, markOperationalUpdatePending } from "../lib/operationalProvisional";
import { resolveBovineCategoryByAgeMonths, resolveEquineCategoryByAgeMonths } from "../lib/animalCategoryRules";
import { ensureAnimalRegistryFields, ensureLotRegistryFields, createIndividualizationEvent, createMicrochipAuditEvent } from "../lib/livestockRegistry";
import { syncLivestockRegistrySnapshot, fetchLivestockRegistrySummary } from "../lib/livestockRegistryApi";
import { getNativeSafeAsset } from "../lib/platformAssets";

const bovineCalfIcon = "/icons/bovinos/bezerro.png";
const bovineYoungIcon = "/icons/bovinos/boi_jovem.png";
const bovineAdultIcon = "/icons/bovinos/boi.png";
const bovineFatIcon = "/icons/bovinos/boi_gordo.png";
const ovineDorperCardIcon = getNativeSafeAsset("/icons/ovinos/dorper-card.webp");
const equineMarchadorCardIcon = getNativeSafeAsset("/icons/equinos/mangalarga-marchador-card.webp");
const equineRationCardIcon = getNativeSafeAsset("/icons/equinos/saco-racao-equinos-card.webp");
const genericRationCardIcon = getNativeSafeAsset("/icons/shared/saco-racao-generico-card.webp");

const bovineCardImageStyle = {
  width: 56,
  height: 56,
  objectFit: "contain",
  display: "block",
};

function renderCardImageIcon(src, alt){
  return <img src={src} alt={alt} loading="eager" decoding="async" fetchpriority="high" style={bovineCardImageStyle} />;
}

function AnimalInlinePanel({ title, onClose, children, tone="default", theme }){
  const accents = {
    default: { border: "rgba(59,130,246,.24)", bg: "rgba(59,130,246,.06)", title: "#3b82f6" },
    warning: { border: "rgba(245,158,11,.28)", bg: "rgba(245,158,11,.08)", title: "#f59e0b" },
    success: { border: "rgba(34,197,94,.24)", bg: "rgba(34,197,94,.06)", title: "#22c55e" },
    danger: { border: "rgba(239,68,68,.24)", bg: "rgba(239,68,68,.06)", title: "#ef4444" },
  };
  const style = accents[tone] || accents.default;
  const bg = theme?.card || '#fff';
  const contentBg = theme?.card2 || theme?.card || '#fff';
  const text = theme?.text || '#0f172a';
  const muted = theme?.muted || '#64748b';
  const shadow = theme?.bg ? '0 12px 34px rgba(0,0,0,.28)' : '0 10px 30px rgba(15,23,42,.08)';
  return (
    <div style={{marginTop:18,background:bg,border:`1px solid ${style.border}`,borderRadius:16,overflow:'hidden',boxShadow:shadow}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:12,padding:'14px 16px',background:contentBg,borderBottom:`1px solid ${style.border}`}}>
        <div style={{fontWeight:800,color:style.title}}>{title}</div>
        <button type="button" onClick={onClose} style={{border:'none',background:'transparent',cursor:'pointer',fontWeight:800,color:style.title,fontSize:14}}>Fechar</button>
      </div>
      <div style={{padding:16,display:'grid',gap:14,background:bg,color:text}}>{children}</div>
      <div style={{padding:'0 16px 14px',fontSize:11,color:muted,background:bg}} />
    </div>
  );
}
function InlineModal({ open, onClose, title, children, tone="default", theme }){
  if(!open) return null;
  return <AnimalInlinePanel theme={theme} tone={tone} onClose={onClose} title={title}>{children}</AnimalInlinePanel>;
}

export function createAnimalsModule(deps){
  const {
    useState,
    useEffect,
    makeSeed,
    useSysLists,
    useFarm,
    useFarmCollection,
    useGlobalCollection,
    useToast,
    useBgSyncToast,
    useReliableSyncOnSave,
    getAllowedCostCentersForModule,
    getDeniedCostCentersForModule,
    itemMatchesCostCenterAccess,
    canCreateInModule,
    canEditInModule,
    moduleId = "animals",
    moduleLabel = "Pecuária",
    speciesFilter = null,
    defaultSpeciesOverride = "",
    T,
    COST_CENTERS,
    CC_COLORS,
    ANIMAL_SPECIES,
    ANIMAL_SEX,
    ANIMAL_STATUS,
    AGE_RANGES,
    LOT_PURPOSES,
    FEED_TYPES,
    SUPPLEMENT_TYPES,
    WEIGHT_MODES,
    today,
    fmtDate,
    fmtMoney,
    isCompactViewport,
    createClientId,
    makeCreateMeta,
    makeUpdateMeta,
    setEditingFlag,
    Btn,
    Field,
    Input,
    Select,
    Textarea,
    StatCard,
    Toast,
    Badge,
    CCBadge,
    Table,
    LotDetailModal,
  } = deps;

  return function AnimalsModule({ session }){
  const seed = makeSeed();
  const SL = useSysLists();
  const [animals,        setAnimals]        = useFarmCollection("animals",        seed.animals);
  const [lots,           setLots]           = useFarmCollection("animalLots",     []);
  const [stockItems,]                      = useGlobalCollection("stock",          seed.stock || []);
  const [stockMovements, setStockMovements]= useFarmCollection("stockMovements", []);
  const [, setFinancialEntries] = useFarmCollection("financial", seed.financial || []);
  const [pastures,]                         = useFarmCollection("pastures",       []);
  const [animalBuyers, setAnimalBuyers] = useGlobalCollection("animalBuyers", SL.ANIMAL_BUYER_DEFAULTS || []);
  const [serviceNutritionEntries, setServiceNutritionEntries] = useFarmCollection("animalServiceNutrition", []);
  const [foalNutritionEntries, setFoalNutritionEntries] = useFarmCollection("animalFoalNutrition", []);
  const [lotIndividualizations, setLotIndividualizations] = useFarmCollection("lotIndividualizations", []);
  const [microchipAudit, setMicrochipAudit] = useFarmCollection("microchipAudit", []);
  const [tab,     setTab]     = useState("animals");
  const [speciesTab, setSpeciesTab] = useState("");
  const [form,    setForm]    = useState(null);
  const [lotForm, setLotForm] = useState(null);
  const [detailLot, setDetailLot] = useState(null);
  const [lotFormFocusSection, setLotFormFocusSection] = useState("");
  const [lotNutritionAuditRange, setLotNutritionAuditRange] = useState({ start:"", end:"" });
  const [detailAnimal, setDetailAnimal] = useState(null);
  const [filterCC, setFcc]   = useState("Todos");
  const [search,   setSearch]= useState("");
  const [birdLotForm, setBirdLotForm] = useState(null);
  const [birdNutritionForm, setBirdNutritionForm] = useState(null);
  const [birdMedicationForm, setBirdMedicationForm] = useState(null);
  const [birdMortalityForm, setBirdMortalityForm] = useState(null);
  const [birdEggForm, setBirdEggForm] = useState(null);
  const [birdEggInfoOpen, setBirdEggInfoOpen] = useState(false);
  const [birdTransferForm, setBirdTransferForm] = useState(null);
  const [lotNutritionForm, setLotNutritionForm] = useState(null);
  const [lotMedicationForm, setLotMedicationForm] = useState(null);
  const [foalForm, setFoalForm] = useState(null);
  const [serviceAnimalForm, setServiceAnimalForm] = useState(null);
  const [serviceAnimalDetail, setServiceAnimalDetail] = useState(null);
  const [serviceMedicationForm, setServiceMedicationForm] = useState(null);
  const [serviceNutritionForm, setServiceNutritionForm] = useState(null);
  const [individualizeForm, setIndividualizeForm] = useState(null);
  const [foalMedicationForm, setFoalMedicationForm] = useState(null);
  const [foalNutritionForm, setFoalNutritionForm] = useState(null);
  const [toast,    show]     = useToast();
  const [registrySummary, setRegistrySummary] = useState(null);
  const trackedLotMembershipMap = React.useMemo(()=> {
    const map = new Map();
    (animals||[]).forEach(item=>{
      const lotKey = String(item?.currentLotId || item?.lotId || "").trim();
      if(!lotKey || item?.deletedAt) return;
      const arr = map.get(lotKey) || [];
      arr.push(item);
      map.set(lotKey, arr);
    });
    return map;
  }, [animals]);
  const registrySyncRef = React.useRef({ timer:null, running:false, signature:"" });
  useBgSyncToast("animals", show);
  const syncOnSave = useReliableSyncOnSave();
  const syncOnSaveRef = React.useRef(syncOnSave);
  const lotNutritionSectionRef = React.useRef(null);
  React.useEffect(() => { syncOnSaveRef.current = syncOnSave; }, [syncOnSave]);
  const { fid: currentFarmId, farm } = useFarm();
  const allowedCostCenters = React.useMemo(()=>getAllowedCostCentersForModule(session, moduleId), [session]);
  const deniedCostCenters = React.useMemo(()=>getDeniedCostCentersForModule(session, moduleId), [session]);
  const canEdit = !session || session.role==="admin" || session.role==="gestor" || canEditInModule(session, moduleId) || canCreateInModule(session, moduleId);

  React.useEffect(()=>{
    if(lotForm && lotFormFocusSection === "nutrition" && lotNutritionSectionRef.current){
      requestAnimationFrame(()=>lotNutritionSectionRef.current?.scrollIntoView?.({ behavior:"smooth", block:"start" }));
    }
  }, [lotForm, lotFormFocusSection]);

  const activeBreeds   = SL.ANIMAL_BREEDS || [];
  const activePurposes = SL.LOT_PURPOSES || LOT_PURPOSES;
  const bovineWeightClassRules = React.useMemo(()=>{
    const base = Array.isArray(SL.BOVINE_WEIGHT_CLASS_RULES) && SL.BOVINE_WEIGHT_CLASS_RULES.length ? SL.BOVINE_WEIGHT_CLASS_RULES : [
      { label:"Abaixo de 17@", minAt:"0", maxAt:"17", color:"#EF4444" },
      { label:"17,1@ a 18,8@", minAt:"17.1", maxAt:"18.8", color:"#F59E0B" },
      { label:"18,9@ ou mais", minAt:"18.9", maxAt:"999", color:"#22C55E" },
    ];
    return base.map(rule=>({
      label:String(rule?.label||""),
      minAt:Number(rule?.minAt ?? rule?.min ?? 0),
      maxAt:Number(rule?.maxAt ?? rule?.max ?? 999),
      color:String(rule?.color || T.green),
    })).sort((a,b)=>a.minAt-b.minAt);
  }, [SL.BOVINE_WEIGHT_CLASS_RULES]);
  const getBovineWeightClass = React.useCallback((arrobas)=>{
    const value = Number(arrobas || 0);
    return bovineWeightClassRules.find(rule=>value >= rule.minAt && value <= rule.maxAt) || bovineWeightClassRules[bovineWeightClassRules.length-1] || { color:T.green };
  }, [bovineWeightClassRules]);
  const getCarcassYieldPct = React.useCallback((lotLike={})=>{
    const value = Number(lotLike?.carcassYieldPct || 50);
    return Math.min(60, Math.max(45, Number.isFinite(value) ? value : 50));
  }, []);
  const getCarcassWeightKg = React.useCallback((weightKg, lotLike={})=>Number(weightKg || 0) * (getCarcassYieldPct(lotLike) / 100), [getCarcassYieldPct]);
  const getCarcassArrobas = React.useCallback((weightKg, lotLike={})=>getCarcassWeightKg(weightKg, lotLike) / 15, [getCarcassWeightKg]);
  const normalizeText = React.useCallback((value) => String(value || "").normalize("NFD").replace(/[̀-ͯ]/g, "").trim().toLowerCase(), []);
  const inferLegacySpecies = React.useCallback((item = null) => {
    if (!item || typeof item !== "object") return "";
    const explicit = String(item?.species || item?.animalSpecies || item?.speciesName || "").trim();
    if (explicit && ANIMAL_SPECIES.includes(explicit)) return explicit;
    if (item?.birdStage || item?.birdBaseCode || item?.eggProduction || item?.eggHistory) return "Ave";
    const haystack = [
      item?.type,
      item?.category,
      item?.group,
      item?.subtype,
      item?.animalType,
      item?.lotType,
      item?.name,
      item?.title,
      item?.description,
      item?.code,
      item?.costCenter,
      item?.breed,
    ].map(normalizeText).filter(Boolean).join(" ");
    if (!haystack) return "";
    if (/(^|\s)(ave|aves|frango|franga|galinha|galinhas|galo|pinto|pintos|postura|corte|poedeira|poedeiras)(\s|$)/.test(haystack)) return "Ave";
    if (/(^|\s)(equino|equinos|cavalo|cavalos|egua|eguas|potro|potros|garanhao|garanhoes|haras)(\s|$)/.test(haystack)) return "Equino";
    if (/(^|\s)(ovino|ovinos|ovelha|ovelhas|carneiro|carneiros|cordeiro|cordeiros)(\s|$)/.test(haystack)) return "Ovino";
    if (/(^|\s)(caprino|caprinos|cabra|cabras|bode|bodes)(\s|$)/.test(haystack)) return "Caprino";
    if (/(^|\s)(suino|suinos|porco|porcos|leitao|leitoes)(\s|$)/.test(haystack)) return "Suíno";
    if (/(^|\s)(bovino|bovinos|boi|bois|vaca|vacas|bezerro|bezerros|novilha|novilhas|garrote|garrotes|touro|touros)(\s|$)/.test(haystack)) return "Bovino";
    const costCenter = normalizeText(item?.costCenter);
    if (costCenter === "galinheiro") return "Ave";
    if (costCenter === "haras") return "Equino";
    if (costCenter === "pecuaria") return "Bovino";
    return "";
  }, [normalizeText]);
  const normalizeSpeciesValue = React.useCallback((value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    if (ANIMAL_SPECIES.includes(raw)) return raw;
    const normalized = normalizeText(raw);
    if (["ave", "aves", "avicultura", "frango", "frangos", "galinha", "galinhas", "galo", "galos", "pinto", "pintos", "postura", "poedeira", "poedeiras", "corte"].includes(normalized)) return "Ave";
    if (["bovino", "bovinos", "pecuaria", "boi", "bois", "vaca", "vacas", "bezerro", "bezerros", "novilha", "novilhas", "garrote", "garrotes", "touro", "touros"].includes(normalized)) return "Bovino";
    if (["equino", "equinos", "haras", "cavalo", "cavalos", "egua", "eguas", "potro", "potros", "garanhao", "garanhoes"].includes(normalized)) return "Equino";
    if (["ovino", "ovinos", "ovelha", "ovelhas", "carneiro", "carneiros", "cordeiro", "cordeiros"].includes(normalized)) return "Ovino";
    if (["caprino", "caprinos", "cabra", "cabras", "bode", "bodes"].includes(normalized)) return "Caprino";
    if (["suino", "suinos", "porco", "porcos", "leitao", "leitoes"].includes(normalized)) return "Suíno";
    return "";
  }, [normalizeText]);
  const normalizeSpeciesList = React.useCallback((list=[]) => {
    const seen = new Set();
    const out = [];
    for (const item of Array.isArray(list) ? list : []) {
      const value = normalizeSpeciesValue(item);
      if (!value || seen.has(value)) continue;
      seen.add(value);
      out.push(value);
    }
    return out;
  }, [normalizeSpeciesValue]);
  const animalSpeciesOf = React.useCallback((item) => {
    const explicit = normalizeSpeciesValue(item?.species || item?.animalSpecies || item?.speciesName);
    return explicit || inferLegacySpecies(item) || "";
  }, [inferLegacySpecies, normalizeSpeciesValue]);
  const farmEnabledSpecies = normalizeSpeciesList(farm?.enabledAnimalSpecies || []);
  const dataSpecies = normalizeSpeciesList([
    ...(animals||[]).map(animalSpeciesOf),
    ...(lots||[]).map(animalSpeciesOf),
  ]);
  const allVisibleSpecies = normalizeSpeciesList([
    ...farmEnabledSpecies,
    ...dataSpecies,
  ]);
  const applySpeciesFilter = React.useCallback((list=[])=>normalizeSpeciesList((Array.isArray(list)?list:[]).filter(species=>!speciesFilter || speciesFilter(species))), [normalizeSpeciesList]);
  const visibleSpecies = applySpeciesFilter(allVisibleSpecies);
  const overviewSpecies = applySpeciesFilter(["Bovino", "Ovino", "Equino", "Ave"]);
  const speciesNav = applySpeciesFilter([...overviewSpecies, ...visibleSpecies]);
  const activeSpecies = speciesNav.includes(speciesTab) ? speciesTab : "";

  React.useEffect(()=>{
    if(defaultSpeciesOverride && !speciesTab && speciesNav.includes(defaultSpeciesOverride)) setSpeciesTab(defaultSpeciesOverride);
  }, [speciesNav, speciesTab]);

  React.useEffect(()=>{
    if(!speciesNav.length){
      if(speciesTab) setSpeciesTab("");
      return;
    }
    if(speciesTab && !speciesNav.includes(speciesTab)){
      setSpeciesTab(defaultSpeciesOverride && speciesNav.includes(defaultSpeciesOverride) ? defaultSpeciesOverride : "");
      return;
    }
    if(defaultSpeciesOverride && !speciesTab && speciesNav.includes(defaultSpeciesOverride)){
      setSpeciesTab(defaultSpeciesOverride);
    }
  }, [speciesNav, speciesTab]);

  React.useEffect(()=>{
    if(!lotForm){
      setLotFormFocusSection("");
      return;
    }
    if(normalizeSpeciesValue(lotForm?.species||activeSpecies) !== "Bovino") return;
    setLotNutritionAuditRange(prev=>({
      start: prev?.start || lotForm?.formedAt || today(),
      end: prev?.end || today(),
    }));
  }, [lotForm, activeSpecies, normalizeSpeciesValue]);

  function defaultTabForSpecies(species){
    if(species === "Ave") return "";
    if(species === "Equino") return "service";
    if(species === "Bovino") return "lots";
    if(species === "Ovino") return "lots";
    return "animals";
  }

  function speciesIcon(species, size = 20){
    if(species === "Equino") return <HorseIcon size={size} />;
    if(species === "Bovino") return <CattleIcon size={Math.round(size*1.15)} />;
    if(species === "Ovino") return <SheepHeadIcon size={Math.round(size*1.2)} style={{transform:"translate(-1px, 1px) scale(1.16)", transformOrigin:"center center"}} title="Ovinos" />;
    if(species === "Caprino") return <span style={{fontSize:size+2,lineHeight:1}}>🐐</span>;
    if(species === "Suíno") return <span style={{fontSize:size+2,lineHeight:1}}>🐖</span>;
    if(species === "Ave") return <ChickenHeadIcon size={Math.round(size*1.05)} />;
    return <span style={{fontSize:size+2,lineHeight:1}}>🐾</span>;
  }

  function defaultCostCenterForSpecies(species){
    return species === "Equino" ? "Haras" : "Pecuária";
  }

  function speciesLabelPlural(species){
    if(species === "Bovino") return "Bovinos";
    if(species === "Equino") return "Equinos";
    if(species === "Ovino") return "Ovinos";
    if(species === "Caprino") return "Caprinos";
    if(species === "Suíno") return "Suínos";
    if(species === "Ave") return "Aves";
    return species || "Espécie";
  }

  const isAtAnimalsModuleRoot = React.useMemo(()=>{
    return !activeSpecies
      && !form
      && !lotForm
      && !detailLot
      && !detailAnimal
      && !birdLotForm
      && !birdNutritionForm
      && !birdMedicationForm
      && !birdMortalityForm
      && !birdEggForm
      && !birdTransferForm
      && !lotNutritionForm
      && !lotMedicationForm
      && !foalForm
      && !serviceAnimalForm
      && !serviceAnimalDetail
      && !serviceMedicationForm
      && !serviceNutritionForm
      && !foalMedicationForm
      && !foalNutritionForm;
  }, [activeSpecies, form, lotForm, detailLot, detailAnimal, birdLotForm, birdNutritionForm, birdMedicationForm, birdMortalityForm, birdEggForm, birdTransferForm, lotNutritionForm, lotMedicationForm, foalForm, serviceAnimalForm, serviceAnimalDetail, serviceMedicationForm, serviceNutritionForm, foalMedicationForm, foalNutritionForm]);

  const handleAnimalsMenuBack = React.useCallback(()=>{
    if (isAtAnimalsModuleRoot) return false;
    setForm(null);
    setLotForm(null);
    setDetailLot(null);
    setDetailAnimal(null);
    setBirdLotForm(null);
    setBirdNutritionForm(null);
    setBirdMedicationForm(null);
    setBirdMortalityForm(null);
    setBirdEggForm(null);
    setBirdEggInfoOpen(false);
    setBirdTransferForm(null);
    setLotNutritionForm(null);
    setLotMedicationForm(null);
    setFoalForm(null);
    setServiceAnimalForm(null);
    setServiceAnimalDetail(null);
    setServiceMedicationForm(null);
    setServiceNutritionForm(null);
    setFoalMedicationForm(null);
    setFoalNutritionForm(null);
    setSpeciesTab("");
    setTab("animals");
    return true;
  }, [isAtAnimalsModuleRoot]);


  function dedupeById(items = []) {
    const map = new Map();
    for (const raw of Array.isArray(items) ? items : []) {
      const item = raw && typeof raw === "object" ? raw : null;
      if (!item) continue;
      const key = String(item.id || createClientId("tmp"));
      const prev = map.get(key);
      if (!prev) { map.set(key, item); continue; }
      const prevAt = String(prev.updatedAt || prev.createdAt || "");
      const nextAt = String(item.updatedAt || item.createdAt || "");
      if (nextAt >= prevAt) map.set(key, item);
    }
    return Array.from(map.values());
  }

  const resolveAnimalsRecordModuleKey = React.useCallback((species)=> normalizeSpeciesValue(species) === "Ave" ? "birds" : "animals", [normalizeSpeciesValue]);
  const resolveAnimalsLivestockSuffixKey = React.useCallback((species)=>{
    const normalized = normalizeSpeciesValue(species);
    if (normalized === "Bovino") return "Bovinos";
    if (normalized === "Equino") return "Equinos";
    if (normalized === "Caprino") return "Caprinos";
    return "Outros";
  }, [normalizeSpeciesValue]);
  const ensureAnimalsRecordNumbers = React.useCallback((records=[])=>{
    const working = Array.isArray(records) ? records.map(item=> item && typeof item === "object" ? { ...item } : item).filter(Boolean) : [];
    working.forEach((record)=>{
      if (!record || record.recordNumber) return;
      const moduleKey = resolveAnimalsRecordModuleKey(record?.species);
      const livestockType = moduleKey === "animals" ? resolveAnimalsLivestockSuffixKey(record?.species) : undefined;
      const next = buildNextRecordNumber(working.filter(Boolean), { moduleKey, livestockType });
      Object.assign(record, next);
    });
    return working;
  }, [resolveAnimalsLivestockSuffixKey, resolveAnimalsRecordModuleKey]);

  function makeAnimalCreatePending(record, records = []) {
    const moduleKey = resolveAnimalsRecordModuleKey(record?.species);
    const livestockType = moduleKey === "animals" ? resolveAnimalsLivestockSuffixKey(record?.species) : undefined;
    const numberParts = record?.recordNumber
      ? { recordNumber: record.recordNumber, recordPrefix: record.recordPrefix, recordSuffix: record.recordSuffix, recordSequence: record.recordSequence }
      : buildNextRecordNumber(Array.isArray(records) ? records.filter(Boolean) : [], { moduleKey, livestockType });
    const rn = appendProvisionalSuffix(numberParts.recordNumber || record.recordNumber || "");
    return markOperationalCreatePending({
      ...record,
      ...numberParts,
      recordNumber: rn,
      provisionalRecordNumber: rn,
      recordNumberStatus: "provisional",
      provisional: true,
    }, { userName: session?.name || "" });
  }

  function makeAnimalLotCreatePending(record, records = []) {
    const moduleKey = resolveAnimalsRecordModuleKey(record?.species);
    const livestockType = moduleKey === "animals" ? resolveAnimalsLivestockSuffixKey(record?.species) : undefined;
    const numberParts = record?.recordNumber
      ? { recordNumber: record.recordNumber, recordPrefix: record.recordPrefix, recordSuffix: record.recordSuffix, recordSequence: record.recordSequence }
      : buildNextRecordNumber(Array.isArray(records) ? records.filter(Boolean) : [], { moduleKey, livestockType });
    const rn = appendProvisionalSuffix(numberParts.recordNumber || record.recordNumber || "");
    return markOperationalCreatePending({
      ...record,
      ...numberParts,
      recordNumber: rn,
      provisionalRecordNumber: rn,
      recordNumberStatus: "provisional",
      provisional: true,
    }, { userName: session?.name || "" });
  }

  function makeAnimalUpdatePending(record, operationType = "update") {
    return markOperationalUpdatePending(record, { userName: session?.name || "", operationType });
  }

  function displayAnimalRecordNumber(record) {
    if(!record) return "—";
    const rn = String(record.recordNumber || record.id || "").trim();
    if(!rn) return "—";
    if(record.pendingLocalUpdate && !record.pendingLocalCreate) return appendUpdatePendingDisplaySuffix(rn);
    return rn;
  }

  const pastureMap = Object.fromEntries(pastures.map(p=>[p.id, p.name]));


  useEffect(() => {
    const now = new Date().toISOString();
    let changed = false;
    const normalizedAnimals = ensureAnimalsRecordNumbers((animals||[]).map(item => {
      if(!item || typeof item !== "object") return item;
      let next = item;
      if(!next.id || typeof next.id !== "string"){ next = { ...next, id: createClientId("ani") }; changed = true; }
      if(!next.farmId){ next = { ...next, farmId: currentFarmId || "" }; changed = true; }
      if(!next.createdAt){ next = { ...next, createdAt: next.updatedAt || now }; changed = true; }
      if(!next.updatedAt){ next = { ...next, updatedAt: next.createdAt || now }; changed = true; }
      if(!next.updatedBy){ next = { ...next, updatedBy: session?.name || "" }; changed = true; }
      const normalizedSpecies = normalizeSpeciesValue(next?.species || next?.animalSpecies || next?.speciesName);
      const inferredSpecies = normalizedSpecies || inferLegacySpecies(next);
      if(inferredSpecies !== String(next?.species || "").trim()){ next = { ...next, species: inferredSpecies }; changed = true; }
      const structuredAnimal = ensureAnimalRegistryFields(next, currentFarmId || next?.farmId || '');
      if(JSON.stringify(structuredAnimal) !== JSON.stringify(next)){ next = structuredAnimal; changed = true; }
      if(next?.sourceLotId && String(next?.originType || '').trim() !== 'individualized_from_lot'){ next = { ...next, originType: 'individualized_from_lot' }; changed = true; }
      const normalizedAnimalAgeRange = inferAnimalAgeRangeId({ ...next, species: inferredSpecies });
      const normalizedAnimalAgeLabel = getSpeciesAgeRanges(inferredSpecies).find(item=>String(item.id)===String(normalizedAnimalAgeRange))?.label || next?.ageLabel || "Faixa não informada";
      if(normalizedAnimalAgeRange && (String(next?.ageRange || "") !== String(normalizedAnimalAgeRange) || String(next?.ageLabel || "") !== String(normalizedAnimalAgeLabel))){ next = { ...next, ageRange: normalizedAnimalAgeRange, ageLabel: normalizedAnimalAgeLabel }; changed = true; }
      return next;
    }));
    if(changed || (normalizedAnimals||[]).some((item, idx)=>String(item?.recordNumber||"") !== String((animals||[])[idx]?.recordNumber||""))) setAnimals(normalizedAnimals);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animals, currentFarmId, ensureAnimalsRecordNumbers, inferLegacySpecies, normalizeSpeciesValue, session?.name, setAnimals]);

  useEffect(() => {
    const now = new Date().toISOString();
    let changed = false;
    const normalizedLots = ensureAnimalsRecordNumbers((lots||[]).map(item => {
      if(!item || typeof item !== "object") return item;
      let next = item;
      if(!next.id || typeof next.id !== "string"){ next = { ...next, id: createClientId("lot") }; changed = true; }
      if(!next.farmId){ next = { ...next, farmId: currentFarmId || "" }; changed = true; }
      if(!next.createdAt){ next = { ...next, createdAt: next.updatedAt || now }; changed = true; }
      if(!next.updatedAt){ next = { ...next, updatedAt: next.createdAt || now }; changed = true; }
      if(!next.updatedBy){ next = { ...next, updatedBy: session?.name || "" }; changed = true; }
      const normalizedSpecies = normalizeSpeciesValue(next?.species || next?.animalSpecies || next?.speciesName);
      const inferredSpecies = normalizedSpecies || inferLegacySpecies(next);
      if(inferredSpecies !== String(next?.species || "").trim()){ next = { ...next, species: inferredSpecies }; changed = true; }
      const structuredLot = ensureLotRegistryFields(next, currentFarmId || next?.farmId || '');
      if(JSON.stringify(structuredLot) !== JSON.stringify(next)){ next = structuredLot; changed = true; }
      const normalizedLotAgeRange = inferLotAgeRangeId({ ...next, species: inferredSpecies });
      const normalizedLotAgeLabel = getSpeciesAgeRanges(inferredSpecies).find(item=>String(item.id)===String(normalizedLotAgeRange))?.label || next?.ageLabel || "Faixa não informada";
      if(String(next?.ageRange || "") !== String(normalizedLotAgeRange) || String(next?.ageLabel || "") !== String(normalizedLotAgeLabel) || !!next?.ageRangeManualEnabled){ next = { ...next, ageRange: normalizedLotAgeRange, ageLabel: normalizedLotAgeLabel, ageRangeManualEnabled:false }; changed = true; }
      return next;
    }));
    if(changed || (normalizedLots||[]).some((item, idx)=>String(item?.recordNumber||"") !== String((lots||[])[idx]?.recordNumber||""))) setLots(normalizedLots);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lots, currentFarmId, ensureAnimalsRecordNumbers, inferLegacySpecies, normalizeSpeciesValue, session?.name, setLots]);


  const livestockRegistryAnimalsPayload = React.useMemo(()=> (animals||[])
    .filter(item=>item && typeof item === "object" && String(item?.farmId || currentFarmId || "") === String(currentFarmId || ""))
    .map(item=>({
      id: item.id,
      registryUid: item.registryUid,
      microchip: item.microchip || '',
      species: item.species || '',
      category: item.ageRange || '',
      sex: item.sex || '',
      born: item.born || '',
      originType: item.originType || '',
      sourceLotId: item.sourceLotId || '',
      currentPastureId: item.currentPastureId || item.pastureId || '',
      currentLotId: item.currentLotId || item.lotId || '',
      currentWeightKg: Number(item.weight || item.currentWeightKg || 0),
      traceMode: item.traceMode || 'individual',
      status: item.status || 'active',
      deletedAt: item.deletedAt || '',
      isActive: !item.deletedAt && String(item.status || '').toLowerCase() !== 'inativo',
    })), [animals, currentFarmId]);

  const livestockRegistryLotsPayload = React.useMemo(()=> (lots||[])
    .filter(item=>item && typeof item === "object" && String(item?.farmId || currentFarmId || "") === String(currentFarmId || ""))
    .map(item=>({
      id: item.id,
      registryUid: item.registryUid,
      lotType: item.lotType || 'collective',
      name: item.name || 'Lote',
      species: item.species || '',
      category: item.ageRange || '',
      sex: item.sex || '',
      currentPastureId: item.currentPastureId || item.pastureId || '',
      quantity: Number(item.quantity || 0),
      avgWeight: Number(item.avgWeight || 0),
      status: item.status || 'active',
      deletedAt: item.deletedAt || '',
      isActive: !item.deletedAt && String(item.status || '').toLowerCase() !== 'inativo',
    })), [lots, currentFarmId]);

  const livestockRegistryIndividualizationsPayload = React.useMemo(()=> (lotIndividualizations||[])
    .filter(item=>item && typeof item === "object" && String(item?.farmId || currentFarmId || "") === String(currentFarmId || ""))
    .map(item=>({
      id: item.id,
      sourceLotUid: item.sourceLotUid || '',
      createdAnimalUid: item.createdAnimalUid || '',
      qtyReducedFromLot: Number(item.qtyReducedFromLot || 1),
      performedBy: item.performedBy || '',
      performedAt: item.performedAt || item.createdAt || '',
      notes: item.notes || '',
    })), [lotIndividualizations, currentFarmId]);
  const livestockRegistryLotAnimalsPayload = React.useMemo(()=> (lots||[])
    .filter(item=>item && typeof item === "object" && !item.deletedAt && String(item?.farmId || currentFarmId || "") === String(currentFarmId || "") && String(item?.lotType || 'collective') === 'tracked')
    .flatMap(item=>{
      const lotKeys = [String(item?.registryUid || ''), String(item?.id || '')].filter(Boolean);
      const linked = lotKeys.flatMap(key=>trackedLotMembershipMap.get(key) || []);
      const uniq = [];
      const seen = new Set();
      linked.forEach(animal=>{ const key = String(animal?.registryUid || animal?.id || ''); if(key && !seen.has(key)){ seen.add(key); uniq.push(animal); } });
      return uniq.filter(animal=>animal && !animal.deletedAt).map(animal=>({
        id: `lan_${String(item?.registryUid || item?.id || 'lot')}_${String(animal?.registryUid || animal?.id || 'animal')}`.replace(/[^a-zA-Z0-9_:-]/g,'_'),
        lotUid: String(item?.registryUid || item?.id || ''),
        animalUid: String(animal?.registryUid || animal?.id || ''),
        enteredAt: animal?.updatedAt || animal?.createdAt || item?.updatedAt || item?.createdAt || new Date().toISOString(),
        status: 'active',
      }));
    }), [lots, currentFarmId, trackedLotMembershipMap]);

  const livestockRegistryMicrochipAuditPayload = React.useMemo(()=> (microchipAudit||[])
    .filter(item=>item && typeof item === "object" && String(item?.farmId || currentFarmId || "") === String(currentFarmId || ""))
    .map(item=>({
      id: item.id,
      animalUid: item.animalUid || '',
      microchip: item.microchip || '',
      eventType: item.eventType || 'set',
      eventAt: item.eventAt || item.createdAt || '',
      performedBy: item.performedBy || '',
      notes: item.notes || '',
    })), [microchipAudit, currentFarmId]);

  React.useEffect(()=>{
    if(!currentFarmId) return;
    const signature = JSON.stringify({
      animals: livestockRegistryAnimalsPayload.map(item=>[item.registryUid, item.currentPastureId, item.currentLotId, item.traceMode, item.status, item.deletedAt]),
      lots: livestockRegistryLotsPayload.map(item=>[item.registryUid, item.currentPastureId, item.lotType, item.quantity, item.avgWeight, item.status, item.deletedAt]),
      individualizations: livestockRegistryIndividualizationsPayload.map(item=>[item.id, item.sourceLotUid, item.createdAnimalUid, item.qtyReducedFromLot]),
      lotAnimals: livestockRegistryLotAnimalsPayload.map(item=>[item.id, item.lotUid, item.animalUid]),
      microchipAudit: livestockRegistryMicrochipAuditPayload.map(item=>[item.id, item.animalUid, item.microchip, item.eventType]),
    });
    if(registrySyncRef.current.signature === signature) return;
    if(registrySyncRef.current.timer) clearTimeout(registrySyncRef.current.timer);
    registrySyncRef.current.timer = setTimeout(async ()=>{
      if(registrySyncRef.current.running) return;
      registrySyncRef.current.running = true;
      try {
        await syncLivestockRegistrySnapshot({
          farmId: currentFarmId,
          animals: livestockRegistryAnimalsPayload,
          lots: livestockRegistryLotsPayload,
          individualizations: livestockRegistryIndividualizationsPayload,
          lotAnimals: livestockRegistryLotAnimalsPayload,
          microchipAudit: livestockRegistryMicrochipAuditPayload,
          userName: session?.name || '',
        });
        const summary = await fetchLivestockRegistrySummary({ farmId: currentFarmId, userName: session?.name || '' }).catch(()=>null);
        if(summary) setRegistrySummary(summary?.summary || summary);
        registrySyncRef.current.signature = signature;
      } catch (err) {
      } finally {
        registrySyncRef.current.running = false;
      }
    }, 1200);
    return ()=>{
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const ref = registrySyncRef.current;
      if(ref.timer) clearTimeout(ref.timer);
    };
  }, [currentFarmId, livestockRegistryAnimalsPayload, livestockRegistryLotsPayload, livestockRegistryIndividualizationsPayload, livestockRegistryLotAnimalsPayload, livestockRegistryMicrochipAuditPayload, session?.name]);



  // ── Individual animal form ───────────────────────────────────────
  const defForm = {
    _new:true, name:"", tag:"", species:activeSpecies || ANIMAL_SPECIES[0], sex:ANIMAL_SEX[0],
    breed:"", weight:"", carcassYieldPct:50, born:"", bornApprox:"", approxAgeMonths:"", costCenter:allowedCostCenters[0] || allowedCostCenters[0] || defaultCostCenterForSpecies(activeSpecies || ANIMAL_SPECIES[0]),
    status:"Ativo", lotId:"", notes:""
  };

  function saveAnimal(){
    if(!form.name.trim()||!form.tag.trim()){show("Nome e brinco obrigatórios.","error");return;}
    const{_new,...d}=form;
    const normalizedMicrochip = String(d?.microchip||"").trim();
    if(normalizedMicrochip){
      const duplicate = (animals||[]).find(item=>item && !item.deletedAt && String(item?.id||"")!==String(d?.id||"") && String(item?.microchip||"").trim() === normalizedMicrochip);
      if(duplicate){ show("Já existe um animal ativo com esse microchip nesta fazenda.", "error"); return; }
    }
    const previousAnimal = !_new ? (animals||[]).find(item=>String(item?.id||"")===String(d?.id||"")) : null;
    const resolvedApproxMonths = d?.born ? approxMonthsFromDate(d.born) : (d?.bornApprox ? approxMonthsFromDate(d.bornApprox) : String(d?.approxAgeMonths || ''));
    const ageRange = inferAnimalAgeRangeId(d);
    const ageLabel = getSpeciesAgeRanges(d?.species).find(item=>String(item.id)===String(ageRange))?.label || d?.ageLabel || "Faixa não informada";
    const payloadBase = { ...d, microchip: normalizedMicrochip, ageRange, ageLabel, currentAgeMonths: resolvedApproxMonths === '' ? '' : Math.max(0, Number(resolvedApproxMonths || 0)), approxAgeMonths: resolvedApproxMonths };
    const payload = ensureAnimalRegistryFields(payloadBase, currentFarmId||payloadBase.farmId||'');
    let savedAnimalUid = String(payload.registryUid || payload.id || '');
    if(_new) setAnimals(p=>{ const recordId = payload.id || createClientId("ani"); const saved = makeAnimalCreatePending({...payload,id:recordId,clientId:payload.clientId||recordId,farmId:currentFarmId||payload.farmId||"",...makeCreateMeta(session?.name||"")}, p||[]); savedAnimalUid = String(saved.registryUid || saved.id || savedAnimalUid); return dedupeById([...p,saved]); })
    else     setAnimals(p=>dedupeById(p.map(a=>String(a.id)===String(payload.id)?makeAnimalUpdatePending(ensureAnimalRegistryFields({...a,...payload,farmId:payload.farmId||a.farmId||currentFarmId||"",...makeUpdateMeta(session?.name||"")}, currentFarmId||payload.farmId||a.farmId||'')):a)));
    const previousMicrochip = String(previousAnimal?.microchip || '').trim();
    if(normalizedMicrochip && normalizedMicrochip !== previousMicrochip){
      const audit = createMicrochipAuditEvent({
        farmId: currentFarmId || payload.farmId || '',
        animalUid: savedAnimalUid,
        microchip: normalizedMicrochip,
        eventType: previousMicrochip ? 'updated' : 'set',
        performedBy: session?.name || '',
        notes: previousMicrochip ? `Microchip alterado de ${previousMicrochip}` : 'Microchip vinculado ao animal',
      });
      setMicrochipAudit(prev=>dedupeById([...(prev||[]), { ...audit, ...makeCreateMeta(session?.name||'') }]));
      syncOnSave('microchipAudit');
    }
    setEditingFlag(false); show("✅ Salvo!"); setForm(null); syncOnSave("animals");
  }

  // ── Lot form ─────────────────────────────────────────────────────
  const defLot = {
    _new:true,
    name:"", species:activeSpecies || ANIMAL_SPECIES[0], breed:"", sex:"Misto",
    ageRange:"0-12", ageLabel:"", ageRangeManualEnabled:false, ageRangeManual:"0-12", purpose:activePurposes[0]||LOT_PURPOSES[0],
    weightMode:"Peso Médio do Lote",
    avgWeight:0,
    carcassYieldPct:50,
    quantity:0,
    lotType:'collective',
    selectedAnimalIds: [],
    avgAgeMonths:"",
    birthDateApprox:"",
    pastureId:"", costCenter:defaultCostCenterForSpecies(activeSpecies || ANIMAL_SPECIES[0]),
    formedAt: today(),
    notes:"",
    // Feeding / nutrition
    feedType: FEED_TYPES[0],
    dailyFeedKg: 0,        // kg/animal/day
    rationPlans: [],       // [{stockId,dailyKg,notes}]
    supplements: [],       // [{type, dailyKg, notes}]
    feedNotes:"",
    // Movement / weighing history
    movements:[],
  };

  const plannedLotNutritionPerHeadKg = React.useCallback((lotLike={})=>{
    const rationTotal = (lotLike?.rationPlans||[]).reduce((sum, entry)=>sum + Number(entry?.dailyKg || 0), 0);
    const supplementTotal = (lotLike?.supplements||[]).reduce((sum, entry)=>sum + Number(entry?.dailyKg || 0), 0);
    return rationTotal + supplementTotal;
  }, []);


  const financialChartAccounts = React.useMemo(()=>(Array.isArray(SL.FIN_CHART_ACCOUNTS) ? SL.FIN_CHART_ACCOUNTS : []), [SL.FIN_CHART_ACCOUNTS]);
  const animalExitReasons = (SL.ANIMAL_EXIT_REASONS && SL.ANIMAL_EXIT_REASONS.length ? SL.ANIMAL_EXIT_REASONS : ["Venda externa","Abate interno","Transferência entre fazendas","Morte","Perda / furto","Doação","Descarte","Consumo interno","Outro"]);
  const animalEntryReasons = (SL.ANIMAL_ENTRY_REASONS && SL.ANIMAL_ENTRY_REASONS.length ? SL.ANIMAL_ENTRY_REASONS : ["Compra","Nascimento","Transferência entre fazendas","Retorno","Ajuste de inventário","Outro"]);
  const appendFinancialEntry = React.useCallback((entry)=>{
    const account = financialChartAccounts.find(acc=>String(acc.code||"")===String(entry?.accountCode||""))
      || financialChartAccounts.find(acc=>String(acc.name||"").toLowerCase().includes(String(entry?.accountName||"").toLowerCase()));
    const payload = {
      id:createClientId('fin'),
      farmId:currentFarmId||'',
      automatic:true,
      originModule: entry.originModule || 'animals',
      originId: entry.originId || '',
      type: entry.type || 'Despesa',
      category: entry.category || (entry.type === 'Receita' ? 'Venda de animais' : 'Compra de Animais'),
      accountCode: entry.accountCode || account?.code || '',
      accountName: entry.accountName || account?.name || '',
      description: entry.description || '',
      amount: Number(entry.amount || 0),
      date: entry.date || today(),
      dueDate: entry.dueDate || entry.date || today(),
      paymentMethod: entry.paymentMethod || '',
      documentNumber: entry.documentNumber || '',
      costCenter: entry.costCenter || 'Pecuária',
      counterCostCenter: entry.counterCostCenter || '',
      notes: entry.notes || '',
      justificationMissing: entry.justificationMissing || '',
      taxModel: entry.taxModel || 'Atual',
      metadata: entry.metadata || {},
      ...makeCreateMeta(session?.name||''),
    };
    setFinancialEntries(prev=>dedupeById([...(prev||[]), payload]));
    syncOnSave('financial');
  }, [currentFarmId, financialChartAccounts, session?.name, setFinancialEntries, syncOnSave]);

  const createAnimalBuyer = React.useCallback((buyer)=>{
    const payload = {
      id:createClientId('buyer'),
      name:String(buyer?.name||'').trim(),
      cpfCnpj:String(buyer?.cpfCnpj||''),
      phone:String(buyer?.phone||''),
      address:String(buyer?.address||''),
      city:String(buyer?.city||''),
      state:String(buyer?.state||''),
      notes:String(buyer?.notes||''),
      ...makeCreateMeta(session?.name||''),
    };
    if(!payload.name) return null;
    setAnimalBuyers(prev=>dedupeById([...(prev||[]), payload]));
    return payload;
  }, [session?.name, setAnimalBuyers]);

  function saveLot(){
    if(!lotForm.name.trim()){show("Nome do lote obrigatório.","error");return;}
    if(!lotForm.species){show("Selecione a espécie.","error");return;}
    const normalizedLotType = String(lotForm?.lotType || 'collective');
    const selectedAnimalIds = Array.isArray(lotForm?.selectedAnimalIds) ? lotForm.selectedAnimalIds.map(v=>String(v||'')).filter(Boolean) : [];
    const selectedTrackedAnimals = normalizedLotType === 'tracked'
      ? (animals||[]).filter(item=>item && !item.deletedAt && String(animalSpeciesOf(item))===String(lotForm?.species||'') && selectedAnimalIds.includes(String(item?.id||'')))
      : [];
    const derivedTrackedQty = selectedTrackedAnimals.length;
    const derivedTrackedAvgWeight = derivedTrackedQty > 0 ? selectedTrackedAnimals.reduce((sum,item)=>sum + Number(item?.weight || 0), 0) / derivedTrackedQty : 0;
    const effectiveQuantity = normalizedLotType === 'tracked' ? derivedTrackedQty : Number(lotForm.quantity || 0);
    const effectiveAvgWeight = normalizedLotType === 'tracked' ? Number(derivedTrackedAvgWeight || 0) : Number(lotForm.avgWeight || 0);
    if(effectiveQuantity<=0){show(normalizedLotType === 'tracked' ? "Selecione pelo menos um animal para o lote rastreado." : "Quantidade deve ser maior que zero.","error");return;}
    const{_new,...d}=lotForm;
    const resolvedAgeRange = inferLotAgeRangeId({ ...lotForm, quantity: effectiveQuantity, avgWeight: effectiveAvgWeight });
    const resolvedAgeLabel = getSpeciesAgeRanges(lotForm?.species).find(item=>String(item.id)===String(resolvedAgeRange))?.label || lotForm?.ageLabel || "Faixa não informada";
    const totalWeight = normalizedLotType === 'tracked'
      ? selectedTrackedAnimals.reduce((s,a)=>s+(Number(a?.weight||0)),0)
      : (lotForm.weightMode==="Peso Médio do Lote"
        ? effectiveAvgWeight * effectiveQuantity
        : (lotForm.animals||[]).reduce((s,a)=>s+(a.weight||0),0));
    const derivedPlannedPerHead = normalizeSpeciesValue(lotForm?.species) === "Bovino" ? plannedLotNutritionPerHeadKg(lotForm) : Number(lotForm.dailyFeedKg || 0);
    const entryBase = {
      ...d,
      ageRange: resolvedAgeRange,
      ageLabel: resolvedAgeLabel,
      ageRangeManualEnabled:false,
      avgAgeMonths: lotForm.avgAgeMonths === "" ? "" : Math.max(0, Number(lotForm.avgAgeMonths||0)),
      lotType: normalizedLotType,
      selectedAnimalIds,
      quantity: effectiveQuantity,
      avgWeight: effectiveAvgWeight,
      totalWeight,
      dailyFeedKg: derivedPlannedPerHead,
      lastUpdated: new Date().toISOString(),
    };
    const entry = ensureLotRegistryFields(entryBase, currentFarmId||entryBase.farmId||'');
    if(_new){
      const newLot = ensureLotRegistryFields({...entry, id:createClientId("lot"), farmId:currentFarmId||entry.farmId||"",
        movements:[{ts:new Date().toISOString(),type:"formação",qty:lotForm.quantity,
          weight:effectiveAvgWeight||0, carcassYieldPct:getCarcassYieldPct(lotForm), note:"Lote formado",by:session?.name||"admin"}]}, currentFarmId||entry.farmId||'');
      const savedLot = makeAnimalLotCreatePending({...newLot, clientId:newLot.clientId||newLot.id, ...makeCreateMeta(session?.name||"")}, lots||[]);
      setLots(p=>dedupeById([...p,savedLot]));
      if(normalizedLotType === 'tracked'){
        setAnimals(prev=>dedupeById((prev||[]).map(item=>{
          const itemId = String(item?.id||'');
          if(selectedAnimalIds.includes(itemId)){
            return ensureAnimalRegistryFields({ ...item, lotId: savedLot.id, currentLotId: savedLot.registryUid || savedLot.id, pastureId: savedLot.pastureId || item?.pastureId || '', currentPastureId: savedLot.pastureId || item?.currentPastureId || item?.pastureId || '', traceMode: item?.microchip ? 'microchip' : (item?.traceMode || 'individual'), ...makeUpdateMeta(session?.name||'') }, currentFarmId||item?.farmId||'');
          }
          return item;
        })));
      }
    } else {
      const previousLot = (lots||[]).find(l=>String(l?.id||'')===String(entry.id||'')) || null;
      const savedLot = makeAnimalUpdatePending(ensureLotRegistryFields({ ...(previousLot||{}), ...entry, farmId:entry.farmId||(previousLot?.farmId)||currentFarmId||"", ...makeUpdateMeta(session?.name||"") }, currentFarmId||entry.farmId||(previousLot?.farmId)||''));
      setLots(p=>dedupeById(p.map(l=>String(l.id)===String(entry.id)?savedLot:l)));
      if(normalizedLotType === 'tracked'){
        const previousSelected = Array.isArray(previousLot?.selectedAnimalIds) ? previousLot.selectedAnimalIds.map(v=>String(v||'')) : (animals||[]).filter(item=>String(item?.lotId||item?.currentLotId||'')===String(previousLot?.id||previousLot?.registryUid||'')).map(item=>String(item?.id||''));
        const selectedSet = new Set(selectedAnimalIds);
        setAnimals(prev=>dedupeById((prev||[]).map(item=>{
          const itemId = String(item?.id||'');
          if(selectedSet.has(itemId)){
            return ensureAnimalRegistryFields({ ...item, lotId: savedLot.id, currentLotId: savedLot.registryUid || savedLot.id, pastureId: savedLot.pastureId || item?.pastureId || '', currentPastureId: savedLot.pastureId || item?.currentPastureId || item?.pastureId || '', traceMode: item?.microchip ? 'microchip' : (item?.traceMode || 'individual'), ...makeUpdateMeta(session?.name||'') }, currentFarmId||item?.farmId||'');
          }
          if(previousSelected.includes(itemId) && !selectedSet.has(itemId) && String(item?.lotId||item?.currentLotId||'').replace(/^$/, '') && (String(item?.lotId||'')===String(savedLot.id) || String(item?.currentLotId||'')===String(savedLot.registryUid||savedLot.id) || String(item?.currentLotId||'')===String(previousLot?.registryUid||previousLot?.id||''))){
            return ensureAnimalRegistryFields({ ...item, lotId: '', currentLotId: '', ...makeUpdateMeta(session?.name||'') }, currentFarmId||item?.farmId||'');
          }
          return item;
        })));
      }
    }
    show("✅ Lote salvo!"); setLotForm(null); syncOnSave("animalLots"); if(normalizedLotType === 'tracked') syncOnSave("animals");
  }

  // ── Add movement to lot ─────────────────────────────────────────
  function openLotIndividualization(lot){
    if(!lot) return;
    setIndividualizeForm({
      lotId: lot.id,
      qty: 1,
      avgWeight: Number(lot.avgWeight || 0),
      tagPrefix: String((lot.name || lot.recordNumber || lot.id || 'ANI')).replace(/\s+/g, '-').toUpperCase().slice(0, 12),
      namePrefix: lot.name || 'Animal',
      notes: '',
    });
  }

  function confirmLotIndividualization(){
    const formState = individualizeForm;
    const lot = getLivestockLotById(formState?.lotId);
    if(!lot){ show('Lote não encontrado.', 'error'); return; }
    const qty = Math.max(1, Number(formState?.qty || 0));
    const available = Math.max(0, Number(lot?.quantity || 0));
    if(qty <= 0 || qty > available){ show('Quantidade inválida para individualização.', 'error'); return; }
    const avgWeight = Math.max(0, Number(formState?.avgWeight || lot?.avgWeight || 0));
    const now = new Date().toISOString();
    const animalsToCreate = Array.from({ length: qty }).map((_, idx) => {
      const base = {
        id: createClientId('ani'),
        name: `${String(formState?.namePrefix || lot?.name || 'Animal').trim()} ${available - qty + idx + 1}`,
        tag: `${String(formState?.tagPrefix || 'ANI').trim()}-${String(Date.now()).slice(-6)}-${idx + 1}`,
        species: lot?.species || activeSpecies || ANIMAL_SPECIES[0],
        sex: lot?.sex === 'Misto' ? '' : (lot?.sex || ANIMAL_SEX[0]),
        breed: lot?.breed || '',
        weight: avgWeight || '',
        carcassYieldPct: Number(lot?.carcassYieldPct || 50),
        born: '',
        bornApprox: lot?.birthDateApprox || '',
        approxAgeMonths: lot?.avgAgeMonths === '' ? '' : Math.max(0, Number(lot?.avgAgeMonths || 0)),
        costCenter: lot?.costCenter || defaultCostCenterForSpecies(lot?.species || activeSpecies || ANIMAL_SPECIES[0]),
        status: 'Ativo',
        lotId: '',
        pastureId: lot?.pastureId || '',
        sourceLotId: lot?.registryUid || lot?.id || '',
        originType: 'individualized_from_lot',
        currentPastureId: lot?.pastureId || '',
        currentLotId: '',
        notes: String(formState?.notes || '').trim(),
        farmId: currentFarmId || lot?.farmId || '',
      };
      const speciesValue = base.species;
      const animal = ensureAnimalRegistryFields({
        ...base,
        ageRange: inferAnimalAgeRangeId({ ...base, species: speciesValue }),
        ageLabel: getSpeciesAgeRanges(speciesValue).find(item=>String(item.id)===String(inferAnimalAgeRangeId({ ...base, species: speciesValue })))?.label || 'Faixa não informada',
        currentAgeMonths: base.approxAgeMonths === '' ? '' : Math.max(0, Number(base.approxAgeMonths || 0)),
        traceMode: 'individual',
      }, currentFarmId || lot?.farmId || '');
      return { ...animal, ...makeCreateMeta(session?.name || '') };
    });

    const events = animalsToCreate.map((animal)=>createIndividualizationEvent({
      farmId: currentFarmId || lot?.farmId || '',
      sourceLot: lot,
      animalUid: animal.registryUid || animal.id,
      qtyReduced: 1,
      performedBy: session?.name || '',
      notes: String(formState?.notes || '').trim(),
    }));

    const nextLot = ensureLotRegistryFields({
      ...lot,
      quantity: Math.max(0, available - qty),
      totalWeight: avgWeight > 0 ? Math.max(0, available - qty) * avgWeight : Math.max(0, Number(lot?.totalWeight || 0) - (avgWeight * qty)),
      individualizedCount: Number(lot?.individualizedCount || 0) + qty,
      individualizationHistory: [...(Array.isArray(lot?.individualizationHistory) ? lot.individualizationHistory : []), {
        id: createClientId('indhist'),
        qty,
        avgWeight,
        animals: animalsToCreate.map(item=>({ id:item.id, registryUid:item.registryUid, tag:item.tag })),
        notes: String(formState?.notes || '').trim(),
        by: session?.name || '',
        ts: now,
      }],
      lastUpdated: now,
      ...makeUpdateMeta(session?.name || ''),
    }, currentFarmId || lot?.farmId || '');

    setAnimals(prev=>dedupeById([...(prev||[]), ...animalsToCreate]));
    setLots(prev=>dedupeById((prev||[]).map(item=>String(item.id)===String(lot.id)?nextLot:item)));
    setDetailLot(prev=>prev && String(prev.id)===String(lot.id) ? nextLot : prev);
    setLotIndividualizations(prev=>dedupeById([...(prev||[]), ...events.map(ev=>({ ...ev, id: ev.id || createClientId('ind'), ...makeCreateMeta(session?.name || '') }))]));
    setIndividualizeForm(null);
    show('✅ Animais individualizados e abatidos do lote.');
    syncOnSave('animals');
    syncOnSave('animalLots');
    syncOnSave('lotIndividualizations');
  }

  function addMovement(lot, type, qty, weight, noteOrMeta){
    const meta = noteOrMeta && typeof noteOrMeta === "object" ? noteOrMeta : { note: noteOrMeta };
    const now = new Date().toISOString();
    const resolvedBuyer = meta?.createBuyer ? createAnimalBuyer(meta.createBuyer) : ((animalBuyers||[]).find(item=>String(item.id)===String(meta?.buyerId||"")) || null);
    const mv = {ts:now, type, qty:Number(qty), weight:Number(weight), carcassYieldPct: meta?.carcassYieldPct == null ? getCarcassYieldPct(lot) : Math.min(60, Math.max(45, Number(meta?.carcassYieldPct||50))), note:meta?.note||"", by:session?.name||"—", reason:meta?.reason||"", buyerId:resolvedBuyer?.id || meta?.buyerId || "", buyerName:resolvedBuyer?.name || meta?.buyerName || "", gta:meta?.gta||"", nfNum:meta?.nfNum||"", amount:Number(meta?.amount||0), paymentMethod:meta?.paymentMethod||"", justificationMissing:meta?.justificationMissing||""};
    let updatedQty = Number(lot.quantity||0);
    let updatedAvgW = Number(lot.avgWeight||0);
    let updatedCarcassYieldPct = getCarcassYieldPct(lot);
    if(type==="entrada")      updatedQty += Number(qty);
    if(type==="saída")        updatedQty = Math.max(0, updatedQty - Number(qty));
    if(type==="pesagem"&&qty) {
      updatedAvgW = Number(weight);
      updatedCarcassYieldPct = Number(mv.carcassYieldPct || updatedCarcassYieldPct);
    }
    const updated = {...lot, quantity:updatedQty, avgWeight:updatedAvgW, carcassYieldPct: updatedCarcassYieldPct,
      totalWeight: updatedAvgW * updatedQty,
      movements:[...(lot.movements||[]),mv], lastUpdated:now, ...makeUpdateMeta(session?.name||"")};
    setLots(p=>p.map(l=>l.id===lot.id?updated:l));
    setDetailLot(updated);
    if(type === "saída" && meta?.reason === "Venda externa"){
      appendFinancialEntry({
        originModule:'animals', originId:lot.id, type:'Receita', category:'Venda de animais',
        accountName:'Receita com Venda de Animais', accountCode:'3.1.01', amount:Number(meta?.amount||0),
        date: meta?.date || today(), dueDate: meta?.dueDate || meta?.date || today(), paymentMethod: meta?.paymentMethod || '',
        documentNumber: meta?.nfNum || '', costCenter:'Pecuária', description:`Venda de lote ${lot.name}${resolvedBuyer?.name?` · ${resolvedBuyer.name}`:''}`,
        notes:[meta?.note, meta?.gta?`GTA ${meta.gta}`:'', meta?.justificationMissing?`Pendência: ${meta.justificationMissing}`:''].filter(Boolean).join(' · '),
        justificationMissing: meta?.justificationMissing || '', metadata:{ qty:Number(qty||0), buyer:resolvedBuyer || null, lotId:lot.id, reason:meta?.reason||'' },
      });
    }
    if(type === "entrada" && meta?.reason === "Compra"){
      appendFinancialEntry({
        originModule:'animals', originId:lot.id, type:'Despesa', category:'Compras',
        accountName:'Compra de Animais', accountCode:'4.1.01', amount:Number(meta?.amount||0),
        date: meta?.date || today(), dueDate: meta?.dueDate || meta?.date || today(), paymentMethod: meta?.paymentMethod || '',
        documentNumber: meta?.nfNum || '', costCenter:'Pecuária', description:`Compra para lote ${lot.name}`,
        notes:[meta?.note, meta?.sellerName?`Vendedor: ${meta.sellerName}`:'', meta?.justificationMissing?`Pendência: ${meta.justificationMissing}`:''].filter(Boolean).join(' · '),
        justificationMissing: meta?.justificationMissing || '', metadata:{ qty:Number(qty||0), lotId:lot.id, reason:meta?.reason||'' },
      });
    }
    syncOnSave("animalLots");
    show(`✅ ${type.charAt(0).toUpperCase()+type.slice(1)} registrada!`);
  }

  const scopedAnimals = animals.filter(a=>itemMatchesCostCenterAccess(session, moduleId, a));
  const scopedLots = lots.filter(l=>itemMatchesCostCenterAccess(session, moduleId, l));
  const speciesAnimals = scopedAnimals.filter(a=>animalSpeciesOf(a)===String(activeSpecies||""));
  const speciesLots = scopedLots.filter(l=>animalSpeciesOf(l)===String(activeSpecies||""));
  const availableTrackedAnimalsForLot = React.useMemo(()=>{
    const editingLotId = String(lotForm?.id || '');
    const editingLotRegistryUid = String(lotForm?.registryUid || '');
    return (animals||[]).filter(item=>item && !item.deletedAt && String(animalSpeciesOf(item))===String(lotForm?.species || activeSpecies || '')).filter(item=>{
      const linkedLotId = String(item?.lotId || '');
      const linkedCurrentLot = String(item?.currentLotId || '');
      if(!linkedLotId && !linkedCurrentLot) return true;
      if(editingLotId && linkedLotId===editingLotId) return true;
      if(editingLotRegistryUid && linkedCurrentLot===editingLotRegistryUid) return true;
      return false;
    });
  }, [animals, lotForm, activeSpecies, animalSpeciesOf]);
  const isBirdSpecies = activeSpecies === "Ave";
  const isEquineSpecies = activeSpecies === "Equino";
  const equineTab = ["potros","service"].includes(tab) ? tab : "potros";
  const totalLotAnimals = speciesAnimals.filter(a => a && a.lotId).length;
  const monthsBetween = React.useCallback((startDate) => {
    if (!startDate) return 0;
    const dt = new Date(startDate);
    if (Number.isNaN(dt.getTime())) return 0;
    const now = new Date();
    let months = (now.getFullYear() - dt.getFullYear()) * 12 + (now.getMonth() - dt.getMonth());
    if (now.getDate() < dt.getDate()) months -= 1;
    return Math.max(0, months);
  }, []);
  const formatDateInput = React.useCallback((date)=>{
    if(!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth()+1).padStart(2,'0');
    const day = String(date.getDate()).padStart(2,'0');
    return `${year}-${month}-${day}`;
  }, []);
  const approxBirthDateFromMonths = React.useCallback((monthsValue)=>{
    const normalized = String(monthsValue ?? '').trim().replace(',', '.');
    if(normalized === '') return '';
    const parsed = Number(normalized);
    if(!Number.isFinite(parsed)) return '';
    const safeMonths = Math.max(0, Math.round(parsed));
    const base = new Date();
    const candidate = new Date(base.getFullYear(), base.getMonth() - safeMonths, base.getDate());
    return formatDateInput(candidate);
  }, [formatDateInput]);
  const approxMonthsFromDate = React.useCallback((dateValue)=>{
    if(!dateValue) return '';
    const months = monthsBetween(dateValue);
    return Number.isFinite(months) ? String(Math.max(0, months)) : '';
  }, [monthsBetween]);
  const syncAnimalApproxMonths = React.useCallback((monthsValue)=>{
    setForm(prev=>({
      ...prev,
      approxAgeMonths: String(monthsValue ?? ''),
      bornApprox: approxBirthDateFromMonths(monthsValue),
    }));
  }, [approxBirthDateFromMonths]);
  const syncAnimalApproxDate = React.useCallback((dateValue)=>{
    setForm(prev=>({
      ...prev,
      bornApprox: dateValue,
      approxAgeMonths: approxMonthsFromDate(dateValue),
    }));
  }, [approxMonthsFromDate]);
  const syncLotApproxMonths = React.useCallback((monthsValue)=>{
    const normalized = String(monthsValue ?? '').trim().replace(',', '.');
    setLotForm(prev=>({
      ...prev,
      avgAgeMonths: normalized,
      birthDateApprox: approxBirthDateFromMonths(normalized),
    }));
  }, [approxBirthDateFromMonths]);
  const syncLotApproxDate = React.useCallback((dateValue)=>{
    setLotForm(prev=>({
      ...prev,
      birthDateApprox: dateValue,
      avgAgeMonths: approxMonthsFromDate(dateValue),
    }));
  }, [approxMonthsFromDate]);
  const normalizeAgeRangeList = React.useCallback((items, fallback=[])=>{
    if(!Array.isArray(items) || !items.length) return fallback;
    return items.map((item, idx)=>{
      if(item && typeof item === 'object'){
        return {
          id:String(item.id || item.value || fallback[idx]?.id || idx),
          label:String(item.label || item.name || item.value || fallback[idx]?.label || item.id || idx),
          icon:item.icon || fallback[idx]?.icon || '🐾',
          minMonths:Number.isFinite(Number(item.minMonths)) ? Number(item.minMonths) : Number(item.min ?? fallback[idx]?.minMonths ?? 0),
          maxMonths:item.maxMonths == null || item.maxMonths === '' ? (fallback[idx]?.maxMonths ?? null) : Number(item.maxMonths ?? item.max),
        };
      }
      return {
        ...(fallback[idx] || {}),
        id:String(item),
        label:fallback[idx]?.label || String(item),
        icon:fallback[idx]?.icon || '🐾',
        minMonths:Number(fallback[idx]?.minMonths || 0),
        maxMonths:fallback[idx]?.maxMonths ?? null,
      };
    });
  }, []);
  const foalAgeRanges = [
    { id:"0-6", label:"0 a 6 meses" },
    { id:"6-12", label:"6 a 12 meses" },
    { id:"12-24", label:"12 a 24 meses" },
  ];
  const bovineAgeRanges = React.useMemo(() => normalizeAgeRangeList(SL.BOVINE_AGE_RANGES, [
    { id:"0-12", label:"0 a 12 meses", icon:renderCardImageIcon(bovineCalfIcon, "Bezerro"), minMonths:0, maxMonths:12 },
    { id:"13-24", label:"13 a 24 meses", icon:renderCardImageIcon(bovineYoungIcon, "Boi jovem"), minMonths:13, maxMonths:24 },
    { id:"25-36", label:"25 a 36 meses", icon:renderCardImageIcon(bovineAdultIcon, "Boi"), minMonths:25, maxMonths:36 },
    { id:"36+", label:"+ de 36 meses", icon:renderCardImageIcon(bovineFatIcon, "Boi mais gordo"), minMonths:37, maxMonths:null },
  ]), [SL.BOVINE_AGE_RANGES, normalizeAgeRangeList]);
  const equineAgeRanges = React.useMemo(() => normalizeAgeRangeList(SL.EQUINE_AGE_RANGES, [
    { id:"0-6", label:"0 a 6 meses", icon:renderCardImageIcon(equineMarchadorCardIcon, "Equinos de 0 a 6 meses"), minMonths:0, maxMonths:6 },
    { id:"6-12", label:"6 a 12 meses", icon:renderCardImageIcon(equineMarchadorCardIcon, "Equinos de 6 a 12 meses"), minMonths:7, maxMonths:12 },
    { id:"12-24", label:"12 a 24 meses", icon:renderCardImageIcon(equineMarchadorCardIcon, "Equinos de 12 a 24 meses"), minMonths:13, maxMonths:24 },
    { id:"24+", label:"24 meses ou mais", icon:renderCardImageIcon(equineMarchadorCardIcon, "Equinos de 24 meses ou mais"), minMonths:25, maxMonths:null },
  ]), [SL.EQUINE_AGE_RANGES, normalizeAgeRangeList]);
  const ovineAgeRanges = React.useMemo(() => normalizeAgeRangeList(SL.OVINE_AGE_RANGES, [
    { id:"0-3", label:"0 a 3 meses", icon:renderCardImageIcon(ovineDorperCardIcon, "Ovinos de 0 a 3 meses"), minMonths:0, maxMonths:3 },
    { id:"4-12", label:"4 a 12 meses", icon:renderCardImageIcon(ovineDorperCardIcon, "Ovinos de 4 a 12 meses"), minMonths:4, maxMonths:12 },
    { id:"13+", label:"+ de 13 meses", icon:renderCardImageIcon(ovineDorperCardIcon, "Ovinos de + de 13 meses"), minMonths:13, maxMonths:null },
  ]), [SL.OVINE_AGE_RANGES, normalizeAgeRangeList]);
  const normalizedLotAgeRanges = React.useMemo(() => {
    if (Array.isArray(AGE_RANGES) && AGE_RANGES.length && typeof AGE_RANGES[0] === "object") return AGE_RANGES;
    if (Array.isArray(AGE_RANGES) && AGE_RANGES.length) {
      return AGE_RANGES.map((item, idx)=>({ id:String(item), label:`${String(item).replace('+','+ de ')} meses`, icon:["🐣","🐮","🌱","🌾","🏅"][idx] || "🐾", minMonths:idx*6, maxMonths:null }));
    }
    return bovineAgeRanges;
  }, [bovineAgeRanges]);
  const getSpeciesAgeRanges = React.useCallback((species)=>{
    const normalizedSpecies = normalizeSpeciesValue(species || activeSpecies);
    if(normalizedSpecies === 'Bovino') return bovineAgeRanges;
    if(normalizedSpecies === 'Ovino') return ovineAgeRanges;
    if(normalizedSpecies === 'Equino') return equineAgeRanges;
    return normalizedLotAgeRanges;
  }, [activeSpecies, bovineAgeRanges, equineAgeRanges, normalizeSpeciesValue, normalizedLotAgeRanges, ovineAgeRanges]);
  const resolveDateForAnimalAge = React.useCallback((animal)=> animal?.born || animal?.birthDate || animal?.bornApprox || animal?.approxBirthDate || '', []);
  const resolveDateForLotAge = React.useCallback((lot)=> lot?.birthDateApprox || lot?.approxBirthDate || lot?.bornApprox || '', []);
  const findRangeByMonths = React.useCallback((months, ranges)=>{
    const safeMonths = Math.max(0, Number(months || 0));
    const source = Array.isArray(ranges) ? ranges : [];
    return source.find(range=> safeMonths >= Number(range?.minMonths || 0) && (range?.maxMonths == null || safeMonths <= Number(range.maxMonths))) || source[source.length-1] || source[0] || null;
  }, []);
  const getAnimalCurrentAgeMonths = React.useCallback((animal)=>{
    const dateValue = resolveDateForAnimalAge(animal);
    if(dateValue) return monthsBetween(dateValue);
    return Number(animal?.currentAgeMonths || animal?.avgAgeMonths || 0) || 0;
  }, [monthsBetween, resolveDateForAnimalAge]);
  const getLotCurrentAgeMonths = React.useCallback((lot) => {
    const approxBirth = resolveDateForLotAge(lot);
    if (approxBirth) return monthsBetween(approxBirth);
    const baseMonths = Number(lot?.avgAgeMonths ?? lot?.currentAgeMonths ?? 0);
    const carriedMonths = Number.isFinite(baseMonths) ? Math.max(0, baseMonths) : 0;
    return carriedMonths;
  }, [monthsBetween, resolveDateForLotAge]);
  const inferLotAgeRangeId = React.useCallback((lot) => {
    const range = findRangeByMonths(getLotCurrentAgeMonths(lot), getSpeciesAgeRanges(lot?.species || activeSpecies));
    return range?.id || '0-12';
  }, [activeSpecies, findRangeByMonths, getLotCurrentAgeMonths, getSpeciesAgeRanges]);
  const inferAnimalAgeRangeId = React.useCallback((animal)=>{
    const range = findRangeByMonths(getAnimalCurrentAgeMonths(animal), getSpeciesAgeRanges(animal?.species || activeSpecies));
    return range?.id || '';
  }, [activeSpecies, findRangeByMonths, getAnimalCurrentAgeMonths, getSpeciesAgeRanges]);
  const getEffectiveLotAgeRangeId = React.useCallback((lot) => {
    if (!lot) return '0-12';
    return inferLotAgeRangeId(lot) || String(lot?.ageRange || '0-12');
  }, [inferLotAgeRangeId]);
  const getEffectiveLotAgeRange = React.useCallback((lot) => {
    const source = getSpeciesAgeRanges(animalSpeciesOf(lot) || lot?.species);
    return source.find(item => item.id === getEffectiveLotAgeRangeId(lot)) || findRangeByMonths(getLotCurrentAgeMonths(lot), source) || source[0] || null;
  }, [animalSpeciesOf, findRangeByMonths, getEffectiveLotAgeRangeId, getLotCurrentAgeMonths, getSpeciesAgeRanges]);
  const getHighestBovineWeightRule = React.useCallback(()=>bovineWeightClassRules[bovineWeightClassRules.length-1] || null, [bovineWeightClassRules]);
  const isLotReadyForSlaughter = React.useCallback((lot) => {
    if (animalSpeciesOf(lot) !== "Bovino" || !lot?.terminationLot) return false;
    const highestRule = getHighestBovineWeightRule();
    if (!highestRule) return false;
    const arrobas = getCarcassArrobas(Number(lot?.avgWeight || 0), lot);
    return arrobas > Number(highestRule.minAt || highestRule.maxAt || 0);
  }, [animalSpeciesOf, getCarcassArrobas, getHighestBovineWeightRule]);
  const foalAnimals = speciesAnimals.filter(a=>String(a?.equinoCategory||"")==="potro");
  const serviceAnimals = speciesAnimals.filter(a=>String(a?.equinoCategory||"")==="service");
  const availableHarasEquines = React.useMemo(()=>{
    return speciesAnimals.filter(item=>animalSpeciesOf(item)==="Equino" && !["potro","service"].includes(String(item?.equinoCategory||"")));
  }, [speciesAnimals, animalSpeciesOf]);
  const foalParentOptions = React.useMemo(()=>{
    return speciesAnimals.filter(item=>animalSpeciesOf(item)==="Equino" && String(item?.status||"Ativo")!=="Inativo");
  }, [speciesAnimals, animalSpeciesOf]);
  const serviceNutritionEnabledCount = serviceAnimals.filter(item=>!!item?.rationConsumer).length;
  const serviceNutritionLatest = (serviceNutritionEntries||[]).slice().sort((a,b)=>String(b?.ts||b?.createdAt||"").localeCompare(String(a?.ts||a?.createdAt||"")))[0] || null;

  const foalNutritionLatest = (foalNutritionEntries||[]).slice().sort((a,b)=>String(b?.ts||b?.createdAt||"").localeCompare(String(a?.ts||a?.createdAt||"")))[0] || null;

  // ── Filtered animals ───────────────────────────────────────────
  const filteredAnimals = speciesAnimals.filter(a=>{
    if(filterCC!=="Todos"&&a.costCenter!==filterCC) return false;
    const t=search.toLowerCase();
    const speciesName = String(animalSpeciesOf(a) || a?.species || "").toLowerCase();
    return matchesRecordNumberSearch(a, t, [a?.name, a?.tag, speciesName, a?.costCenter, a?.lotName]);
  });
  const filteredLots = speciesLots;
  const bovineLots = speciesLots.filter(l=>animalSpeciesOf(l)==="Bovino" && !l?.birdStage);
  const terminatingBovineLots = React.useMemo(()=>bovineLots.filter(l=>!!l?.terminationLot), [bovineLots]);
  const terminatingAnimalCount = React.useMemo(()=>terminatingBovineLots.reduce((sum,l)=>sum + Number(l?.quantity||0), 0), [terminatingBovineLots]);
  const activeSpeciesCount = speciesAnimals.filter(a=>a?.status==="Ativo").length;
  const animalsInLotsCount = speciesAnimals.filter(a=>a?.lotId).length;

  const birdStageDefs = React.useMemo(() => ({
    day1: { key:"day1", label:"Aves de 1 Dia", short:"1 Dia", prefix:"AV1", minAge:1, transferAt:30, allowEggs:false },
    growth: { key:"growth", label:"Aves Crescimento", short:"Crescimento", prefix:"AVD", minAge:30, transferAt:60, allowEggs:false },
    corte: { key:"corte", label:"Aves Corte", short:"Corte", prefix:"AVT", minAge:60, transferAt:null, allowEggs:false },
    postura: { key:"postura", label:"Aves Postura", short:"Postura", prefix:"AVP", minAge:60, transferAt:null, allowEggs:true },
  }), []);
  const birdStages = Object.keys(birdStageDefs);
  const birdTab = birdStages.includes(tab) ? tab : "";
  const birdMortalityReasons = (SL.BIRD_MORTALITY_REASONS && SL.BIRD_MORTALITY_REASONS.length ? SL.BIRD_MORTALITY_REASONS : ["Comorbidades","Acidentes","Abate para Consumo","Predadores","Outro"]);
  const feedPauseReasons = (SL.FEED_PAUSE_REASONS && SL.FEED_PAUSE_REASONS.length ? SL.FEED_PAUSE_REASONS : ["Troca de dieta","Falta de produto no estoque","Orientação veterinária / zootécnica","Jejum programado","Animal em adaptação","Problema no cocho / fornecimento","Clima extremo","Lote temporariamente fora do manejo","Outro"]);
  const birdLots = speciesLots.filter(l=>l?.birdStage);
  const birdLotsByStage = birdStages.reduce((acc, stage)=>{
    acc[stage] = birdLots.filter(l=>String(l?.birdStage||"")===stage).sort((a,b)=>String(b?.formedAt||b?.createdAt||"").localeCompare(String(a?.formedAt||a?.createdAt||"")));
    return acc;
  }, {});

  const moduleCardsConfig = (()=>{
    try{
      const parsed = JSON.parse(localStorage.getItem("fz:moduleCardsConfig") || "null");
      return parsed && typeof parsed === "object" ? parsed : {};
    }catch(_){ return {}; }
  })();
  const getConfiguredSectionCards = React.useCallback((sectionId, fallback=[])=>{
    const moduleConfig = moduleCardsConfig?.[moduleId];
    if(moduleConfig && Object.prototype.hasOwnProperty.call(moduleConfig, sectionId)){
      const configured = moduleConfig?.[sectionId];
      if(Array.isArray(configured)) return configured.map(v=>String(v||""));
      return [];
    }
    return (Array.isArray(fallback) ? fallback : []).map(v=>String(v||""));
  }, [moduleCardsConfig]);
  const allowedAnimalRootCards = React.useMemo(()=>getConfiguredSectionCards("moduleHome", ["bovinos","ovinos","equinos"]), [getConfiguredSectionCards]);
  const allowedAvicultureRootCards = React.useMemo(()=>getConfiguredSectionCards("moduleHome", ["aves_1_dia","aves_crescimento","aves_corte","aves_postura"]), [getConfiguredSectionCards]);
  const filterConfiguredCards = React.useCallback((items=[], allowedIds=[], fallbackIds=[])=>{
    const normalizedAllowed = Array.isArray(allowedIds) ? allowedIds.map(v=>String(v||"")) : [];
    const normalizedFallback = Array.isArray(fallbackIds) ? fallbackIds.map(v=>String(v||"")) : [];
    const normalizedItems = (Array.isArray(items) ? items : []).map((item, index)=>({
      ...item,
      id: String(item?.id || normalizedFallback[index] || `card_${index}`),
    }));
    if(!normalizedAllowed.length) return [];
    return normalizedItems.filter(item=>normalizedAllowed.includes(item.id));
  }, []);
  const currentAnimalSection = React.useMemo(()=>{
    if(moduleId === "aviculture"){
      if(!birdTab) return { id:"moduleHome", cards:["aves_1_dia","aves_crescimento","aves_corte","aves_postura"] };
      return {
        id:`menu:aves_${birdTab === "day1" ? "1_dia" : birdTab === "growth" ? "crescimento" : birdTab === "corte" ? "corte" : "postura"}`,
        cards: birdTab === "day1"
          ? ["day1_lotes","day1_nutricao","day1_medicamentos","day1_mortes"]
          : birdTab === "growth"
            ? ["growth_lotes","growth_nutricao","growth_medicamentos","growth_mortes"]
            : birdTab === "corte"
              ? ["corte_lotes","corte_nutricao","corte_medicamentos","corte_mortes"]
              : ["postura_lotes","postura_nutricao","postura_medicamentos","postura_ovos"],
      };
    }
    if(activeSpecies === "Bovino"){
      if(tab === "animals") return { id:"submenu:bovinos:animais", cards:["bovino_animais_cadastro","bovino_animais_nutricao","bovino_animais_medicamentos"] };
      if(tab === "lots") return { id:"submenu:bovinos:lotes", cards:["bovino_lotes_resumo","bovino_lotes_edicao","bovino_lotes_nutricao","bovino_lotes_medicamentos"] };
    }
    if(activeSpecies === "Ovino"){
      if(tab === "animals") return { id:"submenu:ovinos:animais", cards:["ovino_animais_cadastro","ovino_animais_nutricao","ovino_animais_medicamentos"] };
      if(tab === "lots") return { id:"submenu:ovinos:lotes", cards:["ovino_lotes_resumo","ovino_lotes_edicao","ovino_lotes_nutricao","ovino_lotes_medicamentos"] };
    }
    if(activeSpecies === "Equino"){
      if(equineTab === "potros") return { id:"submenu:equinos:potros", cards:["potros_cadastro","potros_nutricao","potros_medicamentos","potros_historico"] };
      if(equineTab === "service") return { id:"submenu:equinos:servico", cards:["servico_cadastro","servico_nutricao","servico_medicamentos","servico_historico"] };
    }
    return null;
  }, [activeSpecies, tab, equineTab, birdTab]);
  const allowedCurrentSectionCards = React.useMemo(()=>{
    if(!currentAnimalSection) return [];
    return getConfiguredSectionCards(currentAnimalSection.id, currentAnimalSection.cards);
  }, [currentAnimalSection, getConfiguredSectionCards]);

  const animalsMenuItems = React.useMemo(()=>{
    if (!activeSpecies) {
      return [
        {
          id:"bovino-menu",
          label:"Bovinos",
          icon:"🐂",
          children:[
            { id:"bovino-animals", label:"Animais Individuais", icon:"🐂", badge:(animals||[]).filter(a=>animalSpeciesOf(a)==="Bovino").length, onClick:()=>{ setSpeciesTab("Bovino"); setTab("animals"); } },
            { id:"bovino-lots", label:"Lotes de Animais", icon:"📦", badge:(lots||[]).filter(l=>animalSpeciesOf(l)==="Bovino").length, onClick:()=>{ setSpeciesTab("Bovino"); setTab("lots"); } },
          ],
        },
        {
          id:"ovino-menu",
          label:"Ovinos",
          icon:"🐑",
          children:[
            { id:"ovino-animals", label:"Animais Individuais", icon:"🐑", badge:(animals||[]).filter(a=>animalSpeciesOf(a)==="Ovino").length, onClick:()=>{ setSpeciesTab("Ovino"); setTab("animals"); } },
            { id:"ovino-lots", label:"Lotes de Animais", icon:"📦", badge:(lots||[]).filter(l=>animalSpeciesOf(l)==="Ovino").length, onClick:()=>{ setSpeciesTab("Ovino"); setTab("lots"); } },
          ],
        },
        {
          id:"equino-menu-root",
          label:"Equinos",
          icon:"🐴",
          children:[
            { id:"equino-potro", label:"Potros", icon:"🐴", badge:(animals||[]).filter(a=>animalSpeciesOf(a)==="Equino" && String(a?.equinoCategory||"")==="potro").length, onClick:()=>{ setSpeciesTab("Equino"); setTab("potros"); } },
            { id:"equino-service", label:"Equinos Adultos", icon:"🛠️", badge:(animals||[]).filter(a=>animalSpeciesOf(a)==="Equino" && String(a?.equinoCategory||"")!=="potro").length, onClick:()=>{ setSpeciesTab("Equino"); setTab("service"); } },
          ],
        },
      ];
    }
    if (isBirdSpecies) {
      const children = birdStages.map(stageKey=>({
        id:stageKey,
        label:birdStageDefs[stageKey].label,
        icon:"🐔",
        badge:(birdLotsByStage[stageKey]||[]).length,
        active:birdTab===stageKey,
        onClick:()=>setTab(stageKey),
      }));
      return [{ id:"bird-menu", label:"Avicultura", icon:"🐔", active:birdStages.includes(tab), children }];
    }
    if (isEquineSpecies) {
      return [{
        id:"equine-menu",
        label:"Haras",
        icon:"🐴",
        active:["potros","service"].includes(tab),
        children:[
          { id:"potros", label:"Potros", icon:"🐴", badge:foalAnimals.length, active:equineTab==="potros", onClick:()=>setTab("potros") },
          { id:"service", label:"Equinos Adultos", icon:"🛠️", badge:serviceAnimals.length, active:equineTab==="service", onClick:()=>setTab("service") },
        ],
      }];
    }
    const isOvino = activeSpecies === "Ovino";
    return [{
      id:"species-menu",
      label:activeSpecies === "Bovino" ? "Pecuária" : (activeSpecies || "Pecuária"),
      icon:isOvino ? "🐑" : "🐂",
      active:["animals","lots"].includes(tab),
      children:[
        { id:"animals", label:"Animais Individuais", icon:isOvino ? "🐑" : "🐂", badge:speciesAnimals.length, active:tab==="animals", onClick:()=>setTab("animals") },
        { id:"lots", label:"Lotes de Animais", icon:"📦", badge:speciesLots.length, active:tab==="lots", onClick:()=>setTab("lots") },
      ],
    }];
  }, [activeSpecies, animals, lots, tab, isBirdSpecies, birdStages, birdStageDefs, birdLotsByStage, birdTab, isEquineSpecies, foalAnimals.length, serviceAnimals.length, equineTab, speciesAnimals.length, speciesLots.length, animalSpeciesOf]);
  const stockQtyById = (stockItems||[]).reduce((acc,item)=>{
    const qty = (stockMovements||[])
      .filter(m=>String(m?.itemId||"")===String(item?.id||""))
      .reduce((sum,m)=>{
        const q = Math.abs(Number(m?.qty||0));
        if(m?.type === "entrada" || m?.type === "devolucao") return sum + q;
        if(m?.type === "saida" || m?.type === "quebra") return sum - q;
        if(m?.type === "ajuste") return Number(m?.qty||0);
        return sum;
      }, 0);
    acc[String(item?.id||"")] = qty;
    return acc;
  }, {});
  const birdStockItems = (stockItems||[]).map(item=>({ ...item, quantity: Number(stockQtyById[String(item?.id||"")] || 0) }));
  const selectableBirdStockItems = birdStockItems.filter(item=>item && Number(item.quantity||0) > 0);

  const normalizeStockKey = React.useCallback((value="")=>String(value||"").normalize("NFD").replace(/[̀-ͯ]/g,"").trim().toLowerCase(), []);
  const normalizeProfiles = React.useCallback((profiles=[]) => Array.isArray(profiles) ? profiles : [], []);
  const itemSupportsSpecies = React.useCallback((item, species)=>{
    const targetSpecies = Array.isArray(item?.targetSpecies) ? item.targetSpecies.map(v=>String(v||"").trim()) : [];
    if(targetSpecies.length) return targetSpecies.includes(species);
    const allProfiles = [...normalizeProfiles(item?.nutritionProfiles), ...normalizeProfiles(item?.posologyProfiles)];
    if(allProfiles.length) return allProfiles.some(profile=>String(profile?.species||"")===String(species||""));
    return true;
  }, [normalizeProfiles]);
  const itemSupportsCategory = React.useCallback((item, kind, species, category)=>{
    const source = kind === "nutrition" ? normalizeProfiles(item?.nutritionProfiles) : normalizeProfiles(item?.posologyProfiles);
    if(!source.length) return true;
    const exact = source.some(profile=>String(profile?.species||"")===String(species||"") && String(profile?.category||"")===String(category||""));
    if(exact) return true;
    const speciesOnly = source.some(profile=>String(profile?.species||"")===String(species||"") && !String(profile?.category||"").trim());
    if(speciesOnly) return true;
    return itemSupportsSpecies(item, species);
  }, [normalizeProfiles, itemSupportsSpecies]);
  const isEquineRationItem = React.useCallback((item)=>{
    const cat = normalizeStockKey(item?.category);
    return (cat.includes("racao") || cat.includes("suplement")) && normalizeStockKey(item?.costCenter) === "haras" && itemSupportsSpecies(item, "Equino");
  }, [normalizeStockKey, itemSupportsSpecies]);
  const isEquineMedicationItem = React.useCallback((item)=>{
    const cc = normalizeStockKey(item?.costCenter);
    return normalizeStockKey(item?.category).includes("medic") && (cc === "haras" || cc === "pecuaria") && itemSupportsSpecies(item, "Equino");
  }, [normalizeStockKey, itemSupportsSpecies]);
  const equineRationStockItems = React.useMemo(()=>selectableBirdStockItems.filter(isEquineRationItem).sort((a,b)=>String(a?.name||"").localeCompare(String(b?.name||""), "pt-BR")), [selectableBirdStockItems, isEquineRationItem]);
  const equineMedicationStockItems = React.useMemo(()=>selectableBirdStockItems.filter(isEquineMedicationItem).sort((a,b)=>String(a?.name||"").localeCompare(String(b?.name||""), "pt-BR")), [selectableBirdStockItems, isEquineMedicationItem]);
  const commonEquineReapplyIntervals = [
    { value:"7", label:"7 dias" },
    { value:"14", label:"14 dias" },
    { value:"21", label:"21 dias" },
    { value:"28", label:"28 dias" },
    { value:"30", label:"30 dias" },
    { value:"42", label:"42 dias (6 semanas)" },
    { value:"180", label:"180 dias (6 meses)" },
    { value:"custom", label:"Outro intervalo" },
  ];
  const equineNutritionPeriods = [
    { value:"Diário", label:"Diário" },
    { value:"Semanal", label:"Semanal" },
    { value:"Mensal", label:"Mensal" },
  ];

  const birdNutritionPeriods = equineNutritionPeriods;
  const birdCategoryByStage = React.useCallback((stageKey)=>({
    day1:"Aves de 1 Dia",
    growth:"Aves Crescimento",
    corte:"Aves Corte",
    postura:"Aves Postura",
  }[stageKey] || "Aves Crescimento"), []);
  const bovineCategoryByAgeRange = React.useCallback((rangeId, sex="", purpose="")=>{
    if(String(rangeId||"")==="0-12") return "Bezerros";
    if(String(rangeId||"")==="13-24") return "Recria";
    if(String(rangeId||"")==="25-36") return "Engorda";
    return resolveBovineCategoryByAgeMonths(37, sex, purpose);
  }, []);
  const bovineCategoriesForLot = React.useCallback((lotLike={})=>{
    const rangeId = getEffectiveLotAgeRangeId(lotLike);
    if(rangeId !== "36+") return [bovineCategoryByAgeRange(rangeId, lotLike?.sex, lotLike?.purpose)];
    const sex = String(lotLike?.sex||"").trim();
    if(sex === "Macho") return ["Marrucos"];
    if(sex === "Fêmea") return ["Matrizes"];
    return ["Marrucos","Matrizes"];
  }, [getEffectiveLotAgeRangeId, bovineCategoryByAgeRange]);
  const itemSupportsAnyCategory = React.useCallback((item, kind, species, categories=[])=>{
    const list = Array.isArray(categories) ? categories.filter(Boolean) : [];
    if(!list.length) return itemSupportsCategory(item, kind, species, "");
    return list.some(category=>itemSupportsCategory(item, kind, species, category));
  }, [itemSupportsCategory]);
  const defaultNutritionProfiles = React.useMemo(()=>({
    Ave:{
      "Aves de 1 Dia": { basis:"perAnimal", dose:0.03, doseUnit:"kg/dia", notes:"Faixa inicial de consumo diário por ave em manejo inicial." },
      "Aves Crescimento": { basis:"perAnimal", dose:0.09, doseUnit:"kg/dia", notes:"Consumo diário médio por ave em crescimento." },
      "Aves Corte": { basis:"perAnimal", dose:0.12, doseUnit:"kg/dia", notes:"Consumo diário médio por ave em fase de corte." },
      "Aves Postura": { basis:"perAnimal", dose:0.115, doseUnit:"kg/dia", notes:"Consumo diário médio por ave em postura." },
    },
    Bovino:{
      "Bezerros": { basis:"perAnimal", dose:2.5, doseUnit:"kg/dia", notes:"Faixa inicial simplificada para bezerros." },
      "Recria": { basis:"perAnimal", dose:5.5, doseUnit:"kg/dia", notes:"Faixa simplificada para bovinos em recria." },
      "Engorda": { basis:"perAnimal", dose:8, doseUnit:"kg/dia", notes:"Faixa simplificada para bovinos em terminação." },
      "Matrizes": { basis:"perAnimal", dose:7, doseUnit:"kg/dia", notes:"Faixa simplificada para vacas adultas." },
    },
    Ovino:{
      "Cordeiros": { basis:"perAnimal", dose:0.35, doseUnit:"kg/dia", notes:"Faixa inicial simplificada para cordeiros." },
      "Crescimento": { basis:"perAnimal", dose:0.5, doseUnit:"kg/dia", notes:"Faixa simplificada para ovinos em crescimento." },
      "Matrizes": { basis:"perAnimal", dose:0.3, doseUnit:"kg/dia", notes:"Concentrado diário comum para matrizes com bom volumoso." },
      "Reprodutores": { basis:"perAnimal", dose:0.45, doseUnit:"kg/dia", notes:"Faixa simplificada para reprodutores." },
    },
  }), []);
  const commonBirdMedicationIntervals = [
    { value:"1", label:"1 dia" },
    { value:"3", label:"3 dias" },
    { value:"5", label:"5 dias" },
    { value:"7", label:"7 dias" },
    { value:"14", label:"14 dias" },
    { value:"21", label:"21 dias" },
    { value:"custom", label:"Outro intervalo" },
  ];
  const isBirdNutritionItem = React.useCallback((item)=>{
    const cat = normalizeStockKey(item?.category);
    const cc = normalizeStockKey(item?.costCenter);
    return (cat.includes("racao") || cat.includes("suplement")) && cc === "pecuaria" && itemSupportsSpecies(item, "Ave");
  }, [normalizeStockKey, itemSupportsSpecies]);
  const isBirdMedicationItem = React.useCallback((item)=>{
    const cat = normalizeStockKey(item?.category);
    const cc = normalizeStockKey(item?.costCenter);
    return cat.includes("medic") && cc === "pecuaria" && itemSupportsSpecies(item, "Ave");
  }, [normalizeStockKey, itemSupportsSpecies]);
  const birdNutritionStockItems = React.useMemo(()=>selectableBirdStockItems.filter(isBirdNutritionItem).sort((a,b)=>String(a?.name||"").localeCompare(String(b?.name||""), "pt-BR")), [selectableBirdStockItems, isBirdNutritionItem]);
  const birdMedicationStockItems = React.useMemo(()=>selectableBirdStockItems.filter(isBirdMedicationItem).sort((a,b)=>String(a?.name||"").localeCompare(String(b?.name||""), "pt-BR")), [selectableBirdStockItems, isBirdMedicationItem]);
  const isBovineNutritionItem = React.useCallback((item)=>{
    const cat = normalizeStockKey(item?.category);
    const cc = normalizeStockKey(item?.costCenter);
    return (cat.includes("racao") || cat.includes("suplement")) && cc === "pecuaria" && itemSupportsSpecies(item, "Bovino");
  }, [normalizeStockKey, itemSupportsSpecies]);
  const isBovineMedicationItem = React.useCallback((item)=>{
    const cat = normalizeStockKey(item?.category);
    const cc = normalizeStockKey(item?.costCenter);
    return cat.includes("medic") && cc === "pecuaria" && itemSupportsSpecies(item, "Bovino");
  }, [normalizeStockKey, itemSupportsSpecies]);
  const bovineNutritionStockItems = React.useMemo(()=>selectableBirdStockItems.filter(isBovineNutritionItem).sort((a,b)=>String(a?.name||"").localeCompare(String(b?.name||""), "pt-BR")), [selectableBirdStockItems, isBovineNutritionItem]);
  const bovineMedicationStockItems = React.useMemo(()=>selectableBirdStockItems.filter(isBovineMedicationItem).sort((a,b)=>String(a?.name||"").localeCompare(String(b?.name||""), "pt-BR")), [selectableBirdStockItems, isBovineMedicationItem]);
  const profileMatches = React.useCallback((profile, species, category, sex="")=>{
    const sameSpecies = String(profile?.species||"")===String(species||"");
    const sameCategory = String(profile?.category||"")===String(category||"");
    if(!sameSpecies || !sameCategory) return false;
    const profileSex = String(profile?.sex||"Ambos").trim() || "Ambos";
    return !sex || profileSex === "Ambos" || profileSex === String(sex||"");
  }, []);
  const getStockProfile = React.useCallback((item, kind, species, category, sex="")=>{
    const source = kind === "nutrition" ? normalizeProfiles(item?.nutritionProfiles) : normalizeProfiles(item?.posologyProfiles);
    return source.find(profile=>profileMatches(profile, species, category, sex))
      || source.find(profile=>String(profile?.species||"")===String(species||"") && String(profile?.category||"")===String(category||"") && String(profile?.sex||"Ambos") === "Ambos")
      || source.find(profile=>String(profile?.species||"")===String(species||"") && !String(profile?.category||"").trim() && (!sex || String(profile?.sex||"Ambos") === "Ambos" || String(profile?.sex||"")===String(sex||"")))
      || source.find(profile=>String(profile?.species||"")===String(species||"") && (!sex || String(profile?.sex||"Ambos") === "Ambos" || String(profile?.sex||"")===String(sex||"")))
      || source.find(profile=>String(profile?.species||"")===String(species||"") && !String(profile?.category||"").trim())
      || source.find(profile=>String(profile?.species||"")===String(species||""))
      || null;
  }, [normalizeProfiles, profileMatches]);
  const getDefaultNutritionProfile = React.useCallback((species, category)=>defaultNutritionProfiles?.[species]?.[category] || null, [defaultNutritionProfiles]);
  const fmtMeasure = React.useCallback((value, digits=3)=>Number(value || 0).toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: digits }), []);
  const commonBovineMedicationIntervals = [
    { value:"3", label:"3 dias" },
    { value:"7", label:"7 dias" },
    { value:"14", label:"14 dias" },
    { value:"21", label:"21 dias" },
    { value:"28", label:"28 dias" },
    { value:"30", label:"30 dias" },
    { value:"42", label:"42 dias" },
  ];
  const computeSuggestedUsage = React.useCallback(({ item, kind="nutrition", species="Ave", category="", sex="", animalCount=0, avgWeightKg=0 })=>{
    const productProfile = getStockProfile(item, kind, species, category, sex);
    const profile = productProfile || (kind === "nutrition" ? getDefaultNutritionProfile(species, category) : null);
    if(!profile) return { profile:null, qty:null, formula:"", compatibility:null };
    const dose = Number(profile?.dose||0);
    if(!(dose>0)) return { profile, qty:null, formula:"" };
    let qty = null;
    if(profile.basis === "perAnimal") qty = animalCount * dose;
    else if(profile.basis === "per100Animals") qty = (animalCount/100) * dose;
    else if(profile.basis === "perKgLiveweight") {
      const weight = Number(avgWeightKg || profile?.referenceWeightKg || 0);
      if(weight > 0) qty = animalCount * weight * dose;
    }
    const rounded = qty == null ? null : Math.round(qty * 1000) / 1000;
    const profileSex = String(profile?.sex || "Ambos").trim() || "Ambos";
    const compatibility = kind === "medication"
      ? {
          speciesAllowed: String(profile?.species||species||"") === String(species||""),
          categoryAllowed: !String(profile?.category||"").trim() || String(profile?.category||"") === String(category||""),
          sexAllowed: !sex || profileSex === "Ambos" || profileSex === String(sex||""),
          profileSex,
          allowed: profile?.allowed !== false,
          weightPending: kind === "medication" && !(Number(avgWeightKg || profile?.referenceWeightKg || 0) > 0),
        }
      : null;
    const formula = profile.basis === "perAnimal"
      ? `${dose} ${profile.doseUnit||"un"} × ${animalCount} animal(is)`
      : profile.basis === "per100Animals"
        ? `${dose} ${profile.doseUnit||"un"} a cada 100 animais · lote ${animalCount}`
        : `${dose} ${profile.doseUnit||"un"} × ${animalCount} animal(is) × ${Number(avgWeightKg || profile?.referenceWeightKg || 0)} kg`;
    return { profile, qty: rounded, formula, compatibility, source: productProfile ? "produto" : (kind === "nutrition" ? "referência padrão" : "") };
  }, [getStockProfile, getDefaultNutritionProfile]);
  const getItemPackKg = React.useCallback((item)=>{
    if(!item) return null;
    const unit = normalizeStockKey(item?.unit);
    const refUnit = normalizeStockKey(item?.pkgUnitRef);
    const pkgQty = Number(item?.pkgQty || 0);
    if(unit === "kg") return 1;
    if(unit.includes("kg")) return 1;
    if((unit.includes("saco") || unit.includes("saca") || unit.includes("pacote") || unit.includes("fardo")) && pkgQty > 0 && refUnit.includes("kg")) return pkgQty;
    if(pkgQty > 0 && refUnit.includes("kg")) return pkgQty;
    return null;
  }, [normalizeStockKey]);
  const getStockQtyInKg = React.useCallback((item)=>{
    const packKg = getItemPackKg(item);
    if(packKg != null) return Number(item?.quantity||0) * packKg;
    return null;
  }, [getItemPackKg]);
  const roundStockQtyByPack = React.useCallback((item, totalKg)=>{
    const packKg = getItemPackKg(item);
    if(packKg && packKg > 0) return Math.max(0, Math.ceil(Number(totalKg || 0) / packKg));
    return Math.round(Math.max(0, Number(totalKg || 0)) * 1000) / 1000;
  }, [getItemPackKg]);
  const getBirdLotById = React.useCallback((lotId)=>birdLots.find(l=>String(l.id)===String(lotId)), [birdLots]);

  const birdAgeDays = React.useCallback((lot)=>{
    const start = lot?.formedAt || lot?.entryDate || lot?.createdAt;
    const baseAge = Number(lot?.initialAgeDays || 0);
    if(!start) return baseAge;
    const diff = Math.floor((Date.now() - new Date(start).getTime()) / 86400000);
    return Math.max(baseAge, baseAge + (Number.isFinite(diff) ? diff : 0));
  }, []);

  const birdMortalityQty = React.useCallback((lot)=>{
    return (lot?.mortalityEntries||[]).reduce((sum, entry)=>sum + Math.max(0, Number(entry?.qty||0)), 0);
  }, []);

  const birdTransferQty = React.useCallback((lot)=>{
    return (lot?.transfersOut||[]).reduce((sum, entry)=>sum + Math.max(0, Number(entry?.qty||0)), 0);
  }, []);

  const birdCurrentQty = React.useCallback((lot)=>{
    return Math.max(0, Number(lot?.quantity||0) - birdMortalityQty(lot) - birdTransferQty(lot));
  }, [birdMortalityQty, birdTransferQty]);

  const toLocalDateTimeParts = React.useCallback((value)=>{
    const date = value ? new Date(value) : new Date();
    const safe = Number.isNaN(date.getTime()) ? new Date() : date;
    const local = new Date(safe.getTime() - safe.getTimezoneOffset()*60000);
    const iso = local.toISOString();
    return { date: iso.slice(0,10), time: iso.slice(11,16) };
  }, []);

  const makeLocalEggTimestamp = React.useCallback((dateValue, timeValue)=>{
    const datePart = String(dateValue || today() || '').slice(0,10);
    const timePart = String(timeValue || '00:00').slice(0,5);
    const composed = new Date(`${datePart}T${timePart}:00`);
    if(Number.isNaN(composed.getTime())) return new Date().toISOString();
    return composed.toISOString();
  }, []);

  const birdEggAverage = React.useCallback((lot)=>{
    const now = Date.now();
    const entries = (lot?.eggEntries || []).filter(entry=>{
      const ts = new Date(entry?.ts || entry?.date || '').getTime();
      return Number.isFinite(ts) && (now - ts) <= (30 * 86400000);
    });
    if(!entries.length) return 0;
    const total = entries.reduce((sum, entry)=>sum + Math.max(0, Number(entry?.qty||0)), 0);
    return Math.round((total / 30) * 10) / 10;
  }, []);

  const getBirdEggDailyGroups = React.useCallback((lot)=>{
    const grouped = new Map();
    (lot?.eggEntries || []).forEach(entry=>{
      const ts = entry?.ts || entry?.date || '';
      const parts = toLocalDateTimeParts(ts);
      const key = parts.date;
      if(!grouped.has(key)) grouped.set(key, { date:key, entries:[], totalQty:0, withNotes:false });
      const group = grouped.get(key);
      group.entries.push({ ...entry, _date:key, _time:parts.time });
      group.totalQty += Math.max(0, Number(entry?.qty||0));
      if(String(entry?.notes||'').trim()) group.withNotes = true;
    });
    return Array.from(grouped.values()).sort((a,b)=> String(b.date||'').localeCompare(String(a.date||'')));
  }, [toLocalDateTimeParts]);

  const getBirdEggMonthAverage = React.useCallback((lot, dayKey)=>{
    if(!lot || !dayKey) return 0;
    const [year, month, day] = String(dayKey).split('-').map(Number);
    if(!year || !month || !day) return 0;
    const total = (lot.eggEntries || []).reduce((sum, entry)=>{
      const parts = toLocalDateTimeParts(entry?.ts || entry?.date || '');
      if(parts.date && parts.date.startsWith(`${year}-${String(month).padStart(2,'0')}-`) && parts.date <= dayKey){
        return sum + Math.max(0, Number(entry?.qty||0));
      }
      return sum;
    }, 0);
    return Math.round((total / Math.max(1, day)) * 10) / 10;
  }, [toLocalDateTimeParts]);

  const getBirdNutritionSuggestion = React.useCallback((form)=>{
    const lot = getBirdLotById(form?.lotId);
    const stageCategory = birdCategoryByStage(form?.stageKey || lot?.birdStage);
    const item = birdNutritionStockItems.find(s=>String(s.id)===String(form?.stockId||"") && itemSupportsCategory(s, "nutrition", "Ave", stageCategory));
    if(!lot || !item) return { profile:null, qty:null, formula:"" };
    const count = Math.max(0, Number(form?.animalCount || birdCurrentQty(lot) || 0));
    return computeSuggestedUsage({ item, kind:"nutrition", species:"Ave", category:stageCategory, animalCount:count, avgWeightKg:Number(form?.avgWeightKg||0) });
  }, [birdNutritionStockItems, birdCurrentQty, birdCategoryByStage, computeSuggestedUsage, getBirdLotById, itemSupportsCategory]);
  const getBirdMedicationSuggestion = React.useCallback((form)=>{
    const lot = getBirdLotById(form?.lotId);
    const stageCategory = birdCategoryByStage(form?.animalCategory || lot?.birdStage);
    const item = birdMedicationStockItems.find(s=>String(s.id)===String(form?.stockId||"") && itemSupportsCategory(s, "medication", "Ave", stageCategory));
    if(!lot || !item) return { profile:null, qty:null, formula:"" };
    const count = Math.max(0, Number(form?.animalCount || birdCurrentQty(lot) || 0));
    return computeSuggestedUsage({ item, kind:"medication", species:"Ave", category:stageCategory, sex:String(form?.sex||"").trim(), animalCount:count, avgWeightKg:Number(form?.avgWeightKg||0) });
  }, [birdMedicationStockItems, birdCurrentQty, birdCategoryByStage, computeSuggestedUsage, getBirdLotById, itemSupportsCategory]);
  const getLivestockLotById = React.useCallback((lotId)=>speciesLots.find(l=>String(l.id)===String(lotId)), [speciesLots]);
  const getBovineNutritionSuggestion = React.useCallback((form)=>{
    const lot = getLivestockLotById(form?.lotId);
    const lotLike = { ...(lot||{}), ageRange:String(form?.ageRange || getEffectiveLotAgeRangeId(lot) || "0-12"), sex:form?.sex || lot?.sex, purpose:form?.purpose || lot?.purpose };
    const categories = bovineCategoriesForLot(lotLike);
    const selected = bovineNutritionStockItems.find(s=>String(s.id)===String(form?.stockId||"") && itemSupportsAnyCategory(s, "nutrition", "Bovino", categories));
    const count = Math.max(0, Number(form?.animalCount || lot?.quantity || 0));
    if(!lot || !selected || count <= 0) return { profile:null, qty:null, formula:"", categories };
    let result = null;
    for(const category of categories){
      result = computeSuggestedUsage({ item:selected, kind:"nutrition", species:"Bovino", category, animalCount:count, avgWeightKg:Number(form?.avgWeightKg || lot?.avgWeight || 0) });
      if(result?.profile) return { ...result, categories, categoryUsed:category };
    }
    result = computeSuggestedUsage({ item:selected, kind:"nutrition", species:"Bovino", category:categories[0]||"", animalCount:count, avgWeightKg:Number(form?.avgWeightKg || lot?.avgWeight || 0) });
    return { ...result, categories, categoryUsed:categories[0]||"" };
  }, [getLivestockLotById, getEffectiveLotAgeRangeId, bovineCategoriesForLot, bovineNutritionStockItems, itemSupportsAnyCategory, computeSuggestedUsage]);
  const getBovineNutritionPlan = React.useCallback((form)=>{
    const lot = getLivestockLotById(form?.lotId);
    const lotLike = { ...(lot||{}), ageRange:String(form?.ageRange || getEffectiveLotAgeRangeId(lot) || "0-12"), sex:form?.sex || lot?.sex, purpose:form?.purpose || lot?.purpose };
    const categories = bovineCategoriesForLot(lotLike);
    const item = bovineNutritionStockItems.find(s=>String(s.id)===String(form?.stockId||"") && itemSupportsAnyCategory(s, "nutrition", "Bovino", categories));
    const count = Math.max(0, Number(form?.animalCount || lot?.quantity || 0));
    const perHeadKgDay = Math.max(0, Number(form?.consumptionPerHeadKgDay || 0));
    const dailyKg = perHeadKgDay * count;
    const period = String(form?.period || "Diário");
    const totalKg = dailyKg;
    const packKg = getItemPackKg(item);
    const qtyToLaunch = roundStockQtyByPack(item, totalKg);
    const availableKg = getStockQtyInKg(item);
    const stockDays = dailyKg > 0 && availableKg != null ? (availableKg / dailyKg) : null;
    const suggestion = getBovineNutritionSuggestion(form);
    return {
      lot,
      item,
      period,
      animalCount: count,
      categories,
      perHeadKgDay,
      dailyKg,
      weeklyKg: dailyKg * 7,
      monthlyKg: dailyKg * 30,
      packKg,
      qtyToLaunch: Math.round((qtyToLaunch || 0) * 1000) / 1000,
      availableKg,
      stockDays: stockDays == null ? null : Math.round(stockDays * 10) / 10,
      suggestion,
    };
  }, [getLivestockLotById, getEffectiveLotAgeRangeId, bovineCategoriesForLot, bovineNutritionStockItems, itemSupportsAnyCategory, getItemPackKg, getStockQtyInKg, getBovineNutritionSuggestion, roundStockQtyByPack]);

  const getBovineMedicationSuggestion = React.useCallback((form)=>{
    const lot = getLivestockLotById(form?.lotId);
    const lotLike = { ...(lot||{}), ageRange:String(form?.ageRange || getEffectiveLotAgeRangeId(lot) || "0-12"), sex:form?.sex || lot?.sex, purpose:form?.purpose || lot?.purpose };
    const categories = bovineCategoriesForLot(lotLike);
    const selected = bovineMedicationStockItems.find(s=>String(s.id)===String(form?.stockId||"") && itemSupportsAnyCategory(s, "medication", "Bovino", categories));
    const count = Math.max(0, Number(form?.animalCount || lot?.quantity || 0));
    if(!lot || !selected || count <= 0) return { profile:null, qty:null, formula:"", categories };
    let result = null;
    for(const category of categories){
      result = computeSuggestedUsage({ item:selected, kind:"medication", species:"Bovino", category, sex:String(form?.sex || lot?.sex || "").trim(), animalCount:count, avgWeightKg:Number(form?.avgWeightKg || lot?.avgWeight || 0) });
      if(result?.profile) return { ...result, categories, categoryUsed:category };
    }
    result = computeSuggestedUsage({ item:selected, kind:"medication", species:"Bovino", category:categories[0]||"", sex:String(form?.sex || lot?.sex || "").trim(), animalCount:count, avgWeightKg:Number(form?.avgWeightKg || lot?.avgWeight || 0) });
    return { ...result, categories, categoryUsed:categories[0]||"" };
  }, [getLivestockLotById, getEffectiveLotAgeRangeId, bovineCategoriesForLot, bovineMedicationStockItems, itemSupportsAnyCategory, computeSuggestedUsage]);

  const buildBovineMedicationReviewNote = React.useCallback((form, suggestion)=>{
    const notes = [];
    const userName = session?.name || "Usuário";
    const suggestedQty = Number(suggestion?.qty || 0);
    const enteredQty = Number(form?.qty || 0);
    const suggestedDoseText = String(suggestion?.profile?.notes || "").trim();
    const enteredDoseText = String(form?.dosageUsed || "").trim();
    if(suggestedQty > 0 && enteredQty > 0 && Math.abs(enteredQty - suggestedQty) > 0.0001){
      notes.push(`Revisado por ${userName}: quantidade ajustada de ${suggestedQty} para ${enteredQty}.`);
    }
    if(suggestedDoseText && enteredDoseText && enteredDoseText !== suggestedDoseText){
      notes.push(`Revisado por ${userName}: posologia ajustada.`);
    }
    return notes.join(" ");
  }, [session?.name]);
  const buildBovineNutritionReviewNote = React.useCallback((form, plan)=>{
    const notes = [];
    const userName = session?.name || "Usuário";
    const suggestedQty = Number(plan?.qtyToLaunch || 0);
    const enteredQty = Number(form?.qty || suggestedQty || 0);
    const suggestedPerHead = Number(plan?.perHeadKgDay || 0);
    const enteredPerHead = Number(form?.consumptionPerHeadKgDay || 0);
    if(suggestedPerHead > 0 && enteredPerHead > 0 && Math.abs(enteredPerHead - suggestedPerHead) > 0.0001){
      notes.push(`Revisado por ${userName}: consumo por cabeça ajustado de ${suggestedPerHead} para ${enteredPerHead} kg/dia.`);
    }
    if(suggestedQty > 0 && enteredQty > 0 && Math.abs(enteredQty - suggestedQty) > 0.0001){
      notes.push(`Revisado por ${userName}: quantidade a baixar ajustada de ${suggestedQty} para ${enteredQty}.`);
    }
    return notes.join(" ");
  }, [session?.name]);

  const nutritionEntryBucket = React.useCallback((entry)=>{
    const item = bovineNutritionStockItems.find(stock=>String(stock.id)===String(entry?.stockId||""));
    const key = normalizeStockKey(item?.category || item?.name || entry?.stockName || "");
    return key.includes("suplement") ? "supplement" : "ration";
  }, [bovineNutritionStockItems, normalizeStockKey]);

  const buildBovineNutritionAudit = React.useCallback((lotLike={}, range={})=>{
    const start = String(range?.start || lotLike?.formedAt || "");
    const end = String(range?.end || today());
    const entries = (lotLike?.nutritionEntries||[]).filter(entry=>{
      const day = String(entry?.ts || "").slice(0,10);
      if(start && day < start) return false;
      if(end && day > end) return false;
      return true;
    });
    const summary = {
      rationSacks:0,
      supplementSacks:0,
      rationKg:0,
      supplementKg:0,
      avgPerHeadKgDay:0,
      launches:entries.length,
    };
    entries.forEach(entry=>{
      const bucket = nutritionEntryBucket(entry);
      const qty = Number(entry?.qty||0);
      const packKg = Number(entry?.packKg||0);
      if(bucket === "supplement"){
        summary.supplementSacks += qty;
        summary.supplementKg += packKg > 0 ? qty * packKg : 0;
      } else {
        summary.rationSacks += qty;
        summary.rationKg += packKg > 0 ? qty * packKg : 0;
      }
      summary.avgPerHeadKgDay += Number(entry?.consumptionPerHeadKgDay||0);
    });
    summary.avgPerHeadKgDay = entries.length ? (summary.avgPerHeadKgDay / entries.length) : 0;
    const points = [];
    const formationTs = lotLike?.formedAt || lotLike?.createdAt || "";
    const formationMovement = (lotLike?.movements||[]).find(m=>String(m?.type||"") === "formação") || null;
    const formationWeight = Number(formationMovement?.weight || lotLike?.avgWeight || 0);
    if(formationTs && formationWeight > 0) points.push({ ts:formationTs, weight:formationWeight, carcassYieldPct:Number(formationMovement?.carcassYieldPct || lotLike?.carcassYieldPct || 50), label:"Pesagem inicial" });
    (lotLike?.movements||[])
      .filter(m=>String(m?.type||"") === "pesagem" && Number(m?.weight||0) > 0)
      .forEach(m=>points.push({ ts:m.ts, weight:Number(m.weight||0), carcassYieldPct:Number(m.carcassYieldPct || lotLike?.carcassYieldPct || 50), label:m.note || "Pesagem" }));
    points.sort((a,b)=>String(a.ts).localeCompare(String(b.ts)));
    const gainWindows = [];
    for(let i=1;i<points.length;i+=1){
      const prev = points[i-1];
      const curr = points[i];
      const diffDays = Math.max(1, Math.round((new Date(curr.ts) - new Date(prev.ts)) / 86400000));
      const prevCarcassWeight = Number(prev.weight||0) * (Number(prev.carcassYieldPct||50) / 100);
      const currCarcassWeight = Number(curr.weight||0) * (Number(curr.carcassYieldPct||50) / 100);
      const gainPerHead = currCarcassWeight - prevCarcassWeight;
      const intervalEntries = entries.filter(entry=>{
        const ts = new Date(entry?.ts || 0).getTime();
        return ts >= new Date(prev.ts).getTime() && ts <= new Date(curr.ts).getTime();
      });
      const interval = { rationSacks:0, supplementSacks:0, rationKg:0, supplementKg:0, avgPerHeadKgDay:0, launches:intervalEntries.length };
      intervalEntries.forEach(entry=>{
        const bucket = nutritionEntryBucket(entry);
        const qty = Number(entry?.qty||0);
        const packKg = Number(entry?.packKg||0);
        if(bucket === "supplement"){
          interval.supplementSacks += qty;
          interval.supplementKg += packKg > 0 ? qty * packKg : 0;
        } else {
          interval.rationSacks += qty;
          interval.rationKg += packKg > 0 ? qty * packKg : 0;
        }
        interval.avgPerHeadKgDay += Number(entry?.consumptionPerHeadKgDay||0);
      });
      interval.avgPerHeadKgDay = intervalEntries.length ? (interval.avgPerHeadKgDay / intervalEntries.length) : 0;
      gainWindows.push({
        from: prev.ts,
        to: curr.ts,
        fromWeight: prev.weight,
        toWeight: curr.weight,
        fromCarcassWeight: prevCarcassWeight,
        toCarcassWeight: currCarcassWeight,
        fromCarcassYieldPct: Number(prev.carcassYieldPct||50),
        toCarcassYieldPct: Number(curr.carcassYieldPct||50),
        days: diffDays,
        gainPerHead,
        gainPerHeadDay: gainPerHead / diffDays,
        nutrition: interval,
      });
    }
    return { start, end, entries, summary, gainWindows };
  }, [nutritionEntryBucket]);

  const closeAllLivestockInlinePanels = React.useCallback(()=>{
    setDetailLot(null);
    setLotForm(null);
    setLotNutritionForm(null);
    setLotMedicationForm(null);
    setLotFormFocusSection("");
  }, []);

  const currentLivestockInlineLotId = React.useMemo(()=> String(detailLot?.id || lotForm?.id || lotNutritionForm?.lotId || lotMedicationForm?.lotId || ""), [detailLot, lotForm, lotNutritionForm, lotMedicationForm]);
  const isAnimalInlineFocused = !!form;
  const isLotInlineFocused = !!lotForm;
  const responsiveTwoColGrid = isCompactViewport()?"1fr":"repeat(2,minmax(0,1fr))";
  const dateFieldInputStyle = { boxSizing:"border-box", minWidth:0, maxWidth:"100%", width:"100%" };

  const openLivestockLotDetail = React.useCallback((lot)=>{
    if(!lot) return;
    setLotForm(null);
    setLotNutritionForm(null);
    setLotMedicationForm(null);
    setLotFormFocusSection("");
    setDetailLot(lot);
    requestAnimationFrame(()=>{ try{ window.scrollTo?.({ top:0, behavior:"smooth" }); }catch(_e){} });
  }, []);

  const openLotEditInline = React.useCallback((lot)=>{
    if(!lot) return;
    setDetailLot(null);
    setLotNutritionForm(null);
    setLotMedicationForm(null);
    const selectedAnimalIds = Array.isArray(lot?.selectedAnimalIds) && lot.selectedAnimalIds.length
      ? lot.selectedAnimalIds.map(v=>String(v||''))
      : ((trackedLotMembershipMap.get(String(lot?.registryUid || lot?.id || '')) || trackedLotMembershipMap.get(String(lot?.id || '')) || []).map(item=>String(item?.id||'')));
    setLotForm({ ...lot, _new:false, selectedAnimalIds, ageRangeManual:lot.ageRangeManual||lot.ageRange||"0-12", avgAgeMonths: lot?.avgAgeMonths === '' ? '' : String(lot?.avgAgeMonths ?? (lot?.birthDateApprox ? monthsBetween(lot.birthDateApprox) : '')) });
    setLotFormFocusSection("");
    requestAnimationFrame(()=>{ try{ window.scrollTo?.({ top:0, behavior:"smooth" }); }catch(_e){} });
  }, [monthsBetween, trackedLotMembershipMap]);

  const openLotNutritionEditor = React.useCallback((lot)=>{
    if(!lot) return;
    setDetailLot(null);
    setLotForm(null);
    setLotMedicationForm(null);
    const baseForm = {
      lotId: lot.id,
      stockId:"",
      qty:"",
      animalCount:String(lot.quantity||0),
      avgWeightKg:String(lot.avgWeight||0),
      ageRange:getEffectiveLotAgeRangeId(lot),
      sex:lot.sex||"",
      purpose:lot.purpose||"",
      consumptionPerHeadKgDay:String(plannedLotNutritionPerHeadKg(lot)||lot.dailyFeedKg||""),
      notes:"",
      pauseReason:"",
      pauseNotes:"",
    };
    const plan = getBovineNutritionPlan(baseForm);
    setLotNutritionForm({ ...baseForm, qty:String(plan?.qtyToLaunch||"") });
    setLotFormFocusSection("");
    requestAnimationFrame(()=>{ try{ window.scrollTo?.({ top:0, behavior:"smooth" }); }catch(_e){} });
  }, [getBovineNutritionPlan, getEffectiveLotAgeRangeId, plannedLotNutritionPerHeadKg]);

  const openLotMedicationEditor = React.useCallback((lot)=>{
    if(!lot) return;
    setDetailLot(null);
    setLotForm(null);
    setLotNutritionForm(null);
    setLotFormFocusSection("");
    setLotMedicationForm({ lotId:lot.id, stockId:"", qty:"", animalCount:String(lot.quantity||0), avgWeightKg:String(lot.avgWeight||0), ageRange:getEffectiveLotAgeRangeId(lot), sex:lot.sex||"", purpose:lot.purpose||"", dosageUsed:"", reapplyEnabled:false, reapplyPreset:"7", customIntervalDays:"", notes:"" });
    requestAnimationFrame(()=>{ try{ window.scrollTo?.({ top:0, behavior:"smooth" }); }catch(_e){} });
  }, [getEffectiveLotAgeRangeId]);

  const openLotNutritionLauncherFromLot = React.useCallback((lotDraft)=>{
    if(!lotDraft) return;
    const firstPlan = (lotDraft?.rationPlans||[]).find(item=>item?.stockId) || (lotDraft?.supplements||[]).find(item=>item?.stockId) || {};
    const baseForm = {
      lotId: lotDraft.id,
      stockId: firstPlan?.stockId || "",
      qty:"",
      animalCount:String(lotDraft.quantity||0),
      avgWeightKg:String(lotDraft.avgWeight||0),
      ageRange:getEffectiveLotAgeRangeId(lotDraft),
      sex:lotDraft.sex||"",
      purpose:lotDraft.purpose||"",
      consumptionPerHeadKgDay:String(plannedLotNutritionPerHeadKg(lotDraft)||lotDraft.dailyFeedKg||""),
      notes:"",
      pauseReason:"",
      pauseNotes:"",
    };
    const plan = getBovineNutritionPlan(baseForm);
    setLotNutritionForm({ ...baseForm, qty:String(plan?.qtyToLaunch||"") });
  }, [getBovineNutritionPlan, getEffectiveLotAgeRangeId, plannedLotNutritionPerHeadKg]);


  const birdBaseCodeValue = React.useCallback((code)=>{
    const raw = String(code || "").replace(/\D/g, "");
    return raw ? Number(raw) : 0;
  }, []);

  const nextBirdBaseCode = React.useCallback(()=>{
    const maxValue = (lots||[]).reduce((max, lot)=>Math.max(max, birdBaseCodeValue(lot?.birdBaseCode)), 0);
    return String(maxValue + 1).padStart(4, "0");
  }, [birdBaseCodeValue, lots]);

  const makeBirdCode = React.useCallback((stageKey, baseCode)=>{
    const def = birdStageDefs[stageKey] || birdStageDefs.day1;
    return `${def.prefix}-${String(baseCode || nextBirdBaseCode()).padStart(4, "0")}`;
  }, [birdStageDefs, nextBirdBaseCode]);

  const buildBirdLotPayload = React.useCallback((stageKey, raw={}, opts={})=>{
    const ts = new Date().toISOString();
    const def = birdStageDefs[stageKey] || birdStageDefs.day1;
    const quantity = Math.max(0, Number(raw.quantity || 0));
    const maleQty = Math.max(0, Number(raw.maleQty || 0));
    const femaleQty = Math.max(0, Number(raw.femaleQty || 0));
    const baseCode = raw.birdBaseCode || opts.baseCode || nextBirdBaseCode();
    return {
      ...raw,
      species: "Ave",
      birdStage: stageKey,
      birdBaseCode: baseCode,
      code: raw.code || makeBirdCode(stageKey, baseCode),
      name: raw.name || `${def.label} ${String(baseCode).padStart(4, "0")}`,
      quantity,
      maleQty,
      femaleQty,
      entryValueTotal: Number(raw.entryValueTotal || 0),
      entryValuePerBird: Number(raw.entryValuePerBird || 0),
      initialAgeDays: Number(raw.initialAgeDays || def.minAge || 0),
      formedAt: raw.formedAt || raw.entryDate || new Date().toISOString().slice(0, 10),
      entryDate: raw.entryDate || raw.formedAt || new Date().toISOString().slice(0, 10),
      status: raw.status || "Aberto",
      mortalityEntries: Array.isArray(raw.mortalityEntries) ? raw.mortalityEntries : [],
      nutritionEntries: Array.isArray(raw.nutritionEntries) ? raw.nutritionEntries : [],
      medicationEntries: Array.isArray(raw.medicationEntries) ? raw.medicationEntries : [],
      eggEntries: Array.isArray(raw.eggEntries) ? raw.eggEntries : [],
      transfersOut: Array.isArray(raw.transfersOut) ? raw.transfersOut : [],
      transferHistory: Array.isArray(raw.transferHistory) ? raw.transferHistory : [],
      timeline: Array.isArray(raw.timeline) ? raw.timeline : [],
      notes: raw.notes || "",
      costCenter: raw.costCenter || "Pecuária",
      farmId: currentFarmId || raw.farmId || "",
      updatedAt: ts,
      updatedBy: session?.name || "",
    };
  }, [birdStageDefs, currentFarmId, makeBirdCode, nextBirdBaseCode, session?.name]);

  function saveBirdLot(){
    if(!birdLotForm) return;
    if(!birdLotForm.name?.trim()){ show("Informe o nome do lote.", "error"); return; }
    if(Number(birdLotForm.quantity||0) <= 0){ show("A quantidade do lote deve ser maior que zero.", "error"); return; }
    const stageKey = birdLotForm.stageKey || birdTab;
    const payload = buildBirdLotPayload(stageKey, birdLotForm);
    if(birdLotForm._new){
      const created = makeAnimalLotCreatePending({
        ...payload,
        id: createClientId("lot"),
        timeline:[...(payload.timeline||[]), (()=>{ const tid=createClientId("tli"); return { id:tid, clientId:tid, ts:new Date().toISOString(), actor:session?.name||"", action:`Lote ${birdStageDefs[stageKey]?.label||stageKey} criado`, note:"Cadastro inicial do lote" }; })()],
        ...makeCreateMeta(session?.name||""),
      }, lots||[]);
      setLots(prev=>dedupeById([...(prev||[]), created]));
    } else {
      setLots(prev=>dedupeById((prev||[]).map(l=>String(l.id)===String(payload.id)?makeAnimalUpdatePending({ ...l, ...payload, ...makeUpdateMeta(session?.name||"") }):l)));
    }
    setBirdLotForm(null);
    show("✅ Lote de aves salvo!");
    syncOnSave("animalLots");
  }

  function patchBirdLot(lotId, producer){
    const ts = new Date().toISOString();
    let updatedLot = null;
    setLots(prev=>dedupeById((prev||[]).map(l=>{
      if(String(l.id)!==String(lotId)) return l;
      const patch = producer({ ...l });
      updatedLot = makeAnimalUpdatePending({ ...l, ...patch, updatedAt:ts, updatedBy:session?.name||"", ...makeUpdateMeta(session?.name||"", { updatedAt: ts }) });
      return updatedLot;
    })));
    return updatedLot;
  }

  function consumeBirdStock(lot, payload, category){
    const stockId = payload?.stockId;
    const qty = Math.max(0, Number(payload?.qty||0));
    if(!stockId || qty<=0){ show("Selecione o item e informe a quantidade.", "error"); return false; }
    const item = birdStockItems.find(s=>String(s.id)===String(stockId));
    const available = Number(item?.quantity||0);
    if(qty > available){ show(`Estoque insuficiente. Disponível: ${available}.`, "error"); return false; }
    const ts = new Date().toISOString();
    const movement = {
      id:createClientId("smv"),
      clientId:createClientId("smv"),
      itemId:stockId,
      type:"saida",
      qty,
      notes:`${category} · ${lot?.code||lot?.name||"Lote de aves"}`,
      by:session?.name||"",
      costCenter: lot?.costCenter || "Pecuária",
      ts,
      updatedAt:ts,
      origin:`bird_${category.toLowerCase().replace(/[^a-z0-9]+/g, "_")}`,
      originId: lot?.id,
      farmId: currentFarmId || lot?.farmId || "",
      ...makeCreateMeta(session?.name||"", { updatedAt: ts }),
    };
    setStockMovements(prev=>dedupeById([...(prev||[]), movement]));
    syncOnSave("stockMovements");
    return { item, movement };
  }

  function registerBirdNutrition(){
    if(!birdNutritionForm?.lotId) return;
    const lot = birdLots.find(l=>String(l.id)===String(birdNutritionForm.lotId));
    if(!lot) return;
    const consumed = consumeBirdStock(lot, birdNutritionForm, "Nutrição Aves");
    if(!consumed) return;
    patchBirdLot(lot.id, current=>({
      nutritionEntries:[...(current.nutritionEntries||[]), {
        id:createClientId("bnt"),
        clientId:createClientId("bnt"),
        ts:new Date().toISOString(),
        stockId:birdNutritionForm.stockId,
        stockName: consumed.item?.name || "Item",
        qty:Number(birdNutritionForm.qty||0),
        notes:birdNutritionForm.notes||"",
        by:session?.name||"",
      }],
      timeline:[...(current.timeline||[]), { ts:new Date().toISOString(), actor:session?.name||"", action:"Nutrição lançada", note:`${consumed.item?.name||"Item"} · ${birdNutritionForm.qty}` }],
    }));
    setBirdNutritionForm(null);
    show("✅ Nutrição lançada e estoque baixado.");
    syncOnSave("animalLots");
  }

  function registerBirdMedication(){
    if(!birdMedicationForm?.lotId) return;
    const lot = birdLots.find(l=>String(l.id)===String(birdMedicationForm.lotId));
    if(!lot) return;
    const categoryLabel = birdCategoryByStage(birdMedicationForm?.animalCategory || lot?.birdStage);
    const effectiveWeightKg = getMedicationEffectiveWeightKg(birdMedicationForm?.avgWeightKg, resolveMedicationWeightGuide({ species:"Ave", category:categoryLabel, sex:String(birdMedicationForm?.sex||"Ambos"), lotLike:lot, typedWeightKg:birdMedicationForm?.avgWeightKg }));
    const suggestion = getBirdMedicationSuggestion({ ...birdMedicationForm, avgWeightKg:effectiveWeightKg, sex:birdMedicationForm?.sex || "Ambos" });
    const warning = suggestion?.compatibility && (suggestion.compatibility.allowed === false || suggestion.compatibility.speciesAllowed === false || suggestion.compatibility.categoryAllowed === false || suggestion.compatibility.sexAllowed === false) ? 'Uso confirmado fora da recomendação de espécie/categoria/sexo do medicamento.' : '';
    const payload = { ...birdMedicationForm, avgWeightKg:effectiveWeightKg, notes:[birdMedicationForm.notes||"", warning].filter(Boolean).join(' ') };
    const consumed = consumeBirdStock(lot, payload, "Medicamento Aves");
    if(!consumed) return;
    patchBirdLot(lot.id, current=>({
      medicationEntries:[...(current.medicationEntries||[]), {
        id:createClientId("bmd"),
        clientId:createClientId("bmd"),
        ts:new Date().toISOString(),
        stockId:payload.stockId,
        stockName: consumed.item?.name || "Item",
        qty:Number(payload.qty||0),
        avgWeightKg:effectiveWeightKg,
        dosageUsed: String(payload.dosageUsed||"").trim(),
        medicationSummaryLabel: buildMedicationSummaryLabel(consumed.item?.name || "Medicamento", Number(payload.qty||0), "no lote"),
        notes:payload.notes||"",
        compatibilityWarning: warning,
        by:session?.name||"",
      }],
      timeline:[...(current.timeline||[]), { ts:new Date().toISOString(), actor:session?.name||"", action:"Medicamento lançado", note:`${consumed.item?.name||"Item"} · ${payload.qty}${warning?` · ${warning}`:""}` }],
    }));
    setBirdMedicationForm(null);
    show("✅ Medicamento lançado e estoque baixado.");
    syncOnSave("animalLots");
  }

  function consumeLivestockLotStock(lot, payload, categoryLabel){
    const stockId = payload?.stockId;
    const qty = Math.max(0, Number(payload?.qty||0));
    if(!stockId || qty<=0){ show("Selecione o item e informe a quantidade.", "error"); return false; }
    const item = birdStockItems.find(s=>String(s.id)===String(stockId));
    const available = Number(item?.quantity||0);
    if(qty > available){ show(`Estoque insuficiente. Disponível: ${available}.`, "error"); return false; }
    const ts = new Date().toISOString();
    const movement = {
      id:createClientId("smv"),
      clientId:createClientId("smv"),
      itemId:stockId,
      type:"saida",
      qty,
      notes:`${categoryLabel} · ${lot?.name||"Lote"}`,
      by:session?.name||"",
      costCenter: lot?.costCenter || "Pecuária",
      ts,
      updatedAt:ts,
      origin:`livestock_${categoryLabel.toLowerCase().replace(/[^a-z0-9]+/g, "_")}`,
      originId: lot?.id,
      farmId: currentFarmId || lot?.farmId || "",
      ...makeCreateMeta(session?.name||"", { updatedAt: ts }),
    };
    setStockMovements(prev=>dedupeById([...(prev||[]), movement]));
    syncOnSave("stockMovements");
    return { item, movement };
  }

  function registerLotNutrition(){
    if(!lotNutritionForm?.lotId) return;
    const lot = speciesLots.find(l=>String(l.id)===String(lotNutritionForm.lotId));
    if(!lot) return;
    const plan = getBovineNutritionPlan(lotNutritionForm);
    const reviewNote = buildBovineNutritionReviewNote(lotNutritionForm, plan);
    const mergedNotes = [String(lotNutritionForm.notes||"").trim(), reviewNote].filter(Boolean).join(" ");
    const payload = {
      ...lotNutritionForm,
      qty: String(Number(lotNutritionForm?.qty || plan?.qtyToLaunch || 0)),
      notes: mergedNotes,
    };
    const consumed = consumeLivestockLotStock(lot, payload, "Nutrição Lote");
    if(!consumed) return;
    setLots(prev=>dedupeById((prev||[]).map(item=>{
      if(String(item.id)!==String(lot.id)) return item;
      return {
        ...item,
        nutritionEntries:[...(item.nutritionEntries||[]), {
          id:createClientId("lnt"),
          ts:new Date().toISOString(),
          stockId:payload.stockId,
          stockName:consumed.item?.name || "Item",
          qty:Number(payload.qty||0),
          animalCount:Number(payload.animalCount||item.quantity||0),
          ageRange:payload.ageRange || getEffectiveLotAgeRangeId(item),
          consumptionPerHeadKgDay:Number(payload.consumptionPerHeadKgDay||0),
          dailyKg:Number(plan?.dailyKg||0),
          weeklyKg:Number(plan?.weeklyKg||0),
          monthlyKg:Number(plan?.monthlyKg||0),
          packKg:Number(plan?.packKg||0),
          suggestedQty:Number(plan?.qtyToLaunch||0),
          stockDays:Number(plan?.stockDays||0),
          period:payload.period||"Diário",
          notes:payload.notes||"",
          by:session?.name||"",
        }],
        timeline:[...(item.timeline||[]), { ts:new Date().toISOString(), actor:session?.name||"", action:"Nutrição lançada", note:`${consumed.item?.name||"Item"} · ${payload.qty}${reviewNote?` · ${reviewNote}`:""}` }],
        ...makeUpdateMeta(session?.name||""),
      };
    })));
    setLotNutritionForm(null);
    show("✅ Nutrição do lote lançada e estoque baixado.");
    syncOnSave("animalLots");
  }

  function registerLotMedication(){
    if(!lotMedicationForm?.lotId) return;
    const lot = speciesLots.find(l=>String(l.id)===String(lotMedicationForm.lotId));
    if(!lot) return;
    const categories = bovineCategoriesForLot({ ...(lot||{}), ageRange:lotMedicationForm?.ageRange || getEffectiveLotAgeRangeId(lot), sex:lotMedicationForm?.sex || lot?.sex, purpose:lotMedicationForm?.purpose || lot?.purpose });
    const effectiveWeightKg = getMedicationEffectiveWeightKg(lotMedicationForm?.avgWeightKg, resolveMedicationWeightGuide({ species:"Bovino", category:categories[0]||"", sex:String(lotMedicationForm?.sex || lot?.sex || ""), lotLike:lot, typedWeightKg:lotMedicationForm?.avgWeightKg }));
    const suggestion = getBovineMedicationSuggestion({ ...lotMedicationForm, avgWeightKg:effectiveWeightKg });
    const reviewNote = buildBovineMedicationReviewNote({ ...lotMedicationForm, avgWeightKg:effectiveWeightKg }, suggestion);
    const intervalDays = lotMedicationForm.reapplyEnabled
      ? Number(lotMedicationForm.reapplyPreset === "custom" ? lotMedicationForm.customIntervalDays : lotMedicationForm.reapplyPreset || 0)
      : 0;
    const compatibilityWarning = suggestion?.compatibility && (suggestion.compatibility.allowed === false || suggestion.compatibility.speciesAllowed === false || suggestion.compatibility.categoryAllowed === false || suggestion.compatibility.sexAllowed === false) ? 'Uso confirmado fora da recomendação de espécie/categoria/sexo do medicamento.' : '';
    const mergedNotes = [String(lotMedicationForm.notes||"").trim(), reviewNote, compatibilityWarning].filter(Boolean).join(" ");
    const payload = { ...lotMedicationForm, avgWeightKg: effectiveWeightKg, notes: mergedNotes, intervalDays };
    const consumed = consumeLivestockLotStock(lot, payload, "Medicamento Lote");
    if(!consumed) return;
    setLots(prev=>dedupeById((prev||[]).map(item=>{
      if(String(item.id)!==String(lot.id)) return item;
      return {
        ...item,
        medicationEntries:[...(item.medicationEntries||[]), {
          id:createClientId("lmd"),
          ts:new Date().toISOString(),
          stockId:payload.stockId,
          stockName:consumed.item?.name || "Item",
          qty:Number(payload.qty||0),
          suggestedQty:Number(suggestion?.qty||0),
          animalCount:Number(payload.animalCount||item.quantity||0),
          ageRange:payload.ageRange || getEffectiveLotAgeRangeId(item),
          avgWeightKg:Number(payload.avgWeightKg||item.avgWeight||0),
          dosageUsed:payload.dosageUsed||"",
          medicationSummaryLabel: buildMedicationSummaryLabel(consumed.item?.name || "Medicamento", Number(payload.qty||0), "no lote"),
          suggestionFormula:suggestion?.formula || "",
          reapplyEnabled:!!payload.reapplyEnabled,
          reapplyPreset:payload.reapplyPreset||"",
          customIntervalDays:payload.customIntervalDays||"",
          intervalDays:Number(intervalDays||0),
          nextApplicationAt: intervalDays > 0 ? new Date(Date.now() + intervalDays*86400000).toISOString().slice(0,10) : "",
          notes:payload.notes||"",
          by:session?.name||"",
        }],
        timeline:[...(item.timeline||[]), { ts:new Date().toISOString(), actor:session?.name||"", action:"Medicamento lançado", note:`${consumed.item?.name||"Item"} · ${payload.qty}${reviewNote?` · ${reviewNote}`:""}${compatibilityWarning?` · ${compatibilityWarning}`:""}` }],
        ...makeUpdateMeta(session?.name||""),
      };
    })));
    setLotMedicationForm(null);
    show("✅ Medicamento do lote lançado e estoque baixado.");
    syncOnSave("animalLots");
  }

  function registerBirdMortality(){
    if(!birdMortalityForm?.lotId) return;
    const lot = birdLots.find(l=>String(l.id)===String(birdMortalityForm.lotId));
    if(!lot) return;
    const qty = Math.max(0, Number(birdMortalityForm.qty||0));
    if(qty<=0){ show("Informe a quantidade de aves.", "error"); return; }
    if(qty > birdCurrentQty(lot)){ show("A quantidade informada é maior que o saldo atual do lote.", "error"); return; }
    patchBirdLot(lot.id, current=>({
      mortalityEntries:[...(current.mortalityEntries||[]), {
        id:createClientId("bmt"), ts:new Date().toISOString(), qty,
        reason:birdMortalityForm.reason||birdMortalityReasons[0], notes:birdMortalityForm.notes||"", by:session?.name||"",
      }],
      status: qty === birdCurrentQty(current) ? "Fechado" : current.status,
      timeline:[...(current.timeline||[]), { ts:new Date().toISOString(), actor:session?.name||"", action:"Mortes registradas", note:`${qty} aves · ${birdMortalityForm.reason||birdMortalityReasons[0]}` }],
    }));
    setBirdMortalityForm(null);
    show("✅ Mortes registradas no lote.");
    syncOnSave("animalLots");
  }

  function registerBirdEggs(){
    if(!birdEggForm?.lotId) return;
    const lot = birdLots.find(l=>String(l.id)===String(birdEggForm.lotId));
    if(!lot) return;
    const qty = Math.max(0, Number(birdEggForm.qty||0));
    if(qty<=0){ show("Informe a quantidade de ovos da coleta.", "error"); return; }
    const ts = makeLocalEggTimestamp(birdEggForm.date, birdEggForm.time);
    const entryId = birdEggForm.editEntryId || createClientId("beg");
    patchBirdLot(lot.id, current=>{
      const currentEntries = [...(current.eggEntries||[])];
      const nextEntry = { id:entryId, ts, qty, notes:birdEggForm.notes||"", by:session?.name||"" };
      const nextEntries = birdEggForm.editEntryId
        ? currentEntries.map(entry=>String(entry.id)===String(entryId)?{...entry, ...nextEntry}:entry)
        : [...currentEntries, nextEntry];
      return {
        eggEntries: nextEntries,
        timeline:[...(current.timeline||[]), { ts:new Date().toISOString(), actor:session?.name||"", action:birdEggForm.editEntryId?"Coleta de ovos editada":"Produção de ovos lançada", note:`${qty} ovos · ${birdEggForm.date||''} ${birdEggForm.time||''}`.trim() }],
      };
    });
    setBirdEggForm(f=>f?({
      lotId:f.lotId,
      qty:"",
      notes:"",
      date:today(),
      time:toLocalDateTimeParts().time,
      selectedDay: birdEggForm.date || today(),
      editEntryId:null,
    }):null);
    show(birdEggForm.editEntryId ? "✅ Coleta atualizada." : "✅ Produção de ovos registrada.");
    syncOnSave("animalLots");
  }

  function registerBirdTransfer(){
    if(!birdTransferForm?.lotId) return;
    const lot = birdLots.find(l=>String(l.id)===String(birdTransferForm.lotId));
    if(!lot) return;
    const age = birdAgeDays(lot);
    if(age < 60){ show("Esse lote ainda não atingiu 60 dias para transferência.", "error"); return; }
    const qtyCorte = Math.max(0, Number(birdTransferForm.qtyCorte||0));
    const qtyPostura = Math.max(0, Number(birdTransferForm.qtyPostura||0));
    const total = qtyCorte + qtyPostura;
    const available = birdCurrentQty(lot);
    if(total<=0){ show("Informe a quantidade a transferir.", "error"); return; }
    if(total > available){ show(`Saldo insuficiente no lote. Disponível: ${available}.`, "error"); return; }
    const ts = new Date().toISOString();
    const baseCode = lot?.birdBaseCode || nextBirdBaseCode();
    const newLots = [];
    if(qtyCorte>0){
      newLots.push({
        ...buildBirdLotPayload("corte", {
          name:`${birdStageDefs.corte.label} ${String(baseCode).padStart(4, "0")}`,
          quantity:qtyCorte,
          maleQty:Math.round((Number(lot?.maleQty||0)/(Number(lot?.quantity||1)||1))*qtyCorte),
          femaleQty:Math.max(0, qtyCorte - Math.round((Number(lot?.maleQty||0)/(Number(lot?.quantity||1)||1))*qtyCorte)),
          entryValueTotal:0,
          initialAgeDays:age,
          formedAt:today(),
          sourceLotId:lot.id,
          sourceStage:"growth",
          notes:birdTransferForm.notes||"",
        }, { baseCode }),
        id:createClientId("lot"),
        timeline:[{ ts, actor:session?.name||"", action:"Lote criado por transferência", note:`Origem: ${lot.code||lot.name}` }],
        ...makeCreateMeta(session?.name||"", { updatedAt: ts }),
      });
    }
    if(qtyPostura>0){
      newLots.push({
        ...buildBirdLotPayload("postura", {
          name:`${birdStageDefs.postura.label} ${String(baseCode).padStart(4, "0")}`,
          quantity:qtyPostura,
          maleQty:0,
          femaleQty:qtyPostura,
          entryValueTotal:0,
          initialAgeDays:age,
          formedAt:today(),
          sourceLotId:lot.id,
          sourceStage:"growth",
          notes:birdTransferForm.notes||"",
        }, { baseCode }),
        id:createClientId("lot"),
        timeline:[{ ts, actor:session?.name||"", action:"Lote criado por transferência", note:`Origem: ${lot.code||lot.name}` }],
        ...makeCreateMeta(session?.name||"", { updatedAt: ts }),
      });
    }
    setLots(prev=>{
      const updated = (prev||[]).map(item=>{
        if(String(item.id)!==String(lot.id)) return item;
        const transfersOut = [
          ...(item.transfersOut||[]),
          ...(qtyCorte>0?[{ id:createClientId("btr"), ts, targetStage:"corte", qty:qtyCorte, by:session?.name||"", note:birdTransferForm.notes||"" }]:[]),
          ...(qtyPostura>0?[{ id:createClientId("btr"), ts, targetStage:"postura", qty:qtyPostura, by:session?.name||"", note:birdTransferForm.notes||"" }]:[]),
        ];
        const remaining = Math.max(0, Number(item.quantity||0) - birdMortalityQty(item) - transfersOut.reduce((s,e)=>s+Number(e.qty||0),0));
        return {
          ...item,
          transfersOut,
          transferHistory:[...(item.transferHistory||[]), ...newLots.map(n=>({ ts, targetLotId:n.id, targetCode:n.code, targetStage:n.birdStage, qty:n.quantity }))],
          status: remaining<=0 ? "Fechado" : item.status,
          timeline:[...(item.timeline||[]), { ts, actor:session?.name||"", action:"Transferência realizada", note:`Corte: ${qtyCorte} · Postura: ${qtyPostura}` }],
          ...makeUpdateMeta(session?.name||"", { updatedAt: ts }),
        };
      });
      return dedupeById([...updated, ...newLots]);
    });
    setBirdTransferForm(null);
    show("✅ Transferência concluída e novos lotes criados.");
    syncOnSave("animalLots");
  }

  useEffect(() => {
    const pending = (lots||[]).filter(l=>animalSpeciesOf(l)==="Ave" && l?.birdStage === "day1" && (l?.status||"Aberto") !== "Fechado" && birdAgeDays(l) >= 30 && !l?.autoTransitionedToGrowthId);
    if(!pending.length) return;
    const ts = new Date().toISOString();
    setLots(prev=>{
      let list = [...(prev||[])];
      pending.forEach(source=>{
        const fresh = list.find(item=>String(item.id)===String(source.id));
        if(!fresh || fresh.autoTransitionedToGrowthId) return;
        const remaining = birdCurrentQty(fresh);
        const baseCode = fresh.birdBaseCode || nextBirdBaseCode();
        const newLot = {
          ...buildBirdLotPayload("growth", {
            name:`${birdStageDefs.growth.label} ${String(baseCode).padStart(4, "0")}`,
            quantity:remaining,
            maleQty:Number(fresh.maleQty||0),
            femaleQty:Number(fresh.femaleQty||0),
            initialAgeDays:30,
            formedAt:today(),
            sourceLotId:fresh.id,
            sourceStage:"day1",
            notes:`Gerado automaticamente a partir do lote ${fresh.code||fresh.name}`,
          }, { baseCode }),
          id:createClientId("lot"),
          timeline:[{ ts, actor:"Sistema", action:"Lote gerado automaticamente", note:`Origem: ${fresh.code||fresh.name}` }],
          ...makeCreateMeta(session?.name||"Sistema", { updatedAt: ts }),
        };
        list = list.map(item=>String(item.id)!==String(fresh.id)?item:{
          ...item,
          status:"Fechado",
          autoTransitionedToGrowthId:newLot.id,
          transfersOut:[...(item.transfersOut||[]), { id:createClientId("btr"), ts, targetStage:"growth", targetLotId:newLot.id, qty:remaining, by:"Sistema", note:"Transferência automática aos 30 dias" }],
          timeline:[...(item.timeline||[]), { ts, actor:"Sistema", action:"Transferência automática", note:`Lote transferido para ${newLot.code}` }],
          ...makeUpdateMeta(session?.name||"Sistema", { updatedAt: ts }),
        });
        list.push(newLot);
      });
      return dedupeById(list);
    });
    syncOnSaveRef.current?.("animalLots");
  }, [
    lots,
    session?.name,
    birdAgeDays,
    birdCurrentQty,
    birdStageDefs,
    buildBirdLotPayload,
    nextBirdBaseCode,
    setLots,
    animalSpeciesOf,
  ]);

  function openNewBirdLot(stageKey){
    const def = birdStageDefs[stageKey] || birdStageDefs.day1;
    const baseCode = nextBirdBaseCode();
    setBirdLotForm({
      _new:true,
      stageKey,
      species:"Ave",
      name:`${def.label} ${baseCode}`,
      birdBaseCode:baseCode,
      code:makeBirdCode(stageKey, baseCode),
      quantity:0,
    lotType:'collective',
    selectedAnimalIds: [],
      maleQty:0,
      femaleQty:0,
      entryValueTotal:0,
      entryValuePerBird:0,
      initialAgeDays:def.minAge,
      formedAt:today(),
      entryDate:today(),
      status:"Aberto",
      costCenter:"Pecuária",
      notes:"",
    });
  }

  function renderBirdStats(){
    const stageLots = birdLotsByStage[birdTab] || [];
    const openLots = stageLots.filter(l=>(l?.status||"Aberto") !== "Fechado");
    const totalBirds = openLots.reduce((sum, lot)=>sum + birdCurrentQty(lot), 0);
    const avgAge = openLots.length ? Math.round(openLots.reduce((sum, lot)=>sum + birdAgeDays(lot), 0) / openLots.length) : 0;
    const cards = filterConfiguredCards([
      { id: currentAnimalSection?.cards?.[0], node:<StatCard icon={(birdTab === "growth" || birdTab === "corte") ? <ChickenLotsIcon size={42} /> : "🐔"} label="Lotes" value={stageLots.length} sub={birdStageDefs[birdTab].label} color={T.yellow} iconPosition="middleRight" iconWrapStyle={{width:76,height:76,minWidth:76}} iconStyle={{fontSize:44,width:56,height:56}}/> },
      { id: currentAnimalSection?.cards?.[1], node:<StatCard icon={birdTab === "day1" ? <ChickenLotsIcon size={42} /> : <ChickenHeadIcon size={42} />} label={birdTab === "day1" ? "Aves no lote" : "Aves no saldo"} value={totalBirds} sub="ativas no estágio" color={T.green} iconPosition="middleRight" iconWrapStyle={{width:76,height:76,minWidth:76}} iconStyle={{fontSize:44,width:56,height:56}}/> },
      { id: currentAnimalSection?.cards?.[2], node:<StatCard icon="🕒" label="Idade média" value={avgAge} sub="dias" color={T.accent} iconPosition="middleRight" iconWrapStyle={{width:76,height:76,minWidth:76}} iconStyle={{fontSize:44}}/> },
      { id: currentAnimalSection?.cards?.[3], node:<StatCard icon={birdTab==="postura"?"🥚":"☠️"} label={birdTab==="postura"?"Média ovos/dia":"Mortes"} value={birdTab==="postura"?stageLots.reduce((sum,lot)=>sum + birdEggAverage(lot),0):stageLots.reduce((sum,lot)=>sum + birdMortalityQty(lot),0)} sub={birdTab==="postura"?"média 30 dias":"acumuladas"} color={birdTab==="postura"?T.purple:T.red} iconPosition="middleRight" iconWrapStyle={{width:76,height:76,minWidth:76}} iconStyle={{fontSize:44}}/> },
    ], allowedCurrentSectionCards, currentAnimalSection?.cards || []);
    return cards.length ? <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr 1fr":"repeat(4,minmax(0,1fr))",gap:12,marginBottom:18}}>{cards.map(item=><React.Fragment key={item.id}>{item.node}</React.Fragment>)}</div> : null;
  }

  const closeAllBirdInlinePanels = React.useCallback(()=>{
    setBirdLotForm(null);
    setBirdNutritionForm(null);
    setBirdMedicationForm(null);
    setBirdMortalityForm(null);
    setBirdEggForm(null);
    setBirdEggInfoOpen(false);
    setBirdTransferForm(null);
  }, []);

  const openBirdInlinePanel = React.useCallback((panel, payload)=>{
    closeAllBirdInlinePanels();
    if(panel === "lot") setBirdLotForm(payload);
    if(panel === "nutrition") setBirdNutritionForm(payload);
    if(panel === "medication") setBirdMedicationForm(payload);
    if(panel === "mortality") setBirdMortalityForm(payload);
    if(panel === "eggs") setBirdEggForm(payload);
    if(panel === "transfer") setBirdTransferForm(payload);
  }, [closeAllBirdInlinePanels]);

  const currentBirdInlineLotId = birdLotForm?.id || birdLotForm?.lotId || birdNutritionForm?.lotId || birdMedicationForm?.lotId || birdMortalityForm?.lotId || birdEggForm?.lotId || birdTransferForm?.lotId || null;

  function renderBirdLotCard(lot){
    const stageDef = birdStageDefs[lot?.birdStage] || birdStageDefs.day1;
    const saldo = birdCurrentQty(lot);
    const age = birdAgeDays(lot);
    const hasInlinePanelForLot = String(currentBirdInlineLotId||"") === String(lot?.id||"");
    return <div key={lot.id} id={`bird-inline-anchor-${lot.id}`} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14,display:"flex",flexDirection:"column",gap:10}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10}}>
        <div>
          <div style={{fontWeight:800,color:T.text,fontSize:15}}>{lot.name}</div>
          <div style={{fontSize:12,color:T.muted}}>{lot.code || makeBirdCode(lot.birdStage, lot.birdBaseCode)} · {stageDef.label}</div>
        </div>
        <Badge color={(lot?.status||"Aberto")==="Fechado"?T.muted:T.green}>{lot?.status||"Aberto"}</Badge>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
        <div style={{background:T.card2,borderRadius:10,padding:"10px 12px"}}><div style={{fontSize:11,color:T.muted}}>Saldo atual</div><div style={{fontSize:22,fontWeight:800,color:T.yellow}}>{saldo}</div></div>
        <div style={{background:T.card2,borderRadius:10,padding:"10px 12px"}}><div style={{fontSize:11,color:T.muted}}>Idade</div><div style={{fontSize:22,fontWeight:800,color:T.accent}}>{age} dias</div></div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,fontSize:12,color:T.text}}>
        <div>Machos: <strong>{Number(lot?.maleQty||0)}</strong></div>
        <div>Fêmeas: <strong>{Number(lot?.femaleQty||0)}</strong></div>
        <div>Entrada: <strong>{fmtDate(lot?.entryDate || lot?.formedAt)}</strong></div>
        <div>Valor: <strong>{fmtMoney(Number(lot?.entryValueTotal||0))}</strong></div>
        {lot?.birdStage === "growth" && <div>Valor/ave: <strong>{fmtMoney(Number(lot?.entryValuePerBird||0))}</strong></div>}
        {lot?.birdStage === "postura" && <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>Média 30d: <strong>{birdEggAverage(lot)}</strong><button type="button" onClick={()=>setBirdEggInfoOpen(v=>!v)} style={{appearance:"none",border:`1px solid ${T.border}`,background:T.card2,color:T.muted,borderRadius:999,padding:"0 6px",fontSize:10,fontWeight:800,cursor:"pointer"}}>i</button></div>}
      </div>
      {lot?.notes && <div style={{fontSize:12,color:T.muted,whiteSpace:"pre-wrap"}}>{lot.notes}</div>}
      {birdEggInfoOpen && lot?.birdStage === "postura" && <div style={{fontSize:11,color:T.muted,background:T.card2,border:`1px solid ${T.border}`,borderRadius:10,padding:"8px 10px"}}>Média 30d = total de ovos dos últimos 30 dias ÷ 30. Na lista diária, a média do mês = total acumulado no mês até a data ÷ dia do mês.</div>}
      <div style={{display:"grid",gridTemplateColumns:lot?.birdStage === "postura" ? (isCompactViewport()?"1fr 1fr":"repeat(3,minmax(0,1fr))") : (isCompactViewport()?"1fr 1fr":"repeat(auto-fit,minmax(140px,1fr))"),gap:8,alignItems:"stretch"}}>
        {canEdit && <Btn size="sm" variant="ghost" onClick={()=>openBirdInlinePanel("nutrition", { lotId:lot.id, stageKey:lot.birdStage, stockId:"", qty:"", notes:"", period:"Diário", animalCount:String(Math.max(0, birdCurrentQty(lot)||0)), avgWeightKg:"" })}>Nutrição</Btn>}
        {canEdit && <Btn size="sm" variant="ghost" onClick={()=>openBirdInlinePanel("medication", { lotId:lot.id, animalCategory:lot.birdStage, stockId:"", qty:"", notes:"", animalCount:String(Math.max(0, birdCurrentQty(lot)||0)), avgWeightKg:"", reapplyEnabled:false, reapplyPreset:"3", customIntervalDays:"", dosageUsed:"" })}>Medicamentos</Btn>}
        {canEdit && <Btn size="sm" variant="ghost" onClick={()=>openBirdInlinePanel("mortality", { lotId:lot.id, qty:"", reason:birdMortalityReasons[0]||"Outro", notes:"" })}>Mortes</Btn>}
        {canEdit && lot?.birdStage === "growth" && <Btn size="sm" variant="ghost" onClick={()=>openBirdInlinePanel("transfer", { lotId:lot.id, qtyCorte:"", qtyPostura:"", notes:"" })}>Transferir</Btn>}
        {canEdit && <Btn size="sm" variant="ghost" onClick={()=>openBirdInlinePanel("lot", { ...lot, stageKey:lot.birdStage, _new:false })}>Editar</Btn>}
      </div>
      {canEdit && lot?.birdStage === "postura" && <div style={{display:"flex",justifyContent:"flex-end"}}><button type="button" onClick={()=>openBirdInlinePanel("eggs", { lotId:lot.id, qty:"", notes:"", date:today(), time:toLocalDateTimeParts().time, selectedDay:today(), editEntryId:null })} style={{appearance:"none",border:`1px solid rgba(255,153,51,.45)`,background:"rgba(255,153,51,.18)",color:"#ffb261",fontWeight:800,fontSize:16,borderRadius:12,padding:isCompactViewport()?"12px 18px":"14px 24px",minWidth:isCompactViewport()?132:148,boxShadow:"inset 0 1px 0 rgba(255,255,255,.04)"}}>🥚 Ovos</button></div>}
      {hasInlinePanelForLot && renderBirdInlinePanels(String(lot.id))}
    </div>;
  }

  function renderBirdInlinePanels(targetLotId){
    if(!targetLotId) return null;
    return <>
      {birdLotForm && <AnimalInlinePanel theme={T} title={birdLotForm ? (birdLotForm._new ? `Novo lote · ${birdStageDefs[birdLotForm.stageKey||birdTab]?.label||"Aves"}` : `Editar lote · ${birdLotForm.code||birdLotForm.name||"Aves"}`) : ""} onClose={closeAllBirdInlinePanels}>
        {(()=>{ const stageKey=birdLotForm.stageKey||birdTab; return <div style={{display:"grid",gap:14}}>
          <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1.1fr .9fr",gap:12}}>
            <Field label="Código-base"><Input value={birdLotForm.birdBaseCode||""} onChange={e=>setBirdLotForm(f=>({...f,birdBaseCode:e.target.value.toUpperCase()}))} placeholder="Ex.: 0001"/></Field>
            <Field label="Nome do lote"><Input value={birdLotForm.name||""} onChange={e=>setBirdLotForm(f=>({...f,name:e.target.value}))} placeholder={birdStageDefs[stageKey]?.label||"Lote"}/></Field>
          </div>
          <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"repeat(4,minmax(0,1fr))",gap:12}}>
            <Field label="Data de entrada"><Input type="date" value={birdLotForm.entryDate||today()} onChange={e=>setBirdLotForm(f=>({...f,entryDate:e.target.value, formedAt:e.target.value}))}/></Field>
            <Field label="Quantidade total"><Input type="number" min="1" value={birdLotForm.quantity||""} onChange={e=>setBirdLotForm(f=>({...f,quantity:e.target.value}))}/></Field>
            <Field label="Machos"><Input type="number" min="0" value={birdLotForm.maleQty||""} onChange={e=>setBirdLotForm(f=>({...f,maleQty:e.target.value}))}/></Field>
            <Field label="Fêmeas"><Input type="number" min="0" value={birdLotForm.femaleQty||""} onChange={e=>setBirdLotForm(f=>({...f,femaleQty:e.target.value}))}/></Field>
          </div>
          <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"repeat(4,minmax(0,1fr))",gap:12}}>
            <Field label="Idade inicial (dias)"><Input type="number" min="0" value={birdLotForm.initialAgeDays||""} onChange={e=>setBirdLotForm(f=>({...f,initialAgeDays:e.target.value}))}/></Field>
            <Field label="Valor total de entrada"><Input type="number" min="0" step="0.01" value={birdLotForm.entryValueTotal||""} onChange={e=>setBirdLotForm(f=>({...f,entryValueTotal:e.target.value}))}/></Field>
            <Field label="Valor por ave"><Input type="number" min="0" step="0.01" value={birdLotForm.entryValuePerBird||""} onChange={e=>setBirdLotForm(f=>({...f,entryValuePerBird:e.target.value}))}/></Field>
            <Field label="Status"><Select value={birdLotForm.status||"Aberto"} onChange={e=>setBirdLotForm(f=>({...f,status:e.target.value}))}><option>Aberto</option><option>Fechado</option></Select></Field>
          </div>
          <Field label="Observações"><Textarea rows={3} value={birdLotForm.notes||""} onChange={e=>setBirdLotForm(f=>({...f,notes:e.target.value}))}/></Field>
          <div style={{display:"flex",justifyContent:"flex-end",gap:10}}><Btn variant="ghost" onClick={closeAllBirdInlinePanels}>Cancelar</Btn><Btn onClick={saveBirdLot}>Salvar</Btn></div>
        </div>; })()}
      </AnimalInlinePanel>}

      {birdNutritionForm && <AnimalInlinePanel theme={T} title="Nutrição do lote" onClose={closeAllBirdInlinePanels} tone="success">{(()=>{ const lot=getBirdLotById(birdNutritionForm.lotId); const selectedItem=birdNutritionStockItems.find(item=>String(item.id)===String(birdNutritionForm.stockId||"")); const suggestion=getBirdNutritionSuggestion(birdNutritionForm); return <div style={{display:"grid",gap:14}}>
        <div style={{fontSize:13,color:T.muted}}>Lote: <strong style={{color:T.text}}>{lot?.name||"—"}</strong> · saldo atual <strong style={{color:T.text}}>{lot?birdCurrentQty(lot):0}</strong> aves</div>
        <Field label="Produto de nutrição (Pecuária)"><Select value={birdNutritionForm.stockId||""} onChange={e=>setBirdNutritionForm(f=>{ const next={...f,stockId:e.target.value}; const result=getBirdNutritionSuggestion(next); if(result?.qty!=null) next.qty=String(result.qty); return next; })}><option value="">Selecione</option>{birdNutritionStockItems.filter(item=>itemSupportsCategory(item, "nutrition", "Ave", birdCategoryByStage(birdNutritionForm?.stageKey || lot?.birdStage))).map(item=><option key={item.id} value={item.id}>{item.name} · {item.category} · saldo {item.quantity}</option>)}</Select></Field>
        <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr 1fr",gap:12}}>
          <Field label="Periodicidade"><Select value={birdNutritionForm.period||"Diário"} onChange={e=>setBirdNutritionForm(f=>({...f,period:e.target.value}))}>{birdNutritionPeriods.map(item=><option key={item.value} value={item.value}>{item.label}</option>)}</Select></Field>
          <Field label="Qtd. de animais"><Input type="number" min="0" value={birdNutritionForm.animalCount||""} onChange={e=>setBirdNutritionForm(f=>({...f,animalCount:e.target.value}))}/></Field>
          <Field label="Peso médio (kg, opcional)"><Input type="number" min="0" step="0.01" value={birdNutritionForm.avgWeightKg||""} onChange={e=>setBirdNutritionForm(f=>({...f,avgWeightKg:e.target.value}))}/></Field>
        </div>
        {selectedItem && <div style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:12,padding:12,fontSize:12,color:T.text,display:"grid",gap:4}}><div><strong>Produto:</strong> {selectedItem.name}</div><div><strong>Categoria:</strong> {selectedItem.category||"—"} · <strong>Centro:</strong> {selectedItem.costCenter||"—"}</div><div><strong>Unidade:</strong> {selectedItem.unit||"—"} · <strong>Saldo:</strong> {selectedItem.quantity}</div></div>}
        {suggestion?.profile && <div style={{background:`${T.green}12`,border:`1px solid ${T.green}33`,borderRadius:12,padding:12,display:"grid",gap:4,fontSize:12}}><div style={{fontWeight:700,color:T.green}}>Sugestão automática</div><div style={{color:T.text}}>Quantidade sugerida: <strong>{suggestion.qty ?? "—"}</strong> {selectedItem?.unit||suggestion.profile?.doseUnit||""}</div><div style={{color:T.muted}}>{suggestion.formula}</div><div style={{color:T.muted}}>{suggestion.source === "produto" ? "Baseada no perfil cadastrado no estoque." : "Baseada na referência padrão do app para a categoria."}</div><div><Btn size="sm" variant="ghost" onClick={()=>setBirdNutritionForm(f=>({...f,qty:String(suggestion.qty||"")}))}>Usar sugestão</Btn></div></div>}
        <Field label="Quantidade a lançar"><Input type="number" min="0" step="0.001" value={birdNutritionForm.qty||""} onChange={e=>setBirdNutritionForm(f=>({...f,qty:e.target.value}))}/></Field>
        <Field label="Observações"><Textarea rows={2} value={birdNutritionForm.notes||""} onChange={e=>setBirdNutritionForm(f=>({...f,notes:e.target.value}))}/></Field>
        <div style={{fontSize:12,color:T.muted}}>O app sugere a quantidade mais comum a partir do produto e da categoria do lote. Se você ajustar manualmente, ficará registrado como revisado pelo usuário.</div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:10}}><Btn variant="ghost" onClick={closeAllBirdInlinePanels}>Cancelar</Btn><Btn onClick={registerBirdNutrition}>Salvar</Btn></div>
      </div>; })()}</AnimalInlinePanel>}

      {birdMedicationForm && <AnimalInlinePanel theme={T} title="Medicamentos do lote" onClose={closeAllBirdInlinePanels} tone="warning">{(()=>{ const lot=getBirdLotById(birdMedicationForm.lotId); const selectedItem=birdMedicationStockItems.find(item=>String(item.id)===String(birdMedicationForm.stockId||"")); const categoryLabel=birdCategoryByStage(birdMedicationForm?.animalCategory || lot?.birdStage); const weightGuide=resolveMedicationWeightGuide({ species:"Ave", category:categoryLabel, sex:String(birdMedicationForm?.sex||"Ambos"), lotLike:lot, typedWeightKg:birdMedicationForm?.avgWeightKg }); const effectiveWeightKg=getMedicationEffectiveWeightKg(birdMedicationForm?.avgWeightKg, weightGuide); const suggestion=getBirdMedicationSuggestion({ ...birdMedicationForm, avgWeightKg: effectiveWeightKg, sex: birdMedicationForm?.sex || "Ambos" }); const selectedProfile=suggestion?.profile; const reviewCard=buildMedicationInlineReview({ suggestion, item:selectedItem, targetLabel:"no lote", extraLines:[weightGuide?.message, weightGuide?.latestRecordedAt ? `${weightGuide.latestSource}: ${fmtMeasure(weightGuide.latestWeightKg,1)} kg em ${fmtDate(weightGuide.latestRecordedAt)}` : "", weightGuide?.averageWeightKg ? `Média da fazenda para o perfil: ${fmtMeasure(weightGuide.averageWeightKg,1)} kg` : ""] }); return <div style={{display:"grid",gap:14}}>
        <div style={{fontSize:13,color:T.muted}}>Lote: <strong style={{color:T.text}}>{lot?.name||"—"}</strong> · saldo atual <strong style={{color:T.text}}>{lot?birdCurrentQty(lot):0}</strong> aves</div>
        <Field label="Medicamento do estoque (Pecuária)"><Select value={birdMedicationForm.stockId||""} onChange={e=>setBirdMedicationForm(f=>{ const next={...f,stockId:e.target.value}; const result=getBirdMedicationSuggestion(next); if(result?.qty!=null) next.qty=String(result.qty); if(result?.profile?.notes && !next.dosageUsed) next.dosageUsed=result.profile.notes; return next; })}><option value="">Selecione</option>{birdMedicationStockItems.filter(item=>itemSupportsCategory(item, "medication", "Ave", birdCategoryByStage(birdMedicationForm?.animalCategory || lot?.birdStage))).map(item=><option key={item.id} value={item.id}>{item.name} · saldo {item.quantity}</option>)}</Select></Field>
        <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr 1fr",gap:12}}>
          <Field label="Categoria do animal"><Select value={birdMedicationForm.animalCategory||lot?.birdStage||"day1"} onChange={e=>setBirdMedicationForm(f=>({...f,animalCategory:e.target.value}))}>{birdStages.map(stageKey=><option key={stageKey} value={stageKey}>{birdStageDefs[stageKey].label}</option>)}</Select></Field>
          <Field label="Qtd. de animais"><Input type="number" min="0" value={birdMedicationForm.animalCount||""} onChange={e=>setBirdMedicationForm(f=>({...f,animalCount:e.target.value}))}/></Field>
          <Field label="Peso médio usado no cálculo (kg)"><Input type="number" min="0" step="0.01" value={getMedicationWeightInputValue(birdMedicationForm.avgWeightKg, weightGuide)} onChange={e=>setBirdMedicationForm(f=>({...f,avgWeightKg:e.target.value}))}/></Field>
        </div>
        {selectedItem && <div style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:12,padding:12,fontSize:12,color:T.text,display:"grid",gap:4}}><div><strong>Produto:</strong> {selectedItem.name}</div><div><strong>Categoria:</strong> {selectedItem.category||"—"} · <strong>Centro:</strong> {selectedItem.costCenter||"—"}</div><div><strong>Unidade:</strong> {selectedItem.unit||"—"} · <strong>Saldo:</strong> {selectedItem.quantity}</div></div>}
        {reviewCard && <div style={{background:`${T.purple}12`,border:`2px solid ${T.purple}`,borderRadius:12,padding:12,display:"grid",gap:6,fontSize:12}}><div style={{fontWeight:800,color:T.purple}}>{reviewCard.title}</div><div style={{color:T.text}}>Resultado: <strong>{reviewCard.amountText}</strong></div><div style={{color:T.muted}}>{reviewCard.formula}</div>{reviewCard.extraLines.map((line, idx)=><div key={idx} style={{color:T.muted}}>{line}</div>)}{reviewCard.notes && <div style={{color:T.muted}}>{reviewCard.notes}</div>}{reviewCard.warning && <div style={{color:T.red,fontWeight:700}}>{reviewCard.warning} Você pode confirmar o uso mesmo assim; o prontuário ficará marcado em destaque.</div>}<div><Btn size="sm" variant="ghost" onClick={()=>setBirdMedicationForm(f=>({...f,avgWeightKg:String(effectiveWeightKg||f.avgWeightKg||""), qty:String(suggestion.qty||""), dosageUsed:selectedProfile?.notes||f.dosageUsed||""}))}>Usar cálculo do app</Btn></div></div>}
        <Field label="Dosagem utilizada"><Input value={birdMedicationForm.dosageUsed||""} onChange={e=>setBirdMedicationForm(f=>({...f,dosageUsed:e.target.value}))} placeholder="Ex.: 1 mL/L de água ou 0,02 mL/kg"/></Field>
        <Field label="Quantidade a lançar"><Input type="number" min="0" step="0.001" value={birdMedicationForm.qty||""} onChange={e=>setBirdMedicationForm(f=>({...f,qty:e.target.value}))}/></Field>
        <Field label="Reaplicar?"><div style={{display:"flex",alignItems:"center",gap:10,paddingTop:8}}><input type="checkbox" checked={!!birdMedicationForm.reapplyEnabled} onChange={e=>setBirdMedicationForm(f=>({...f,reapplyEnabled:e.target.checked}))}/><span style={{fontSize:13,color:T.text}}>Gravar reaplicação com intervalo.</span></div></Field>
        {birdMedicationForm.reapplyEnabled && <><Field label="Intervalo comum"><Select value={birdMedicationForm.reapplyPreset||"3"} onChange={e=>setBirdMedicationForm(f=>({...f,reapplyPreset:e.target.value}))}>{commonBirdMedicationIntervals.map(item=><option key={item.value} value={item.value}>{item.label}</option>)}</Select></Field>{birdMedicationForm.reapplyPreset === "custom" && <Field label="Outro intervalo (dias)"><Input type="number" min="1" value={birdMedicationForm.customIntervalDays||""} onChange={e=>setBirdMedicationForm(f=>({...f,customIntervalDays:e.target.value}))}/></Field>}</>}
        <Field label="Observações"><Textarea rows={2} value={birdMedicationForm.notes||""} onChange={e=>setBirdMedicationForm(f=>({...f,notes:e.target.value}))}/></Field>
        <div style={{fontSize:12,color:T.muted}}>A sugestão automática usa a área de posologia cadastrada no produto do estoque. Se você alterar a quantidade sugerida, o sistema registra que a dose foi revisada pelo usuário.</div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:10}}><Btn variant="ghost" onClick={closeAllBirdInlinePanels}>Cancelar</Btn><Btn onClick={registerBirdMedication}>Salvar</Btn></div>
      </div>; })()}</AnimalInlinePanel>}

      {birdMortalityForm && <AnimalInlinePanel theme={T} title="Registrar mortes do lote" onClose={closeAllBirdInlinePanels} tone="danger">
        <div style={{display:"grid",gap:14}}>
          <Field label="Quantidade"><Input type="number" min="0" value={birdMortalityForm.qty||""} onChange={e=>setBirdMortalityForm(f=>({...f,qty:e.target.value}))}/></Field>
          <Field label="Motivo"><Select value={birdMortalityForm.reason||birdMortalityReasons[0]||"Outro"} onChange={e=>setBirdMortalityForm(f=>({...f,reason:e.target.value}))}>{birdMortalityReasons.map(reason=><option key={reason}>{reason}</option>)}</Select></Field>
          <Field label="Observações"><Textarea rows={2} value={birdMortalityForm.notes||""} onChange={e=>setBirdMortalityForm(f=>({...f,notes:e.target.value}))}/></Field>
          <div style={{display:"flex",justifyContent:"flex-end",gap:10}}><Btn variant="ghost" onClick={closeAllBirdInlinePanels}>Cancelar</Btn><Btn onClick={registerBirdMortality}>Salvar</Btn></div>
        </div>
      </AnimalInlinePanel>}

      {birdEggForm && <AnimalInlinePanel theme={T} title="Coleta de ovos" onClose={closeAllBirdInlinePanels} tone="success">
        {(()=>{
          const lot = getBirdLotById(birdEggForm.lotId);
          const groups = getBirdEggDailyGroups(lot);
          const selectedDay = birdEggForm.selectedDay || groups[0]?.date || today();
          return <div style={{display:"grid",gap:14}}>
            <div style={{fontSize:13,color:T.muted}}>Lote: <strong style={{color:T.text}}>{lot?.name||"—"}</strong> · média 30d <strong style={{color:T.text}}>{birdEggAverage(lot)}</strong> ovos/dia</div>
            <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:12}}>
              <Field label="Data da coleta"><Input type="date" value={birdEggForm.date||today()} onChange={e=>setBirdEggForm(f=>({...f,date:e.target.value, selectedDay:e.target.value}))}/></Field>
              <Field label="Horário da coleta"><Input type="time" value={birdEggForm.time||toLocalDateTimeParts().time} onChange={e=>setBirdEggForm(f=>({...f,time:e.target.value}))}/></Field>
            </div>
            <Field label="Quantidade de ovos da coleta"><Input type="number" min="0" value={birdEggForm.qty||""} onChange={e=>setBirdEggForm(f=>({...f,qty:e.target.value}))}/></Field>
            <Field label="Observação (opcional)"><Textarea rows={2} value={birdEggForm.notes||""} onChange={e=>setBirdEggForm(f=>({...f,notes:e.target.value}))} placeholder="Observação opcional da coleta"/></Field>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap"}}>
              <button type="button" onClick={()=>setBirdEggInfoOpen(v=>!v)} style={{appearance:"none",border:`1px solid ${T.border}`,background:T.card2,color:T.muted,borderRadius:999,padding:"6px 10px",fontSize:12,fontWeight:700,cursor:"pointer"}}>i Fórmula das médias</button>
              <div style={{display:"flex",justifyContent:"flex-end",gap:10}}><Btn variant="ghost" onClick={closeAllBirdInlinePanels}>Cancelar</Btn><Btn onClick={registerBirdEggs}>{birdEggForm.editEntryId?"Salvar edição":"Salvar coleta"}</Btn></div>
            </div>
            {birdEggInfoOpen && <div style={{fontSize:11,color:T.muted,background:T.card2,border:`1px solid ${T.border}`,borderRadius:10,padding:"8px 10px"}}>Média do mês = total de ovos acumulado no mês até a data ÷ dia do mês. Média 30d = total de ovos dos últimos 30 dias ÷ 30.</div>}
            <div style={{display:"grid",gap:10}}>
              <div style={{fontWeight:800,color:T.text}}>Coletas do dia</div>
              {groups.length===0 ? <div style={{fontSize:12,color:T.muted,background:T.card2,border:`1px solid ${T.border}`,borderRadius:12,padding:12}}>Nenhuma coleta registrada ainda.</div> : groups.map(group=>{
                const monthAvg = getBirdEggMonthAverage(lot, group.date);
                const expanded = selectedDay === group.date;
                return <div key={group.date} style={{display:"grid",gap:8}}>
                  <button type="button" onClick={()=>setBirdEggForm(f=>({...f,selectedDay: expanded ? "" : group.date}))} style={{appearance:"none",textAlign:"left",background:T.card2,border:`1px solid ${T.border}`,borderRadius:12,padding:12,cursor:"pointer",display:"grid",gap:8}}>
                    <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr 1fr":"1.2fr .8fr .9fr .8fr .8fr",gap:10,fontSize:12,color:T.text,alignItems:"center"}}>
                      <div><div style={{fontSize:10,color:T.muted,textTransform:"uppercase"}}>Data</div><strong>{fmtDate(group.date)}</strong></div>
                      <div><div style={{fontSize:10,color:T.muted,textTransform:"uppercase"}}>Coletas</div><strong>{group.entries.length}</strong></div>
                      <div><div style={{fontSize:10,color:T.muted,textTransform:"uppercase"}}>Qtd. ovos</div><strong>{group.totalQty}</strong></div>
                      <div><div style={{fontSize:10,color:T.muted,textTransform:"uppercase"}}>Média/mês</div><strong>{monthAvg}</strong></div>
                      <div><div style={{fontSize:10,color:T.muted,textTransform:"uppercase"}}>Observação</div><strong>{group.withNotes?"Sim":"Não"}</strong></div>
                    </div>
                  </button>
                  {expanded && <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:12,display:"grid",gap:10}}>
                    <div style={{fontWeight:800,color:T.text}}>Detalhe das coletas de {fmtDate(group.date)}</div>
                    {group.entries.sort((a,b)=>String(a._time).localeCompare(String(b._time))).map(entry=><div key={entry.id} style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:10,padding:10,display:"grid",gap:6}}>
                      <div style={{display:"flex",justifyContent:"space-between",gap:10,flexWrap:"wrap",fontSize:12,color:T.text}}>
                        <div>Horário: <strong>{entry._time||"—"}</strong></div>
                        <div>Qtd.: <strong>{entry.qty}</strong></div>
                        <div>Observação: <strong>{String(entry.notes||"").trim()?"Sim":"Não"}</strong></div>
                      </div>
                      {String(entry.notes||"").trim() && <div style={{fontSize:12,color:T.muted,whiteSpace:"pre-wrap"}}>{entry.notes}</div>}
                      <div style={{display:"flex",justifyContent:"flex-end"}}><Btn size="sm" variant="ghost" onClick={()=>setBirdEggForm(f=>({...f, date:entry._date, time:entry._time||toLocalDateTimeParts().time, qty:String(entry.qty||""), notes:entry.notes||"", selectedDay:group.date, editEntryId:entry.id }))}>Editar</Btn></div>
                    </div>)}
                  </div>}
                </div>;
              })}
            </div>
          </div>;
        })()}
      </AnimalInlinePanel>}

      {birdTransferForm && <AnimalInlinePanel theme={T} title="Transferir lote para Corte / Postura" onClose={closeAllBirdInlinePanels} tone="warning">
        <div style={{display:"grid",gap:14}}>
          <Field label="Quantidade para Corte"><Input type="number" min="0" value={birdTransferForm.qtyCorte||""} onChange={e=>setBirdTransferForm(f=>({...f,qtyCorte:e.target.value}))}/></Field>
          <Field label="Quantidade para Postura"><Input type="number" min="0" value={birdTransferForm.qtyPostura||""} onChange={e=>setBirdTransferForm(f=>({...f,qtyPostura:e.target.value}))}/></Field>
          <Field label="Observações"><Textarea rows={2} value={birdTransferForm.notes||""} onChange={e=>setBirdTransferForm(f=>({...f,notes:e.target.value}))}/></Field>
          <div style={{display:"flex",justifyContent:"flex-end",gap:10}}><Btn variant="ghost" onClick={closeAllBirdInlinePanels}>Cancelar</Btn><Btn onClick={registerBirdTransfer}>Transferir</Btn></div>
        </div>
      </AnimalInlinePanel>}
    </>;
  }

  function renderBirdStage(){
    const stageLots = birdLotsByStage[birdTab] || [];
    return <>
      {renderBirdStats()}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:10}}>
        <div style={{fontSize:13,color:T.muted}}>{birdStageDefs[birdTab].label} · gerenciamento por lote com rastreabilidade, estoque e histórico.</div>
        {canEdit && <Btn onClick={()=>{ closeAllBirdInlinePanels(); openNewBirdLot(birdTab); }}>+ Novo Lote</Btn>}
      </div>
      {birdLotForm?._new && renderBirdInlinePanels("__new__")}
      {stageLots.length===0 ? (
        <div style={{color:T.muted,textAlign:"center",padding:42,background:T.card,border:`1px solid ${T.border}`,borderRadius:14}}>Nenhum lote cadastrado nesta etapa.</div>
      ) : (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:14}}>{stageLots.map(renderBirdLotCard)}</div>
      )}
    </>;
  }

  React.useEffect(()=>{
    if(!currentBirdInlineLotId || typeof document === "undefined") return;
    const el = document.getElementById(`bird-inline-anchor-${currentBirdInlineLotId}`);
    if(!el) return;
    requestAnimationFrame(()=>{
      try { el.scrollIntoView({ behavior:"smooth", block:"start" }); } catch(_) {}
    });
  }, [currentBirdInlineLotId]);


  const nextServiceAnimalCode = React.useCallback(()=>{
    const maxCode = (animals||[]).reduce((max, item)=>{
      const m = String(item?.serviceCode || item?.tag || "").match(/AS[_-]?(\d+)/i);
      return Math.max(max, m ? Number(m[1]) : 0);
    }, 0);
    return `AS_${String(maxCode + 1).padStart(3, "0")}`;
  }, [animals]);

  function openNewServiceAnimal(mode="manual"){
    const base = {
      _new:true,
      mode,
      name:"",
      sex:"Macho",
      breed:"",
      status:"Ativo",
      notes:"",
      rationConsumer:true,
      harasEquineId:"",
      serviceCode: nextServiceAnimalCode(),
      medications:[],
    };
    setServiceAnimalForm(base);
  }


  function saveFoal(){
    if(!String(foalForm?.name||"").trim()){ show("Informe o nome do potro.", "error"); return; }
    const father = foalParentOptions.find(item=>String(item.id)===String(foalForm?.fatherHarasId||"")) || null;
    const mother = String(foalForm?.motherSource||"haras") === "haras"
      ? (foalParentOptions.find(item=>String(item.id)===String(foalForm?.motherHarasId||"")) || null)
      : null;
    const record = {
      id: foalForm?._new ? createClientId("ani") : foalForm.id,
      species:"Equino",
      equinoCategory:"potro",
      costCenter:"Pecuária",
      status: foalForm?.status || "Ativo",
      name:String(foalForm?.name||"").trim(),
      sex:foalForm?.sex || "Macho",
      breed:String(foalForm?.breed||"").trim(),
      foalAgeRange:foalForm?.foalAgeRange || "0-6",
      fatherHarasId: father?.id || "",
      fatherName: father?.name || "",
      motherHarasId: mother?.id || "",
      motherName: mother?.name || String(foalForm?.motherNameManual||"").trim(),
      born: foalForm?.born || "",
      notes: foalForm?.notes || "",
      medications: Array.isArray(foalForm?.medications) ? foalForm.medications : [],
      ...(!foalForm?._new ? {} : makeCreateMeta(session?.name||"")),
      ...makeUpdateMeta(session?.name||""),
    };
    setAnimals(prev=>{ const prepared = foalForm?._new ? makeAnimalCreatePending({ ...record, clientId: record.clientId || record.id }, prev||[]) : makeAnimalUpdatePending(record); return dedupeById([...(prev||[]).filter(item=>String(item.id)!==String(record.id)), prepared]); });
    setFoalForm(null);
    show(foalForm?._new ? "✅ Potro cadastrado." : "✅ Potro atualizado.");
    syncOnSave("animals");
  }

  function saveServiceAnimal(){
    if(String(serviceAnimalForm?.mode||"manual") === "haras" && !String(serviceAnimalForm?.harasEquineId||"").trim()){
      show("Selecione um equino do Haras para transferir.", "error");
      return;
    }
    if(!String(serviceAnimalForm?.name||"").trim() && String(serviceAnimalForm?.mode||"manual") !== "haras"){
      show("Informe o nome do animal de serviço.", "error");
      return;
    }
    const sourceAnimal = availableHarasEquines.find(item=>String(item.id)===String(serviceAnimalForm?.harasEquineId||"")) || null;
    const baseName = String(serviceAnimalForm?.mode||"manual") === "haras" ? (sourceAnimal?.name || "") : String(serviceAnimalForm?.name||"").trim();
    if(!baseName){ show("Não foi possível identificar o animal selecionado.", "error"); return; }
    const record = {
      id: serviceAnimalForm?._new ? createClientId("ani") : serviceAnimalForm.id,
      species:"Equino",
      equinoCategory:"service",
      equinoAgeClass: resolveEquineCategoryByAgeMonths(monthsBetween(sourceAnimal?.born || sourceAnimal?.birthDate || sourceAnimal?.bornApprox || sourceAnimal?.approxBirthDate || serviceAnimalForm?.born || serviceAnimalForm?.birthDate || "")),
      costCenter:"Pecuária",
      sourceType: String(serviceAnimalForm?.mode||"manual") === "haras" ? "Transferido do Haras" : "Cadastro manual",
      harasEquineId: sourceAnimal?.id || serviceAnimalForm?.harasEquineId || "",
      name: baseName,
      sex: serviceAnimalForm?.sex || sourceAnimal?.sex || "Macho",
      breed: String(serviceAnimalForm?.breed || sourceAnimal?.breed || "").trim(),
      status: serviceAnimalForm?.status || sourceAnimal?.status || "Ativo",
      notes: serviceAnimalForm?.notes || "",
      rationConsumer: serviceAnimalForm?.rationConsumer !== false,
      serviceCode: String(serviceAnimalForm?.serviceCode || sourceAnimal?.serviceCode || nextServiceAnimalCode()).trim(),
      tag: String(serviceAnimalForm?.tag || sourceAnimal?.tag || serviceAnimalForm?.serviceCode || "").trim(),
      medications: Array.isArray(serviceAnimalForm?.medications) ? serviceAnimalForm.medications : [],
      ...(!serviceAnimalForm?._new ? {} : makeCreateMeta(session?.name||"")),
      ...makeUpdateMeta(session?.name||""),
    };
    setAnimals(prev=>{ const prepared = serviceAnimalForm?._new ? makeAnimalCreatePending({ ...record, clientId: record.clientId || record.id }, prev||[]) : makeAnimalUpdatePending(record); return dedupeById([...(prev||[]).filter(item=>String(item.id)!==String(record.id)), prepared]); });
    setServiceAnimalForm(null);
    show(serviceAnimalForm?._new ? "✅ Animal de serviço cadastrado." : "✅ Animal de serviço atualizado.");
    syncOnSave("animals");
  }

  function consumeStockGeneric(itemId, qty, notes, originId, origin, costCenter="Pecuária"){
    const item = birdStockItems.find(s=>String(s.id)===String(itemId||""));
    const quantity = Math.max(0, Number(qty||0));
    const available = Number(item?.quantity||0);
    if(!itemId || quantity<=0){ show("Selecione o item e informe a quantidade.", "error"); return null; }
    if(quantity > available){ show(`Estoque insuficiente. Disponível: ${available}.`, "error"); return null; }
    const ts = new Date().toISOString();
    const movement = {
      id:createClientId("smv"),
      clientId:createClientId("smv"),
      itemId,
      type:"saida",
      qty:quantity,
      notes,
      by:session?.name||"",
      costCenter,
      ts,
      updatedAt:ts,
      farmId: currentFarmId || "",
      origin,
      originId,
      ...makeCreateMeta(session?.name||"", { updatedAt: ts }),
    };
    setStockMovements(prev=>dedupeById([...(prev||[]), movement]));
    return { item, ts, movement };
  }

  const getEquineMedicationSuggestion = React.useCallback((form, animalCategory="Potros")=>{
    const item = equineMedicationStockItems.find(s=>String(s.id)===String(form?.stockId||"") && itemSupportsCategory(s, "medication", "Equino", animalCategory));
    if(!item) return { profile:null, qty:null, formula:"" };
    return computeSuggestedUsage({ item, kind:"medication", species:"Equino", category:animalCategory, sex:String(form?.sex||"").trim(), animalCount:1, avgWeightKg:Number(form?.avgWeightKg||0) });
  }, [equineMedicationStockItems, itemSupportsCategory, computeSuggestedUsage]);
  const getEquineNutritionSuggestion = React.useCallback((form, animalCategory="Potros", animalCount=0)=>{
    const item = equineRationStockItems.find(s=>String(s.id)===String(form?.stockId||"") && itemSupportsCategory(s, "nutrition", "Equino", animalCategory));
    if(!item) return { profile:null, qty:null, formula:"" };
    return computeSuggestedUsage({ item, kind:"nutrition", species:"Equino", category:animalCategory, animalCount:Number(animalCount||0), avgWeightKg:Number(form?.avgWeightKg||0) });
  }, [equineRationStockItems, itemSupportsCategory, computeSuggestedUsage]);

  const buildMedicationSummaryLabel = React.useCallback((itemName="", qty=0, targetLabel="animal")=>{
    const safeQty = Math.round(Number(qty || 0) * 1000) / 1000;
    const prettyQty = Number.isFinite(safeQty) ? fmtMeasure(safeQty, 3) : "0";
    return [String(itemName||"").trim(), prettyQty ? `${prettyQty} mL ${targetLabel}` : ""].filter(Boolean).join(" • ");
  }, [fmtMeasure]);

  const buildMedicationInlineReview = React.useCallback(({ suggestion, item, targetLabel="animal", highlightLabel="Dosagem calculada pelo app", extraLines=[] })=>{
    if(!suggestion?.profile || suggestion?.qty == null) return null;
    const compatibility = suggestion?.compatibility || null;
    const warningParts = [];
    if(compatibility){
      if(compatibility.allowed === false) warningParts.push("perfil marcado como não habilitado no catálogo");
      if(compatibility.speciesAllowed === false) warningParts.push("espécie fora da recomendação");
      if(compatibility.categoryAllowed === false) warningParts.push("categoria fora da recomendação");
      if(compatibility.sexAllowed === false) warningParts.push("sexo fora da recomendação");
      if(compatibility.weightPending === true) warningParts.push("peso aguardando confirmação manual");
    }
    return {
      title: highlightLabel,
      amountText: `${fmtMeasure(suggestion.qty, 3)} ${item?.unit || suggestion?.profile?.doseUnit || "mL"} ${targetLabel}`,
      formula: suggestion?.formula || "",
      notes: suggestion?.profile?.notes || "",
      warning: warningParts.length ? `Atenção: ${warningParts.join(" · ")}.` : "",
      extraLines: Array.isArray(extraLines) ? extraLines.filter(Boolean) : [],
    };
  }, [fmtMeasure]);

  const daysBetweenNow = React.useCallback((value)=>{
    const ts = new Date(value || '').getTime();
    if(!Number.isFinite(ts)) return Number.POSITIVE_INFINITY;
    return Math.floor((Date.now() - ts) / 86400000);
  }, []);
  const safePositiveWeight = React.useCallback((value)=>{
    const num = Number(value || 0);
    return num > 0 ? num : 0;
  }, []);
  const inferAnimalCategory = React.useCallback((animalLike={})=>{
    const species = String(animalLike?.species || '').trim();
    if(species === 'Equino') return resolveEquineCategoryByAgeMonths(monthsBetween(animalLike?.born || animalLike?.birthDate || animalLike?.bornApprox || animalLike?.approxBirthDate || ''));
    if(species === 'Bovino') return bovineCategoryByAgeRange(inferAnimalAgeRangeId(animalLike), animalLike?.sex, animalLike?.purpose);
    if(species === 'Ave') return birdCategoryByStage(animalLike?.birdStage || animalLike?.animalCategory || 'growth');
    if(species === 'Ovino') return inferAnimalAgeRangeId(animalLike) === '0-12' ? 'Cordeiros' : (String(animalLike?.sex||'') === 'Macho' ? 'Reprodutores' : 'Matrizes');
    return String(animalLike?.ageLabel || animalLike?.category || '').trim();
  }, [monthsBetween, bovineCategoryByAgeRange, inferAnimalAgeRangeId, birdCategoryByStage]);
  const normalizeSexLabel = React.useCallback((value='')=>{
    const raw = String(value||'').trim().toLowerCase();
    if(!raw) return 'ambos';
    if(['m','masc','macho','male'].includes(raw)) return 'macho';
    if(['f','fem','fêmea','femea','female'].includes(raw)) return 'fêmea';
    if(['ambos','misto','todos','all'].includes(raw)) return 'ambos';
    return raw.normalize('NFD').replace(/[̀-ͯ]/g, '');
  }, []);

  const collectFarmProfileWeights = React.useCallback(({ species='', category='', sex='' })=>{
    const animalWeights = (animals || []).filter(item=>String(item?.species||'')===String(species||'')).filter(item=>{
      if(sex && normalizeSexLabel(item?.sex||'Ambos') !== normalizeSexLabel(sex||'Ambos')) return false;
      const inferred = inferAnimalCategory(item);
      return !category || inferred === category;
    }).map(item=>safePositiveWeight(item?.weight)).filter(Boolean);
    const lotWeights = (lots || []).filter(item=>String(item?.species||'')===String(species||'')).filter(item=>{
      if(sex && String(item?.sex||'').trim() && String(item?.sex||'Misto').trim() !== 'Misto' && normalizeSexLabel(item?.sex||'Ambos') !== normalizeSexLabel(sex||'Ambos')) return false;
      const categories = species === 'Bovino' ? bovineCategoriesForLot(item) : [inferAnimalCategory(item)];
      return !category || categories.includes(category);
    }).map(item=>safePositiveWeight(item?.avgWeight)).filter(Boolean);
    return [...animalWeights, ...lotWeights];
  }, [animals, lots, normalizeSexLabel, inferAnimalCategory, safePositiveWeight, bovineCategoriesForLot]);
  const getFarmAverageProfileWeight = React.useCallback(({ species='', category='', sex='' })=>{
    const values = collectFarmProfileWeights({ species, category, sex });
    if(!values.length) return 0;
    return Math.round((values.reduce((sum, value)=>sum + Number(value||0), 0) / values.length) * 10) / 10;
  }, [collectFarmProfileWeights]);
  const getLotLatestWeightMeta = React.useCallback((lotLike={})=>{
    const weightFromMovement = (lotLike?.movements || []).filter(entry=>String(entry?.type||'') === 'pesagem' && Number(entry?.weight||0) > 0).sort((a,b)=>String(b?.ts||'').localeCompare(String(a?.ts||'')))[0] || null;
    if(weightFromMovement) return { weightKg:Number(weightFromMovement.weight||0), recordedAt:weightFromMovement.ts || weightFromMovement.updatedAt || weightFromMovement.createdAt || '', source:'Última pesagem do lote' };
    const avgWeight = safePositiveWeight(lotLike?.avgWeight);
    if(avgWeight > 0) return { weightKg:avgWeight, recordedAt:lotLike?.updatedAt || lotLike?.formedAt || lotLike?.createdAt || '', source:'Peso médio atual do lote' };
    return { weightKg:0, recordedAt:'', source:'' };
  }, [safePositiveWeight]);
  const getAnimalLatestWeightMeta = React.useCallback((animalLike={})=>{
    const weightKg = safePositiveWeight(animalLike?.weight);
    if(weightKg > 0) return { weightKg, recordedAt:animalLike?.updatedAt || animalLike?.createdAt || animalLike?.born || '', source:'Último peso do animal' };
    return { weightKg:0, recordedAt:'', source:'' };
  }, [safePositiveWeight]);
  const resolveMedicationWeightGuide = React.useCallback(({ species='', category='', sex='', animalLike=null, lotLike=null, typedWeightKg=0 })=>{
    const latest = lotLike ? getLotLatestWeightMeta(lotLike) : getAnimalLatestWeightMeta(animalLike||{});
    const lastWeightKg = safePositiveWeight(latest?.weightKg);
    const daysOld = lastWeightKg > 0 ? daysBetweenNow(latest?.recordedAt) : Number.POSITIVE_INFINITY;
    const averageWeightKg = getFarmAverageProfileWeight({ species, category, sex });
    const typed = safePositiveWeight(typedWeightKg);
    let recommendedWeightKg = typed;
    let suggestedWeightKg = typed;
    let shouldAutofill = typed > 0;
    let status = typed > 0 ? 'typed' : 'missing';
    let message = 'Informe o peso para o app calcular a dose ideal.';
    if(!typed){
      if(lastWeightKg > 0 && daysOld <= 15){
        recommendedWeightKg = lastWeightKg;
        suggestedWeightKg = lastWeightKg;
        shouldAutofill = true;
        status = 'recent';
        message = `Última pesagem com ${daysOld} dia(s). O app usou ${fmtMeasure(lastWeightKg,1)} kg e aguarda sua confirmação.`;
      } else if(lastWeightKg > 0 && daysOld <= 45){
        recommendedWeightKg = lastWeightKg;
        suggestedWeightKg = lastWeightKg;
        shouldAutofill = true;
        status = 'stale';
        message = `Última pesagem com ${daysOld} dia(s). Revise o peso atual; o último peso (${fmtMeasure(lastWeightKg,1)} kg) ficou sugerido.`;
      } else if(averageWeightKg > 0){
        recommendedWeightKg = 0;
        suggestedWeightKg = averageWeightKg;
        shouldAutofill = false;
        status = 'farmAverage';
        message = `Sem pesagem recente. Informe o peso atual; a média da fazenda para ${category || species} ficou como referência (${fmtMeasure(averageWeightKg,1)} kg).`;
      }
    }
    return { latestWeightKg:lastWeightKg, latestRecordedAt:latest?.recordedAt || '', latestSource:latest?.source || '', daysOld, averageWeightKg, recommendedWeightKg, suggestedWeightKg, shouldAutofill, status, message };
  }, [getLotLatestWeightMeta, getAnimalLatestWeightMeta, safePositiveWeight, daysBetweenNow, getFarmAverageProfileWeight, fmtMeasure]);
  const getMedicationEffectiveWeightKg = React.useCallback((typedWeightKg=0, weightGuide=null)=>{
    const typed = safePositiveWeight(typedWeightKg);
    if(typed > 0) return typed;
    if(weightGuide?.shouldAutofill) return safePositiveWeight(weightGuide?.recommendedWeightKg);
    return 0;
  }, [safePositiveWeight]);
  const getMedicationWeightInputValue = React.useCallback((typedWeightKg=0, weightGuide=null)=>{
    const typed = String(typedWeightKg || '').trim();
    if(typed) return typed;
    if(weightGuide?.shouldAutofill && safePositiveWeight(weightGuide?.recommendedWeightKg) > 0) return String(weightGuide.recommendedWeightKg);
    return '';
  }, [safePositiveWeight]);

  function buildEquineMedicationEntry(form, consumed, summary={}){
    const rawInterval = form?.reapplyPreset === "custom" ? Number(form?.customIntervalDays||0) : Number(form?.reapplyPreset||0);
    const intervalDays = form?.reapplyEnabled && rawInterval > 0 ? rawInterval : 0;
    const nextDate = intervalDays > 0 ? new Date(new Date(consumed.ts).getTime() + (intervalDays * 86400000)).toISOString().slice(0,10) : "";
    const item = consumed?.item || {};
    return {
      id:createClientId("smd"),
      ts:consumed.ts,
      stockId:form?.stockId,
      itemName:item?.name||"",
      itemCategory:item?.category||"",
      itemUnit:item?.unit||"",
      itemCostCenter:item?.costCenter||"",
      qty:Number(form?.qty||0),
      avgWeightKg:Number(summary?.weightKg || form?.avgWeightKg || 0),
      dosageUsed: String(form?.dosageUsed||"").trim(),
      medicationSummaryLabel: buildMedicationSummaryLabel(item?.name||"Medicamento", Number(form?.qty||0), "por animal"),
      notes:[form?.notes||"", summary?.warning||""].filter(Boolean).join(" "),
      compatibilityWarning: summary?.warning || "",
      reapplyEnabled: !!form?.reapplyEnabled,
      reapplyIntervalDays: intervalDays,
      reapplyLabel: intervalDays > 0 ? `${intervalDays} dia${intervalDays>1?"s":""}` : "",
      nextReapplyAt: nextDate,
      by:session?.name||"",
    };
  }

  function registerFoalMedication(){
    if(!foalMedicationForm?.animalId) return;
    const animal = foalAnimals.find(item=>String(item.id)===String(foalMedicationForm.animalId));
    if(!animal){ show("Potro não encontrado.", "error"); return; }
    const effectiveWeightKg = getMedicationEffectiveWeightKg(foalMedicationForm?.avgWeightKg, resolveMedicationWeightGuide({ species:"Equino", category:"Potros", sex:String(foalMedicationForm?.sex || animal?.sex || ""), animalLike:animal, typedWeightKg:foalMedicationForm?.avgWeightKg }));
    const suggestion = getEquineMedicationSuggestion({ ...foalMedicationForm, avgWeightKg:effectiveWeightKg, sex:foalMedicationForm?.sex || animal?.sex || "" }, "Potros");
    const warning = suggestion?.compatibility && (suggestion.compatibility.allowed === false || suggestion.compatibility.speciesAllowed === false || suggestion.compatibility.categoryAllowed === false || suggestion.compatibility.sexAllowed === false) ? 'Uso confirmado fora da recomendação de espécie/categoria/sexo do medicamento.' : '';
    const payload = { ...foalMedicationForm, avgWeightKg:effectiveWeightKg, notes:[foalMedicationForm.notes||"", warning].filter(Boolean).join(' ') };
    const consumed = consumeStockGeneric(payload.stockId, payload.qty, `Medicamento · Potro ${animal.name}`, animal.id, "foal_medication", "Pecuária");
    if(!consumed) return;
    const entry = buildEquineMedicationEntry(payload, consumed, { weightKg:effectiveWeightKg, warning });
    setAnimals(prev=>dedupeById((prev||[]).map(item=>String(item.id)===String(animal.id)?{ ...item, medications:[...(item.medications||[]), entry], ...makeUpdateMeta(session?.name||"") }:item)));
    setFoalMedicationForm(null);
    show("✅ Medicamento do potro lançado!");
    syncOnSave("animals");
    syncOnSave("stockMovements");
  }

  function registerServiceMedication(){
    if(!serviceMedicationForm?.animalId) return;
    const animal = serviceAnimals.find(item=>String(item.id)===String(serviceMedicationForm.animalId));
    if(!animal){ show("Animal não encontrado.", "error"); return; }
    const effectiveWeightKg = getMedicationEffectiveWeightKg(serviceMedicationForm?.avgWeightKg, resolveMedicationWeightGuide({ species:"Equino", category:"Equinos Adultos", sex:String(serviceMedicationForm?.sex || animal?.sex || ""), animalLike:animal, typedWeightKg:serviceMedicationForm?.avgWeightKg }));
    const suggestion = getEquineMedicationSuggestion({ ...serviceMedicationForm, avgWeightKg:effectiveWeightKg, sex:serviceMedicationForm?.sex || animal?.sex || "" }, "Equinos Adultos");
    const warning = suggestion?.compatibility && (suggestion.compatibility.allowed === false || suggestion.compatibility.speciesAllowed === false || suggestion.compatibility.categoryAllowed === false || suggestion.compatibility.sexAllowed === false) ? 'Uso confirmado fora da recomendação de espécie/categoria/sexo do medicamento.' : '';
    const payload = { ...serviceMedicationForm, avgWeightKg:effectiveWeightKg, notes:[serviceMedicationForm.notes||"", warning].filter(Boolean).join(' ') };
    const consumed = consumeStockGeneric(payload.stockId, payload.qty, `Medicamento · ${animal.name}`, animal.id, "service_medication", animal.costCenter || "Pecuária");
    if(!consumed) return;
    const entry = buildEquineMedicationEntry(payload, consumed, { weightKg:effectiveWeightKg, warning });
    setAnimals(prev=>dedupeById((prev||[]).map(item=>String(item.id)===String(animal.id)?{ ...item, medications:[...(item.medications||[]), entry], ...makeUpdateMeta(session?.name||"") }:item)));
    setServiceMedicationForm(null);
    if(serviceAnimalDetail && String(serviceAnimalDetail.id)===String(animal.id)){
      setServiceAnimalDetail(cur=>cur?{ ...cur, medications:[...(cur.medications||[]), entry] }:cur);
    }
    show("✅ Medicamento lançado!");
    syncOnSave("animals");
    syncOnSave("stockMovements");
  }

  function registerFoalNutrition(){
    const qty = Math.max(0, Number(foalNutritionForm?.qty||0));
    if(foalAnimals.length<=0){ show("Cadastre ao menos um potro para lançar a nutrição geral.", "error"); return; }
    const consumed = consumeStockGeneric(foalNutritionForm?.stockId, qty, `Nutrição geral · Potros (${foalAnimals.length} animais)`, foalNutritionLatest?.id || createClientId("afn"), "foal_nutrition", "Pecuária");
    if(!consumed) return;
    const entry = { id:createClientId("afn"), ts:consumed.ts, stockId:foalNutritionForm.stockId, itemName:consumed.item?.name||"", qty, period:foalNutritionForm?.period||"Diário", notes:foalNutritionForm.notes||"", consumerCount:foalAnimals.length, avgPerAnimal: foalAnimals.length ? Math.round((qty/foalAnimals.length)*100)/100 : 0, by:session?.name||"" };
    setFoalNutritionEntries(prev=>dedupeById([...(prev||[]), { ...entry, farmId: currentFarmId || "", costCenter:"Pecuária", ...makeCreateMeta(session?.name||"") }]));
    setFoalNutritionForm(null);
    show("✅ Nutrição geral dos potros lançada!");
    syncOnSave("animalFoalNutrition");
    syncOnSave("stockMovements");
  }

  function registerServiceNutrition(){
    const qty = Math.max(0, Number(serviceNutritionForm?.qty||0));
    if(serviceNutritionEnabledCount<=0){ show("Marque ao menos um animal apto ao consumo de ração.", "error"); return; }
    const consumed = consumeStockGeneric(serviceNutritionForm?.stockId, qty, `Nutrição geral · Equinos de serviço (${serviceNutritionEnabledCount} animais)`, serviceNutritionLatest?.id || createClientId("asn"), "service_nutrition", "Pecuária");
    if(!consumed) return;
    const entry = { id:createClientId("asn"), ts:consumed.ts, stockId:serviceNutritionForm.stockId, itemName:consumed.item?.name||"", qty, period:serviceNutritionForm?.period||"Diário", notes:serviceNutritionForm.notes||"", consumerCount:serviceNutritionEnabledCount, avgPerAnimal: serviceNutritionEnabledCount ? Math.round((qty/serviceNutritionEnabledCount)*100)/100 : 0, by:session?.name||"" };
    setServiceNutritionEntries(prev=>dedupeById([...(prev||[]), { ...entry, farmId: currentFarmId || "", costCenter:"Pecuária", ...makeCreateMeta(session?.name||"") }]));
    setServiceNutritionForm(null);
    show("✅ Nutrição geral lançada!");
    syncOnSave("animalServiceNutrition");
    syncOnSave("stockMovements");
  }

  function renderFoalsTab(){
    const summary = foalAgeRanges.map(range=>({
      ...range,
      male: foalAnimals.filter(item=>item.foalAgeRange===range.id && item.sex==="Macho").length,
      female: foalAnimals.filter(item=>item.foalAgeRange===range.id && item.sex==="Fêmea").length,
    }));
    return <>
      <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr 1fr":"repeat(4,minmax(0,1fr))",gap:12,marginBottom:18}}>
        {summary.map(item=><StatCard key={item.id} icon={renderCardImageIcon(equineMarchadorCardIcon, item.label)} label={item.label} value={item.male + item.female} sub={`M ${item.male} · F ${item.female}`} color={T.pink} iconPosition="middleRight" iconWrapStyle={{width:76,height:76,minWidth:76}} iconStyle={{fontSize:44,width:76,height:76}}/>)}
        <StatCard icon="🥣" label="Última nutrição" value={foalNutritionLatest?.qty || 0} sub={foalNutritionLatest ? `${foalNutritionLatest.period} · ${foalNutritionLatest.itemName||"—"}` : "sem lançamento"} color={T.yellow} iconPosition="middleRight" iconWrapStyle={{width:76,height:76,minWidth:76}} iconStyle={{fontSize:44}}/>
      </div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:10}}>
        <div style={{fontSize:13,color:T.muted}}>Controle simples de potros por sexo e faixa etária, com vínculo opcional de pai e mãe ao plantel do Haras. Nutrição geral por sacos de ração e medicamentos com reaplicação opcional e intervalos operacionais usuais para equinos.</div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {canEdit && <Btn variant="ghost" onClick={()=>setFoalNutritionForm({ stockId:"", qty:"", period:"Diário", notes:"" })}>Nutrição Geral</Btn>}
          {canEdit && <Btn onClick={()=>setFoalForm({_new:true,name:"",sex:"Macho",foalAgeRange:"0-6",fatherHarasId:"",motherSource:"haras",motherHarasId:"",motherNameManual:"",born:"",notes:"",medications:[]})}>+ Novo Potro</Btn>}
        </div>
      </div>
      {foalAnimals.length===0 ? <div style={{color:T.muted,textAlign:"center",padding:42,background:T.card,border:`1px solid ${T.border}`,borderRadius:14}}>Nenhum potro cadastrado.</div> : <div style={{display:"grid",gridTemplateColumns:(currentLivestockInlineLotId && ["Bovino","Ovino"].includes(String(activeSpecies||"")))?"1fr":"repeat(auto-fill,minmax(280px,1fr))",gap:14}}>{foalAnimals.map(item=>{ const meds=(item.medications||[]); const latestMedication=meds.slice().sort((a,b)=>String(b?.ts||"").localeCompare(String(a?.ts||"")))[0] || null; return <div key={item.id} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14,display:"flex",flexDirection:"column",gap:10}}><div style={{display:"flex",justifyContent:"space-between",gap:10}}><div><div style={{fontSize:10,fontWeight:800,color:T.accent,letterSpacing:.6,marginBottom:2}}>{displayAnimalRecordNumber(item)}</div><div style={{fontWeight:800,color:T.text}}>{item.name}</div><div style={{fontSize:12,color:T.muted}}>{foalAgeRanges.find(r=>r.id===item.foalAgeRange)?.label || "Sem faixa"}</div></div><Badge color={item.sex==="Macho"?T.accent:T.pink}>{item.sex}</Badge></div><div style={{fontSize:12,color:T.text}}>Pai: <strong>{item.fatherName || "—"}</strong></div><div style={{fontSize:12,color:T.text}}>Mãe: <strong>{item.motherName || "—"}</strong></div><div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,fontSize:12}}><div>Medic.: <strong>{meds.length}</strong></div><div>Última nutrição: <strong>{foalNutritionLatest?.period || "—"}</strong></div></div>{latestMedication && <div style={{display:"grid",gap:6}}><div style={{fontSize:12,color:T.muted}}>Último med.: {latestMedication.itemName||"Item"}{latestMedication.nextReapplyAt?` · reaplicar em ${fmtDate(latestMedication.nextReapplyAt)}`:""}</div>{latestMedication.medicationSummaryLabel && <div style={{display:"inline-flex",alignItems:"center",gap:6,padding:"6px 10px",borderRadius:999,border:`1px solid ${T.purple}33`,background:`${T.purple}12`,fontSize:12,fontWeight:700,color:T.purple,width:"fit-content"}}>{latestMedication.medicationSummaryLabel}</div>}{latestMedication.dosageUsed && <div style={{fontSize:12,color:T.muted}}>{latestMedication.dosageUsed}</div>}</div>}{item.born && <div style={{fontSize:12,color:T.muted}}>Nascimento: {fmtDate(item.born)}</div>}{item.notes && <div style={{fontSize:12,color:T.muted,whiteSpace:"pre-wrap"}}>{item.notes}</div>}<div style={{display:"flex",justifyContent:"flex-end",gap:8,flexWrap:"wrap"}}>{canEdit && <Btn size="sm" variant="ghost" onClick={()=>setFoalMedicationForm({ animalId:item.id, stockId:"", qty:"", avgWeightKg:String(item.weight||""), sex:item.sex||"", dosageUsed:"", reapplyEnabled:false, reapplyPreset:"14", customIntervalDays:"", notes:"" })}>Medicamento</Btn>}{canEdit && <Btn size="sm" variant="ghost" onClick={()=>setFoalForm({ ...item, _new:false, motherSource:item.motherHarasId?"haras":"manual", motherNameManual:item.motherName||"" })}>Editar</Btn>}</div></div>;})}</div>}
    </>;
  }

  function renderServiceTab(){
    return <>
      {(()=>{
        const cards = filterConfiguredCards([
          { id: currentAnimalSection?.cards?.[0], node:<StatCard icon={renderCardImageIcon(equineMarchadorCardIcon, "Equinos Ativos")} label="Equinos Ativos" value={serviceAnimals.length} sub="adultos" color={T.pink} iconPosition="middleRight" iconWrapStyle={{width:76,height:76,minWidth:76}} iconStyle={{fontSize:44,width:76,height:76}}/> },
          { id: currentAnimalSection?.cards?.[1], node:<StatCard icon={renderCardImageIcon(equineRationCardIcon, "Consumo méd. ração")} label="Consomem ração" value={serviceNutritionEnabledCount} sub="na média geral" color={T.yellow} iconPosition="middleRight" iconWrapStyle={{width:76,height:76,minWidth:76}} iconStyle={{fontSize:44,width:76,height:76}}/> },
          { id: currentAnimalSection?.cards?.[2], node:<StatCard icon="💊" label="Medicamentos" value={serviceAnimals.reduce((sum,item)=>sum + ((item.medications||[]).length),0)} sub="lançamentos" color={T.accent} iconPosition="middleRight" iconWrapStyle={{width:76,height:76,minWidth:76}} iconStyle={{fontSize:44}}/> },
          { id: currentAnimalSection?.cards?.[3], node:<StatCard icon="📦" label="Última nutrição" value={serviceNutritionLatest?.qty || 0} sub={serviceNutritionLatest?.itemName || "sem lançamento"} color={T.green} iconPosition="middleRight" iconWrapStyle={{width:76,height:76,minWidth:76}} iconStyle={{fontSize:44}}/> },
        ], allowedCurrentSectionCards, currentAnimalSection?.cards || []);
        return cards.length ? <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr 1fr":"repeat(4,minmax(0,1fr))",gap:12,marginBottom:18}}>{cards.map(item=><React.Fragment key={item.id}>{item.node}</React.Fragment>)}</div> : null;
      })()}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:10}}>
        <div style={{fontSize:13,color:T.muted}}>Nutrição geral por média dos animais marcados no detalhe, com registro diário, semanal ou mensal em sacos de ração. Medicamentos lançados individualmente com reaplicação opcional e histórico de intervalo gravado no animal.</div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {canEdit && <Btn variant="ghost" onClick={()=>setServiceNutritionForm({ stockId:"", qty:"", period:"Diário", notes:"" })}>Nutrição Geral</Btn>}
          {canEdit && <Btn variant="ghost" onClick={()=>openNewServiceAnimal("haras")}>Transferir do Haras</Btn>}
          {canEdit && <Btn onClick={()=>openNewServiceAnimal("manual")}>+ Novo Animal</Btn>}
        </div>
      </div>
      {serviceAnimals.length===0 ? <div style={{color:T.muted,textAlign:"center",padding:42,background:T.card,border:`1px solid ${T.border}`,borderRadius:14}}>Nenhum animal de serviço cadastrado.</div> : <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(290px,1fr))",gap:14}}>{serviceAnimals.map(item=><div key={item.id} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14,display:"flex",flexDirection:"column",gap:10}}><div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10}}><div><div style={{fontSize:10,fontWeight:800,color:T.accent,letterSpacing:.6,marginBottom:2}}>{displayAnimalRecordNumber(item)}</div><div style={{fontWeight:800,color:T.text}}>{item.name}</div><div style={{fontSize:12,color:T.muted}}>{item.serviceCode || item.tag || "—"} · {item.sourceType || "Cadastro manual"}</div></div><Badge color={item.rationConsumer?T.green:T.muted}>{item.rationConsumer?"Na nutrição":"Fora da média"}</Badge></div><div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,fontSize:12}}><div>Sexo: <strong>{item.sex || "—"}</strong></div><div>Status: <strong>{item.status || "Ativo"}</strong></div><div>Raça: <strong>{item.breed || "—"}</strong></div><div>Medic.: <strong>{(item.medications||[]).length}</strong></div></div>{item.notes && <div style={{fontSize:12,color:T.muted,whiteSpace:"pre-wrap"}}>{item.notes}</div>}<div style={{display:"flex",flexWrap:"wrap",gap:8}}><Btn size="sm" variant="ghost" onClick={()=>setServiceAnimalDetail(item)}>Detalhes</Btn>{canEdit && <Btn size="sm" variant="ghost" onClick={()=>setServiceMedicationForm({ animalId:item.id, stockId:"", qty:"", avgWeightKg:String(item.weight||""), sex:item.sex||"", dosageUsed:"", reapplyEnabled:false, reapplyPreset:"14", customIntervalDays:"", notes:"" })}>Medicamento</Btn>}{canEdit && <Btn size="sm" variant="ghost" onClick={()=>setServiceAnimalForm({ ...item, _new:false, mode:item.harasEquineId?"haras":"manual" })}>Editar</Btn>}</div></div>)}</div>}
    </>;
  }

  const animalCols = isCompactViewport()
    ? [
        {key:"name", label:"Animal", render:r=><div style={{display:"flex",flexDirection:"column",gap:2}}><span style={{fontSize:10,fontWeight:800,color:T.accent,letterSpacing:.6}}>{displayAnimalRecordNumber(r)}</span><span style={{fontWeight:700,color:T.text}}>{r.name}</span><span style={{fontFamily:"monospace",fontSize:11,color:T.accent}}>{r.tag}</span></div>},
        {key:"lotId", label:"Lote", render:r=>{const l=lots.find(x=>x.id===r.lotId);return l?<Badge color={T.yellow}>{l.name}</Badge>:<span style={{color:T.muted}}>—</span>; }},
        {key:"status", label:"Status", render:r=><Badge color={r.status==="Ativo"?T.green:T.muted}>{r.status}</Badge>},
        {key:"edit", label:"Editar", render:r=><Btn variant="ghost" onClick={(e)=>{e.stopPropagation(); setForm({...r,_new:false});}}>Editar</Btn>},
      ]
    : [
        {key:"tag", label:"Brinco", render:r=><span style={{fontFamily:"monospace",color:T.accent}}>{r.tag}</span>},
        {key:"name", label:"Nome", render:r=><div style={{display:"flex",flexDirection:"column",gap:2}}><span style={{fontSize:10,fontWeight:800,color:T.accent,letterSpacing:.6}}>{displayAnimalRecordNumber(r)}</span><span style={{fontWeight:700,color:T.text}}>{r.name}</span></div>},
        {key:"lotId", label:"Lote", render:r=>{const l=lots.find(x=>x.id===r.lotId);return l?<Badge color={T.yellow}>{l.name}</Badge>:<span style={{color:T.muted}}>—</span>; }},
        {key:"costCenter", label:"Centro", render:r=><CCBadge cc={r.costCenter}/>},
        {key:"status", label:"Status", render:r=><Badge color={r.status==="Ativo"?T.green:T.muted}>{r.status}</Badge>},
        {key:"edit", label:"Editar", render:r=><Btn variant="ghost" onClick={(e)=>{e.stopPropagation(); setForm({...r,_new:false});}}>Editar</Btn>},
      ];

  return(
    <div><Toast toast={toast}/>
      {!!deniedCostCenters.length && <div style={{background:`${T.yellow}12`,border:`1px solid ${T.yellow}33`,borderRadius:12,padding:"10px 14px",marginBottom:14,fontSize:12,color:T.text}}>⚠️ Sem acesso a: <strong>{deniedCostCenters.join(", ")}</strong>. Esses centros ficam ocultos em {moduleLabel}.</div>}


      {/* Species tabs / overview */}
      {speciesNav.length>0 ? (
        <>
          <ModuleHeaderMenu
            T={T}
            title="Menu da Pecuária / Avicultura"
            items={animalsMenuItems}
            onBack={handleAnimalsMenuBack}
          />

          {!activeSpecies && (
            <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr 1fr":"repeat(4,minmax(0,1fr))",gap:12,marginBottom:24}}>
              {overviewSpecies.filter(species=>{
                const cardId = species === "Bovino" ? "bovinos" : species === "Ovino" ? "ovinos" : species === "Equino" ? "equinos" : "aves";
                return allowedAnimalRootCards.includes(cardId);
              }).map(species=>{
                const total = animals.filter(a=>animalSpeciesOf(a)===species).length + lots.filter(l=>animalSpeciesOf(l)===species).length;
                const isBovino = species === "Bovino";
                const cardLabel = isBovino ? "Animais em Terminação" : speciesLabelPlural(species);
                const cardValue = isBovino ? terminatingAnimalCount : total;
                const cardSub = isBovino ? `${terminatingBovineLots.length} lote(s)` : "registros";
                return (
                  <div key={species} onClick={()=>{ setSpeciesTab(species); setTab(defaultTabForSpecies(species)); }} style={{cursor:"pointer"}}>
                    <StatCard
                      icon={isBovino ? "🏁" : speciesIcon(species,26)}
                      label={cardLabel}
                      value={cardValue}
                      sub={cardSub}
                      color={species==="Equino"?T.pink:(species==="Ovino"?T.yellow:(species==="Ave"?T.green:(CC_COLORS[species==="Equino"?"Haras":"Pecuária"]||T.accent)))}
                      iconPosition="middleRight"
                      iconWrapStyle={{width:76,height:76,minWidth:76}}
                      iconStyle={{fontSize:44,width:56,height:56}}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <div style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:12,padding:16,color:T.muted,marginBottom:20}}>
          Nenhuma espécie disponível neste módulo. Ative as espécies em <strong>Admin &gt; Fazendas &gt; Editar Fazenda</strong> para começar o cadastro.
        </div>
      )}

      {activeSpecies && isBirdSpecies && !birdTab && (
        <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr 1fr":"repeat(4,minmax(0,1fr))",gap:12,marginBottom:24}}>
          {allowedAvicultureRootCards.includes("aves_1_dia") && <StatCard icon="🐣" label="Aves de 1 Dia" value={(birdLotsByStage.day1||[]).reduce((s,l)=>s+birdCurrentQty(l),0)} color={T.green} onClick={()=>setTab("day1")} iconPosition="middleRight" contentPadRight={84} iconWrapStyle={{width:56,height:56,minWidth:56}} iconStyle={{fontSize:26}} />}
          {allowedAvicultureRootCards.includes("aves_crescimento") && <StatCard icon={<ChickenHeadIcon size={42} />} label="Aves Crescimento" value={(birdLotsByStage.growth||[]).reduce((s,l)=>s+birdCurrentQty(l),0)} color={T.blue||T.accent} onClick={()=>setTab("growth")} iconPosition="middleRight" contentPadRight={84} iconWrapStyle={{width:56,height:56,minWidth:56}} iconStyle={{fontSize:24,width:56,height:56}} />}
          {allowedAvicultureRootCards.includes("aves_corte") && <StatCard icon="🍗" label="Aves Corte" value={(birdLotsByStage.corte||[]).reduce((s,l)=>s+birdCurrentQty(l),0)} color={T.yellow} onClick={()=>setTab("corte")} iconPosition="middleRight" contentPadRight={84} iconWrapStyle={{width:56,height:56,minWidth:56}} iconStyle={{fontSize:24}} />}
          {allowedAvicultureRootCards.includes("aves_postura") && <StatCard icon="🥚" label="Aves Postura" value={(birdLotsByStage.postura||[]).reduce((s,l)=>s+birdCurrentQty(l),0)} sub={`Ovos/dia ${(birdLotsByStage.postura||[]).reduce((s,l)=>s+birdEggAverage(l),0)}`} color={T.accent} onClick={()=>setTab("postura")} iconPosition="middleRight" contentPadRight={84} iconWrapStyle={{width:56,height:56,minWidth:56}} iconStyle={{fontSize:24}} />}
        </div>
      )}
      {activeSpecies && isBirdSpecies && !!birdTab && renderBirdStage()}
      {activeSpecies && isEquineSpecies && equineTab==="potros" && renderFoalsTab()}
      {activeSpecies && isEquineSpecies && equineTab==="service" && renderServiceTab()}

      {/* ── INDIVIDUAL ANIMALS TAB ─────────────────────────────── */}
      {activeSpecies && !isBirdSpecies && !isEquineSpecies && tab==="animals"&&(
        <>
          {(()=>{
            const cards = filterConfiguredCards([
              { id: currentAnimalSection?.cards?.[0], node:<StatCard
                icon={activeSpecies === "Ovino" ? renderCardImageIcon(ovineDorperCardIcon, "Ovinos ativos") : renderCardImageIcon(bovineAdultIcon, "Bovinos ativos")}
                label={speciesLabelPlural(activeSpecies||"Bovino")}
                value={activeSpeciesCount}
                sub="ativos"
                color={activeSpecies==="Equino"?T.pink:(CC_COLORS["Pecuária"]||T.accent)}
                iconPosition="middleRight"
                iconWrapStyle={{width:76,height:76,minWidth:76}}
                iconStyle={{fontSize:44,width:56,height:56}}
              /> },
              { id: currentAnimalSection?.cards?.[1], node:<StatCard
                icon="🔗"
                label="Em lotes"
                value={animalsInLotsCount}
                sub="vinculados"
                color={T.yellow}
                iconPosition="middleRight"
                iconWrapStyle={{width:76,height:76,minWidth:76}}
                iconStyle={{fontSize:44,width:56,height:56}}
              /> },
              { id: currentAnimalSection?.cards?.[2], node:<StatCard
                icon="📋"
                label="Cadastrados"
                value={speciesAnimals.length}
                sub="total"
                color={T.green}
                iconPosition="middleRight"
                iconWrapStyle={{width:76,height:76,minWidth:76}}
                iconStyle={{fontSize:44,width:56,height:56}}
              /> },
            ], allowedCurrentSectionCards, currentAnimalSection?.cards || []);
            return cards.length ? <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr 1fr":"repeat(3,minmax(0,1fr))",gap:12,marginBottom:24}}>{cards.map(item=><React.Fragment key={item.id}>{item.node}</React.Fragment>)}</div> : null;
          })()}
          {/* Individual animal form */}
          <InlineModal theme={T} tone="success" open={!!form} onClose={()=>setForm(null)} title={form?(form._new?"Novo Animal":"Editar Animal"):""}>
            {form&&<div style={{display:"grid",gridTemplateColumns:responsiveTwoColGrid,gap:14}}>
              <Field label="Nome *"><Input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))}/></Field>
              <Field label="Brinco *"><Input value={form.tag} onChange={e=>setForm(f=>({...f,tag:e.target.value}))}/></Field>
              <Field label="Espécie"><Select value={form.species} onChange={e=>setForm(f=>({...f,species:e.target.value}))}>{(visibleSpecies.length?visibleSpecies:(SL.ANIMAL_SPECIES||ANIMAL_SPECIES)).map(s=><option key={s}>{s}</option>)}</Select></Field>
              <Field label="Sexo"><Select value={form.sex} onChange={e=>setForm(f=>({...f,sex:e.target.value}))}>{ANIMAL_SEX.map(s=><option key={s}>{s}</option>)}</Select></Field>
              <Field label="Raça">
                {activeBreeds.length>0?(
                  <Select value={form.breed||""} onChange={e=>setForm(f=>({...f,breed:e.target.value}))}>
                    <option value="">Selecione a raça...</option>
                    {activeBreeds.map(b=><option key={b}>{b}</option>)}
                  </Select>
                ):(
                  <Input value={form.breed||""} onChange={e=>setForm(f=>({...f,breed:e.target.value}))} placeholder="Ex: Nelore, Angus..."/>
                )}
              </Field>
              <Field label="Finalidade">
                <Select value={form.purpose||activePurposes[0]} onChange={e=>setForm(f=>({...f,purpose:e.target.value}))}>
                  {activePurposes.map(p=><option key={p}>{p}</option>)}
                </Select>
              </Field>
              <Field label="Peso da balança (kg)"><Input type="number" value={form.weight} onChange={e=>setForm(f=>({...f,weight:e.target.value}))}/></Field>
              <Field label="Aproveitamento de Carcaça (%)"><Input type="number" min="45" max="60" step="0.1" value={form.carcassYieldPct ?? 50} onChange={e=>setForm(f=>({...f,carcassYieldPct:e.target.value}))}/>{Number(form.weight||0) > 0 && <div style={{fontSize:12,color:T.muted,marginTop:4}}>Peso de carcaça: <strong style={{color:getBovineWeightClass(getCarcassArrobas(Number(form.weight||0), form)).color}}>{fmtMeasure(getCarcassArrobas(Number(form.weight||0), form),2)} @</strong> · {fmtMeasure(getCarcassWeightKg(Number(form.weight||0), form),1)} kg</div>}</Field>
              <Field label="Nascimento real"><Input type="date" value={form.born} onChange={e=>setForm(f=>({...f,born:e.target.value}))} style={dateFieldInputStyle}/><div style={{fontSize:12,color:T.muted,marginTop:4}}>Quando preenchida, a faixa etária usa esta data como prioridade.</div></Field>
              <Field label="Idade aproximada (meses)"><Input type="number" min="0" step="1" value={form.born ? approxMonthsFromDate(form.born) : (form.approxAgeMonths ?? (form.bornApprox ? approxMonthsFromDate(form.bornApprox) : ""))} onChange={e=>syncAnimalApproxMonths(e.target.value)} disabled={!!form.born}/><div style={{fontSize:12,color:T.muted,marginTop:4}}>{form.born ? "Com data real preenchida, os campos aproximados ficam só para conferência." : "Ao preencher os meses, o app calcula a data aproximada automaticamente."}</div></Field>
              <Field label="Nascimento aproximado"><Input type="date" value={form.bornApprox||""} onChange={e=>syncAnimalApproxDate(e.target.value)} disabled={!!form.born} style={dateFieldInputStyle}/><div style={{fontSize:12,color:T.muted,marginTop:4}}>{form.born ? "A data real tem prioridade sobre a aproximada." : "Se você ajustar a data aproximada, o app recalcula os meses automaticamente."}</div></Field>
              <Field label="Microchip / SISBOV">
                <Input value={form.microchip||""} onChange={e=>setForm(f=>({...f,microchip:e.target.value}))}
                  placeholder="Número do microchip ou SISBOV"/>
              </Field>
              <Field label="Centro"><Select value={form.costCenter} onChange={e=>setForm(f=>({...f,costCenter:e.target.value}))}>{(SL.COST_CENTERS||COST_CENTERS).map(c=><option key={c}>{c}</option>)}</Select></Field>
              <Field label="Vincular a Lote">
                <Select value={form.lotId||""} onChange={e=>setForm(f=>({...f,lotId:e.target.value}))}>
                  <option value="">Sem lote</option>
                  {speciesLots.map(l=><option key={l.id} value={l.id}>{l.name} ({l.species})</option>)}
                </Select>
              </Field>
              <Field label="Status"><Select value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>{(SL.ANIMAL_STATUS||ANIMAL_STATUS).map(s=><option key={s}>{s}</option>)}</Select></Field>
              <Field label="Obs." col="1/-1"><Textarea rows={2} value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/></Field>
              <div style={{gridColumn:"1/-1",display:"flex",gap:10,justifyContent:"space-between"}}>
                <div>{!form._new&&canEdit&&<Btn variant="danger" onClick={()=>{setAnimals(p=>p.filter(a=>a.id!==form.id));setForm(null);show("🗑️ Removido.");syncOnSave("animals");}}>Excluir</Btn>}</div>
                <div style={{display:"flex",gap:10}}>
                  <Btn variant="ghost" onClick={()=>setForm(null)}>Cancelar</Btn>
                  {canEdit&&<Btn onClick={saveAnimal}>Salvar</Btn>}
                </div>
              </div>
            </div>}
          </InlineModal>

          {!isAnimalInlineFocused && <>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:10}}>
            <div style={{display:"flex",gap:8}}>
              <Input placeholder="🔍 Número, nome, brinco..." value={search} onChange={e=>setSearch(e.target.value)} style={{width:200}}/>
              <Select value={filterCC} onChange={e=>setFcc(e.target.value)} style={{width:"auto"}}>
                <option value="Todos">Todos</option>
                {(SL.COST_CENTERS||COST_CENTERS).map(c=><option key={c}>{c}</option>)}
              </Select>
            </div>
            {canEdit&&<Btn onClick={()=>setForm({...defForm, species:activeSpecies || defForm.species, costCenter:defaultCostCenterForSpecies(activeSpecies || defForm.species)})}>+ Novo Animal</Btn>}
          </div>
          <Table
            cols={animalCols}
            rows={filteredAnimals}
            onRow={r=>setDetailAnimal(r)}
          />


          <InlineModal theme={T} tone="default" open={!!detailAnimal} onClose={()=>setDetailAnimal(null)} title={detailAnimal?`Animal · ${detailAnimal.name}`:""}>
            {detailAnimal&&<div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14}}><div style={{gridColumn:"1/-1",fontSize:12,fontWeight:800,color:T.accent,letterSpacing:.7}}>{displayAnimalRecordNumber(detailAnimal)}</div>
              <Field label="Brinco"><div style={{color:T.accent,fontFamily:"monospace",fontWeight:700}}>{detailAnimal.tag||"—"}</div></Field>
              <Field label="Status"><Badge color={detailAnimal.status==="Ativo"?T.green:T.muted}>{detailAnimal.status||"—"}</Badge></Field>
              <Field label="Espécie"><div>{detailAnimal.species||"—"}</div></Field>
              <Field label="Raça"><div>{detailAnimal.breed||"—"}</div></Field>
              <Field label="Sexo"><div>{detailAnimal.sex||"—"}</div></Field>
              <Field label="Peso"><div>{detailAnimal.weight ? <span><strong style={{color:getBovineWeightClass(getCarcassArrobas(Number(detailAnimal.weight||0), detailAnimal)).color}}>{fmtMeasure(getCarcassArrobas(Number(detailAnimal.weight||0), detailAnimal),2)} @</strong><span style={{color:T.muted}}> · {fmtMeasure(getCarcassWeightKg(Number(detailAnimal.weight||0), detailAnimal),1)} kg carcaça · balança {fmtMeasure(Number(detailAnimal.weight||0),1)} kg</span></span> : "—"}</div></Field>
              <Field label="Lote"><div>{(lots.find(x=>x.id===detailAnimal.lotId)?.name)||"—"}</div></Field>
              <Field label="Centro"><CCBadge cc={detailAnimal.costCenter}/></Field>
              <Field label="Nascimento real"><div>{detailAnimal.born?fmtDate(detailAnimal.born):"—"}</div></Field>
              <Field label="Nascimento aproximado"><div>{detailAnimal.bornApprox?fmtDate(detailAnimal.bornApprox):"—"}</div></Field>
              <Field label="Microchip"><div style={{fontFamily:"monospace",fontSize:12,color:T.muted}}>{detailAnimal.microchip||"—"}</div></Field>
              {detailAnimal.notes&&<Field label="Observações" col="1/-1"><div style={{whiteSpace:"pre-wrap",color:T.text}}>{detailAnimal.notes}</div></Field>}
              <div style={{gridColumn:"1/-1",display:"flex",justifyContent:"flex-end",gap:10}}>
                <Btn variant="ghost" onClick={()=>setDetailAnimal(null)}>Fechar</Btn>
                {canEdit&&<Btn onClick={()=>{ setForm({...detailAnimal,_new:false}); setDetailAnimal(null); }}>Editar</Btn>}
              </div>
            </div>}
          </InlineModal>

          </>}
        </>
      )}

      {/* ── OPÇÕES TAB ─────────────────────────────────────────── */}

      {/* ── LOTS TAB ───────────────────────────────────────────── */}
      {activeSpecies === "Ovino" && tab==="lots" && (()=>{
        const ovinoLots = speciesLots.filter(l=>animalSpeciesOf(l)==="Ovino");
        const ovinoRangeAnimals = ovineAgeRanges.map(range=>({
          range,
          count: ovinoLots.filter(l=>getEffectiveLotAgeRangeId(l)===range.id).reduce((s,l)=>s+Number(l?.quantity||0),0),
        }));
        const rationScDay = ovinoLots.reduce((s,l)=>s + ((Number(l?.dailyFeedKg||0) * Number(l?.quantity||0))/60), 0);
        const termination = ovinoLots.filter(l=>!!l?.terminationLot).reduce((s,l)=>s+Number(l?.quantity||0),0);
        const cards = filterConfiguredCards([
          { id: currentAnimalSection?.cards?.[0], node:<StatCard icon={ovineAgeRanges[0]?.icon || "🐑"} label={ovineAgeRanges[0]?.label || "Faixa 1"} value={ovinoRangeAnimals[0]?.count || 0} color={T.yellow} iconPosition="middleRight" iconWrapStyle={{width:76,height:76,minWidth:76}} iconStyle={{fontSize:44,width:56,height:56}}/> },
          { id: currentAnimalSection?.cards?.[1], node:<StatCard icon={renderCardImageIcon(genericRationCardIcon, "Consumo méd. ração ovinos")} label="Consumo méd. ração" value={`${fmtMeasure(rationScDay,2)} sc/dia`} color={T.green} iconPosition="middleRight" iconWrapStyle={{width:76,height:76,minWidth:76}} iconStyle={{fontSize:44,width:56,height:56}}/> },
          { id: currentAnimalSection?.cards?.[2], node:<StatCard icon={ovineAgeRanges[1]?.icon || "🐏"} label={ovineAgeRanges[1]?.label || "Faixa 2"} value={ovinoRangeAnimals[1]?.count || 0} color={T.accent} iconPosition="middleRight" iconWrapStyle={{width:76,height:76,minWidth:76}} iconStyle={{fontSize:44,width:56,height:56}}/> },
          { id: currentAnimalSection?.cards?.[3], node:<StatCard icon={ovineAgeRanges[2]?.icon || "🏁"} label={ovineAgeRanges[2]?.label || "Faixa 3"} value={ovinoRangeAnimals[2]?.count || termination} sub={`Terminação ${termination}`} color={T.red} iconPosition="middleRight" iconWrapStyle={{width:76,height:76,minWidth:76}} iconStyle={{fontSize:44,width:56,height:56}}/> },
        ], allowedCurrentSectionCards, currentAnimalSection?.cards || []);
        return cards.length ? <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr 1fr":"repeat(4,minmax(0,1fr))",gap:12,marginBottom:24}}>{cards.map(item=><React.Fragment key={item.id}>{item.node}</React.Fragment>)}</div> : null;
      })()}
      {activeSpecies && !isBirdSpecies && !isEquineSpecies && tab==="lots"&&(
        <>
          {/* Summary stats */}
          {(()=>{
            const bovineCards = bovineAgeRanges.map((range, index)=>{
              const countLots = bovineLots.filter(l=>getEffectiveLotAgeRangeId(l)===range.id).length;
              const countAnimals = bovineLots.filter(l=>getEffectiveLotAgeRangeId(l)===range.id).reduce((sum,l)=>sum+Number(l.quantity||0),0);
              return { id: currentAnimalSection?.cards?.[index], node:<StatCard key={range.id} icon={range.icon} label={range.label} value={countLots} sub={`${countAnimals} animais`} color={T.yellow} iconPosition="middleRight" iconWrapStyle={{width:76,height:76,minWidth:76}} iconStyle={{fontSize:44,width:56,height:56}}/> };
            });
            const genericCards = activeSpecies === "Ovino" ? [] : [
              { id: currentAnimalSection?.cards?.[0], node:<StatCard icon="📦" label="Lotes" value={speciesLots.length} color={T.yellow} iconPosition="middleRight" iconWrapStyle={{width:76,height:76,minWidth:76}} iconStyle={{fontSize:44}}/> },
              { id: currentAnimalSection?.cards?.[1], node:<StatCard icon={<CattleIcon size={24} />} label="Animais em Lotes" value={totalLotAnimals} color={T.accent} iconPosition="middleRight" iconWrapStyle={{width:76,height:76,minWidth:76}} iconStyle={{fontSize:44,width:56,height:56}}/> },
              { id: currentAnimalSection?.cards?.[2], node:<StatCard icon="🧬" label="Estruturados" value={Number(registrySummary?.animals?.total || 0)} sub={`${Number(registrySummary?.individualizations?.total || 0)} individualizações`} color={T.green} iconPosition="middleRight" iconWrapStyle={{width:76,height:76,minWidth:76}} iconStyle={{fontSize:44}}/> },
              { id: currentAnimalSection?.cards?.[2], node:<StatCard icon="⚖️" label="Peso Total (kg)" value={fmtMoney(speciesLots.reduce((s,l)=>s+(l.totalWeight||0),0))} color={T.green} iconPosition="middleRight" iconWrapStyle={{width:76,height:76,minWidth:76}} iconStyle={{fontSize:44}}/> },
            ];
            const cards = filterConfiguredCards(activeSpecies==="Bovino" ? bovineCards : genericCards, allowedCurrentSectionCards, currentAnimalSection?.cards || []);
            return cards.length ? <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr 1fr":"repeat(4,minmax(0,1fr))",gap:10,marginBottom:20}}>{cards.map(item=><React.Fragment key={item.id}>{item.node}</React.Fragment>)}</div> : null;
          })()}

          {/* Lot Form Modal */}
          <InlineModal theme={T} tone="success" open={!!lotForm} onClose={closeAllLivestockInlinePanels} title={lotForm?(lotForm._new?"Novo Lote de Animais":"Editar Lote"):""} wide>
            {lotForm&&<div style={{display:"grid",gridTemplateColumns:responsiveTwoColGrid,gap:14}}>{!lotForm._new && <div style={{gridColumn:"1/-1",fontSize:12,fontWeight:800,color:T.accent,letterSpacing:.7}}>{displayAnimalRecordNumber(lotForm)}</div>}

              <Field label="Nome do Lote *" col="1/-1">
                <Input value={lotForm.name} placeholder="Ex: Lote Boi Gordo Curral Norte" onChange={e=>setLotForm(f=>({...f,name:e.target.value}))}/>
              </Field>

              <Field label="Faixa etária calculada" col="1/-1">
                <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                  {(() => { const ageInfo = getEffectiveLotAgeRange(lotForm); return ageInfo ? <Badge color={T.accent}>{ageInfo.icon || "🐾"} {ageInfo.label}</Badge> : <Badge color={T.muted}>Faixa não informada</Badge>; })()}
                  <Badge color={T.yellow}>🗓️ {fmtMeasure(getLotCurrentAgeMonths(lotForm),0)} mês(es)</Badge>
                </div>
                <div style={{fontSize:12,color:T.muted,marginTop:6}}>A faixa etária é calculada automaticamente pelo app com base na data aproximada informada para o lote.</div>
              </Field>

              {normalizeSpeciesValue(lotForm?.species||activeSpecies) !== "Bovino" && <Field label="Espécie *">
                <Select value={lotForm.species} onChange={e=>setLotForm(f=>({...f,species:e.target.value}))}>
                  {(visibleSpecies.length?visibleSpecies:(SL.ANIMAL_SPECIES||ANIMAL_SPECIES)).map(s=><option key={s}>{s}</option>)}
                </Select>
              </Field>}
              <Field label="Raça">
                {activeBreeds.length>0?(
                  <Select value={lotForm.breed||""} onChange={e=>setLotForm(f=>({...f,breed:e.target.value}))}>
                    <option value="">Selecione a raça...</option>
                    {activeBreeds.map(b=><option key={b}>{b}</option>)}
                  </Select>
                ):(
                  <Input value={lotForm.breed||""} placeholder="Ex: Nelore, Angus..." onChange={e=>setLotForm(f=>({...f,breed:e.target.value}))}/>
                )}
              </Field>

              <Field label="Sexo">
                <Select value={lotForm.sex||"Misto"} onChange={e=>setLotForm(f=>({...f,sex:e.target.value}))}>
                  {["Macho","Fêmea","Misto"].map(s=><option key={s}>{s}</option>)}
                </Select>
              </Field>
              <Field label="Finalidade">
                <Select value={lotForm.purpose||activePurposes[0]||LOT_PURPOSES[0]} onChange={e=>setLotForm(f=>({...f,purpose:e.target.value}))}>
                  {activePurposes.map(p=><option key={p}>{p}</option>)}
                </Select>
              </Field>
              <Field label="Tipo do lote">
                <Select value={lotForm.lotType||"collective"} onChange={e=>setLotForm(f=>({...f, lotType:e.target.value, selectedAnimalIds:e.target.value==='tracked' ? (Array.isArray(f.selectedAnimalIds)?f.selectedAnimalIds:[]) : []}))}>
                  <option value="collective">Lote coletivo</option>
                  <option value="tracked">Lote rastreado</option>
                </Select>
              </Field>

              <Field label="Quantidade de Animais *">
                <Input type="number" min="1" disabled={String(lotForm.lotType||'collective')==='tracked'} value={lotForm.quantity||""} onChange={e=>setLotForm(f=>({...f,quantity:Number(e.target.value)}))}/>
                {String(lotForm.lotType||'collective')==='tracked' && <div style={{fontSize:12,color:T.muted,marginTop:6}}>Em lote rastreado, a quantidade é calculada automaticamente pelos animais selecionados.</div>}
              </Field>
              {String(lotForm.lotType||'collective')==='tracked' && (
                <Field label="Animais vinculados" col="1/-1">
                  <div style={{display:'grid',gap:8,maxHeight:220,overflow:'auto',padding:10,border:`1px solid ${T.border}`,borderRadius:12,background:T.card}}>
                    {!availableTrackedAnimalsForLot.length && <div style={{color:T.muted,fontSize:13}}>Nenhum animal individual disponível para este lote rastreado.</div>}
                    {availableTrackedAnimalsForLot.map(animal=>{
                      const checked = Array.isArray(lotForm.selectedAnimalIds) && lotForm.selectedAnimalIds.includes(String(animal.id));
                      return <label key={animal.id} style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer'}}>
                        <input type="checkbox" checked={checked} onChange={e=>setLotForm(f=>{
                          const prev = Array.isArray(f.selectedAnimalIds)?f.selectedAnimalIds.map(v=>String(v||'')):[];
                          const next = e.target.checked ? Array.from(new Set([...prev, String(animal.id)])) : prev.filter(id=>id!==String(animal.id));
                          const linkedAnimals = availableTrackedAnimalsForLot.filter(item=>next.includes(String(item.id)));
                          const avgWeight = linkedAnimals.length ? Number((linkedAnimals.reduce((sum,item)=>sum+Number(item?.weight||0),0)/linkedAnimals.length).toFixed(2)) : 0;
                          return { ...f, selectedAnimalIds: next, quantity: next.length, avgWeight };
                        })} />
                        <span style={{fontWeight:700,color:T.text}}>{animal.name||animal.tag||'Animal'}</span>
                        <span style={{fontSize:12,color:T.muted}}>Brinco {animal.tag||'—'} · {animal.microchip ? `Microchip ${animal.microchip}` : 'sem microchip'} · {animal.weight ? `${animal.weight} kg` : 'sem peso'}</span>
                      </label>;
                    })}
                  </div>
                </Field>
              )}
              <Field label="Idade média aproximada (meses)">
                <Input type="number" min="0" step="1" value={lotForm.avgAgeMonths ?? (lotForm.birthDateApprox ? approxMonthsFromDate(lotForm.birthDateApprox) : "")} onChange={e=>syncLotApproxMonths(e.target.value)}/>
                <div style={{fontSize:12,color:T.muted,marginTop:4}}>Ao preencher os meses, o app calcula a data aproximada de nascimento do lote automaticamente.</div>
              </Field>
              <Field label="Data aproximada de nascimento do lote">
                <Input type="date" value={lotForm.birthDateApprox||""} onChange={e=>syncLotApproxDate(e.target.value)} style={dateFieldInputStyle}/>
                <div style={{fontSize:12,color:T.muted,marginTop:4}}>Se você ajustar a data aproximada, o app recalcula a idade média em meses automaticamente.</div>
              </Field>
              <Field label="Data de Formação">
                <Input type="date" value={lotForm.formedAt||today()} onChange={e=>setLotForm(f=>({...f,formedAt:e.target.value}))} style={dateFieldInputStyle}/>
              </Field>

              {/* Weight mode */}
              <Field label="Modo de Pesagem" col="1/-1">
                <div style={{display:"flex",gap:8}}>
                  {WEIGHT_MODES.map(m=>(
                    <div key={m} onClick={()=>setLotForm(f=>({...f,weightMode:m}))}
                      style={{flex:1,textAlign:"center",padding:"9px 12px",borderRadius:8,cursor:"pointer",
                        border:`2px solid ${lotForm.weightMode===m?T.green:T.border}`,
                        background:lotForm.weightMode===m?`${T.green}18`:T.card2,
                        color:lotForm.weightMode===m?T.green:T.muted,fontWeight:600,fontSize:12,transition:"all .15s"}}>
                      {m==="Peso Médio do Lote"?"⚖️ "+m:"📋 "+m}
                    </div>
                  ))}
                </div>
              </Field>

              {lotForm.weightMode==="Peso Médio do Lote"&&(
                <>
                  <Field label="Peso Médio da Balança por Animal (kg)" col="1/-1">
                    <Input type="number" min="0" step="0.1" value={lotForm.avgWeight||0}
                      onChange={e=>setLotForm(f=>({...f,avgWeight:Number(e.target.value)}))}/>
                    {(lotForm.avgWeight>0&&lotForm.quantity>0)&&(
                      <div style={{fontSize:12,color:T.green,marginTop:4}}>
                        ⚖️ Peso total estimado na balança: <strong>{fmtMoney(lotForm.avgWeight*lotForm.quantity)} kg</strong>
                      </div>
                    )}
                  </Field>
                  <Field label="Aproveitamento de Carcaça (%)" col="1/-1">
                    <Input type="number" min="45" max="60" step="0.1" value={lotForm.carcassYieldPct ?? 50}
                      onChange={e=>setLotForm(f=>({...f,carcassYieldPct:Number(e.target.value)}))}/>
                    {Number(lotForm.avgWeight||0) > 0 && <div style={{fontSize:12,color:T.muted,marginTop:4}}>Peso médio de carcaça: <strong style={{color:getBovineWeightClass(getCarcassArrobas(Number(lotForm.avgWeight||0), lotForm)).color}}>{fmtMeasure(getCarcassWeightKg(Number(lotForm.avgWeight||0), lotForm),1)} kg · {fmtMeasure(getCarcassArrobas(Number(lotForm.avgWeight||0), lotForm),2)} @</strong></div>}
                  </Field>
                </>
              )}

              <Field label="Localização (Pasto/Curral)">
                <Select value={lotForm.pastureId||""} onChange={e=>setLotForm(f=>({...f,pastureId:e.target.value}))}>
                  <option value="">Sem localização definida</option>
                  {pastures.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                </Select>
              </Field>
              <Field label="Centro de Custo">
                <Select value={lotForm.costCenter} onChange={e=>setLotForm(f=>({...f,costCenter:e.target.value}))}>
                  {(SL.COST_CENTERS||COST_CENTERS).map(c=><option key={c}>{c}</option>)}
                </Select>
              </Field>

              <Field label="Observações" col="1/-1">
                <Textarea rows={2} value={lotForm.notes||""} placeholder="Informações adicionais sobre o lote..." onChange={e=>setLotForm(f=>({...f,notes:e.target.value}))}/>
              </Field>

              {/* ── Alimentação ── */}
              {(() => {
                const plannedPerHead = plannedLotNutritionPerHeadKg(lotForm);
                const lotQty = Number(lotForm.quantity || 0);
                const plannedDailyLotKg = plannedPerHead * lotQty;
                const nutritionAudit = buildBovineNutritionAudit(lotForm, lotNutritionAuditRange);
                return <div ref={lotNutritionSectionRef} style={{gridColumn:"1/-1",borderTop:`1px solid ${T.border}`,paddingTop:14,marginTop:4}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:12,flexWrap:"wrap"}}>
                  <div style={{fontWeight:700,color:T.green,fontSize:13,display:"flex",alignItems:"center",gap:8}}>
                    <span style={{display:"inline-flex",alignItems:"center",gap:8}}><FeedBagIcon size={19} /> Alimentação e Nutrição</span>
                  </div>
                  {String(lotForm.id||"") && <Btn size="sm" variant="ghost" onClick={()=>openLotNutritionLauncherFromLot(lotForm)}>Lançar consumo / baixa do estoque</Btn>}
                </div>
                <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:12}}>
                  <Field label="Sistema de Alimentação">
                    <Select value={lotForm.feedType||FEED_TYPES[0]} onChange={e=>setLotForm(f=>({...f,feedType:e.target.value}))}>
                      {FEED_TYPES.map(t=><option key={t}>{t}</option>)}
                    </Select>
                  </Field>
                  <div style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:12,padding:12,display:"grid",gap:6,fontSize:12}}>
                    <div style={{fontWeight:700,color:T.text}}>Painel automático de consumo do lote</div>
                    <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr 1fr":"repeat(4,minmax(0,1fr))",gap:8}}>
                      <div style={{background:T.card,borderRadius:10,padding:10}}><div style={{fontSize:11,color:T.muted}}>Por cabeça / dia</div><div style={{fontWeight:800,color:T.text}}>{fmtMeasure(plannedPerHead)} kg</div></div>
                      <div style={{background:T.card,borderRadius:10,padding:10}}><div style={{fontSize:11,color:T.muted}}>Lote / dia</div><div style={{fontWeight:800,color:T.text}}>{fmtMeasure(plannedDailyLotKg)} kg</div></div>
                      <div style={{background:T.card,borderRadius:10,padding:10}}><div style={{fontSize:11,color:T.muted}}>Lote / semana</div><div style={{fontWeight:800,color:T.text}}>{fmtMeasure(plannedDailyLotKg*7)} kg</div></div>
                      <div style={{background:T.card,borderRadius:10,padding:10}}><div style={{fontSize:11,color:T.muted}}>Lote / mês</div><div style={{fontWeight:800,color:T.text}}>{fmtMeasure(plannedDailyLotKg*30)} kg</div></div>
                    </div>
                    <div style={{fontSize:11,color:T.muted}}>Nesta tela, <strong>Ração Planejada</strong> e <strong>Suplementos Planejados</strong> são informados em <strong>kg por cabeça/dia</strong>. O consumo diário consolidado do lote é calculado automaticamente a partir deles.</div>
                    <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr 1fr":"repeat(4,minmax(0,1fr))",gap:8}}>
                      <div style={{background:T.card,borderRadius:10,padding:10}}><div style={{fontSize:11,color:T.muted}}>Ração / dia</div><div style={{fontWeight:800,color:T.text}}>{fmtMeasure((lotForm.rationPlans||[]).reduce((sum, entry)=>sum + roundStockQtyByPack((bovineNutritionStockItems||[]).find(stock=>String(stock.id)===String(entry?.stockId||"")), Number(entry?.dailyKg||0) * lotQty), 0))} saco(s)</div></div>
                      <div style={{background:T.card,borderRadius:10,padding:10}}><div style={{fontSize:11,color:T.muted}}>Suplemento / dia</div><div style={{fontWeight:800,color:T.text}}>{fmtMeasure((lotForm.supplements||[]).reduce((sum, entry)=>sum + roundStockQtyByPack((bovineNutritionStockItems||[]).find(stock=>String(stock.id)===String(entry?.stockId||"")), Number(entry?.dailyKg||0) * lotQty), 0))} saco(s)</div></div>
                      <div style={{background:T.card,borderRadius:10,padding:10}}><div style={{fontSize:11,color:T.muted}}>Ração / semana</div><div style={{fontWeight:800,color:T.text}}>{fmtMeasure((lotForm.rationPlans||[]).reduce((sum, entry)=>sum + roundStockQtyByPack((bovineNutritionStockItems||[]).find(stock=>String(stock.id)===String(entry?.stockId||"")), Number(entry?.dailyKg||0) * lotQty), 0) * 7)} saco(s)</div></div>
                      <div style={{background:T.card,borderRadius:10,padding:10}}><div style={{fontSize:11,color:T.muted}}>Suplemento / semana</div><div style={{fontWeight:800,color:T.text}}>{fmtMeasure((lotForm.supplements||[]).reduce((sum, entry)=>sum + roundStockQtyByPack((bovineNutritionStockItems||[]).find(stock=>String(stock.id)===String(entry?.stockId||"")), Number(entry?.dailyKg||0) * lotQty), 0) * 7)} saco(s)</div></div>
                    </div>
                  </div>

                  <div style={{gridColumn:"1/-1"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                      <div style={{fontSize:12,fontWeight:700,color:T.text}}>🌾 Ração planejada</div>
                      <Btn size="sm" variant="ghost" onClick={()=>setLotForm(f=>({...f,rationPlans:[...(f.rationPlans||[]),{stockId:"",type:"Ração",dailyKg:0,notes:""}]}))}>+ Adicionar</Btn>
                    </div>
                    {(lotForm.rationPlans||[]).length===0&&<div style={{fontSize:12,color:T.muted,fontStyle:"italic"}}>Nenhuma ração planejada cadastrada</div>}
                    {(lotForm.rationPlans||[]).map((sup,i)=>{
                      const item = (bovineNutritionStockItems||[]).find(stock=>String(stock.id)===String(sup.stockId||""));
                      const lotKgDay = Number(sup.dailyKg||0) * lotQty;
                      const packKg = getItemPackKg(item);
                      const qtyUnits = roundStockQtyByPack(item, lotKgDay);
                      return <div key={i} style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 150px 32px",gap:8,marginBottom:10,alignItems:"start"}}>
                        <Select value={sup.stockId||""} onChange={e=>{const s=[...(lotForm.rationPlans||[])]; const item=(bovineNutritionStockItems||[]).find(stock=>String(stock.id)===String(e.target.value)); s[i]={...s[i],stockId:e.target.value,type:item?.name||"Ração"}; setLotForm(f=>({...f,rationPlans:s}));}}>
                          <option value="">Selecione uma ração do estoque</option>
                          {bovineNutritionStockItems.filter(item=>normalizeStockKey(item?.category).includes("racao")).map(item=><option key={item.id} value={item.id}>{item.name} · saldo {item.quantity}</option>)}
                        </Select>
                        <div style={{position:"relative"}}>
                          <Input type="number" min="0" step="0.001" value={sup.dailyKg||0} onChange={e=>{const s=[...(lotForm.rationPlans||[])];s[i]={...s[i],dailyKg:Number(e.target.value)};setLotForm(f=>({...f,rationPlans:s}));}} style={{paddingRight:28}}/>
                          <span style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",fontSize:11,color:T.muted}}>kg</span>
                        </div>
                        <button onClick={()=>setLotForm(f=>({...f,rationPlans:(f.rationPlans||[]).filter((_,j)=>j!==i)}))} style={{background:"none",border:`1px solid ${T.red}`,borderRadius:6,color:T.red,cursor:"pointer",width:32,height:36,fontSize:16}}>×</button>
                        <div style={{gridColumn:"1/-1",display:"grid",gap:4}}>
                          <div style={{fontSize:11,color:T.muted}}>Consumo por cabeça/dia. Total do lote: <strong>{fmtMeasure(lotKgDay)} kg/dia</strong>{packKg>0?<> · baixa sugerida: <strong>{fmtMeasure(qtyUnits)} {item?.unit||"un"}/dia</strong></>:null}</div>
                          <Input placeholder="Observações..." value={sup.notes||""} onChange={e=>{const s=[...(lotForm.rationPlans||[])];s[i]={...s[i],notes:e.target.value};setLotForm(f=>({...f,rationPlans:s}));}}/>
                        </div>
                      </div>;
                    })}
                  </div>

                  <div style={{gridColumn:"1/-1"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                      <div style={{fontSize:12,fontWeight:700,color:T.text}}>💊 Suplementos planejados</div>
                      <Btn size="sm" variant="ghost" onClick={()=>setLotForm(f=>({...f,supplements:[...(f.supplements||[]),{stockId:"",type:SUPPLEMENT_TYPES[0],dailyKg:0,notes:""}]}))}>+ Adicionar</Btn>
                    </div>
                    {(lotForm.supplements||[]).length===0&&(
                      <div style={{fontSize:12,color:T.muted,fontStyle:"italic"}}>Nenhum suplemento cadastrado</div>
                    )}
                    {(lotForm.supplements||[]).map((sup,i)=>{
                      const item = (bovineNutritionStockItems||[]).find(stock=>String(stock.id)===String(sup.stockId||""));
                      const lotKgDay = Number(sup.dailyKg||0) * lotQty;
                      const packKg = getItemPackKg(item);
                      const qtyUnits = roundStockQtyByPack(item, lotKgDay);
                      return <div key={i} style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 150px 32px",gap:8,marginBottom:10,alignItems:"start"}}>
                        <Select value={sup.stockId||""} onChange={e=>{const s=[...lotForm.supplements]; const item=(bovineNutritionStockItems||[]).find(stock=>String(stock.id)===String(e.target.value)); s[i]={...s[i],stockId:e.target.value,type:item?.name||sup.type||SUPPLEMENT_TYPES[0]}; setLotForm(f=>({...f,supplements:s}));}}>
                          <option value="">Selecione um suplemento do estoque</option>
                          {bovineNutritionStockItems.filter(item=>normalizeStockKey(item?.category).includes("suplement")).map(item=><option key={item.id} value={item.id}>{item.name} · saldo {item.quantity}</option>)}
                        </Select>
                        <div style={{position:"relative"}}>
                          <Input type="number" min="0" step="0.001" value={sup.dailyKg||0} onChange={e=>{const s=[...lotForm.supplements];s[i]={...s[i],dailyKg:Number(e.target.value)};setLotForm(f=>({...f,supplements:s}));}} style={{paddingRight:28}}/>
                          <span style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",fontSize:11,color:T.muted}}>kg</span>
                        </div>
                        <button onClick={()=>setLotForm(f=>({...f,supplements:f.supplements.filter((_,j)=>j!==i)}))} style={{background:"none",border:`1px solid ${T.red}`,borderRadius:6,color:T.red,cursor:"pointer",width:32,height:36,fontSize:16}}>×</button>
                        <div style={{gridColumn:"1/-1",display:"grid",gap:4}}>
                          <div style={{fontSize:11,color:T.muted}}>Consumo por cabeça/dia. Total do lote: <strong>{fmtMeasure(lotKgDay)} kg/dia</strong>{packKg>0?<> · baixa sugerida: <strong>{fmtMeasure(qtyUnits)} {item?.unit||"un"}/dia</strong></>:null}</div>
                          <Input placeholder="Observações..." value={sup.notes||""} onChange={e=>{const s=[...lotForm.supplements];s[i]={...s[i],notes:e.target.value};setLotForm(f=>({...f,supplements:s}));}}/>
                        </div>
                      </div>;
                    })}
                  </div>

                  <Field label="Consumo diário de ração / concentrado / suplemento (kg por cabeça)">
                    <div style={{display:"grid",gap:6}}>
                      <Input type="number" min="0" step="0.001" value={plannedPerHead||0} readOnly />
                      <div style={{fontSize:11,color:T.muted}}>Esse valor vem da soma de <strong>Ração Planejada</strong> + <strong>Suplementos Planejados</strong>.</div>
                      {plannedPerHead>0&&lotQty>0&&(
                        <div style={{fontSize:11,color:T.green}}>Total lote/dia: <strong>{fmtMeasure(plannedDailyLotKg)} kg</strong></div>
                      )}
                    </div>
                  </Field>

                  <Field label="Observações sobre alimentação" col="1/-1">
                    <Textarea rows={2} value={lotForm.feedNotes||""} placeholder="Rotina de alimentação, horários, restrições..." onChange={e=>setLotForm(f=>({...f,feedNotes:e.target.value}))}/>
                  </Field>

                  {String(lotForm.id||"") && <div style={{gridColumn:"1/-1",background:T.card2,border:`1px solid ${T.border}`,borderRadius:12,padding:12,display:"grid",gap:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                      <div style={{fontWeight:700,color:T.text}}>Consumo realizado e ganho de peso</div>
                      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                        <Input type="date" value={lotNutritionAuditRange.start||""} onChange={e=>setLotNutritionAuditRange(prev=>({...prev,start:e.target.value}))} style={dateFieldInputStyle}/>
                        <Input type="date" value={lotNutritionAuditRange.end||""} onChange={e=>setLotNutritionAuditRange(prev=>({...prev,end:e.target.value}))} style={dateFieldInputStyle}/>
                      </div>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr 1fr":"repeat(5,minmax(0,1fr))",gap:8}}>
                      <div style={{background:T.card,borderRadius:10,padding:10}}><div style={{fontSize:11,color:T.muted}}>Ração no período</div><div style={{fontWeight:800,color:T.text}}>{fmtMeasure(nutritionAudit.summary.rationSacks)} saco(s)</div></div>
                      <div style={{background:T.card,borderRadius:10,padding:10}}><div style={{fontSize:11,color:T.muted}}>Suplemento no período</div><div style={{fontWeight:800,color:T.text}}>{fmtMeasure(nutritionAudit.summary.supplementSacks)} saco(s)</div></div>
                      <div style={{background:T.card,borderRadius:10,padding:10}}><div style={{fontSize:11,color:T.muted}}>Ração em kg</div><div style={{fontWeight:800,color:T.text}}>{fmtMeasure(nutritionAudit.summary.rationKg)} kg</div></div>
                      <div style={{background:T.card,borderRadius:10,padding:10}}><div style={{fontSize:11,color:T.muted}}>Suplemento em kg</div><div style={{fontWeight:800,color:T.text}}>{fmtMeasure(nutritionAudit.summary.supplementKg)} kg</div></div>
                      <div style={{background:T.card,borderRadius:10,padding:10}}><div style={{fontSize:11,color:T.muted}}>Consumo médio planejado</div><div style={{fontWeight:800,color:T.text}}>{fmtMeasure(nutritionAudit.summary.avgPerHeadKgDay)} kg/cab/dia</div></div>
                    </div>
                    {nutritionAudit.gainWindows.length===0 ? <div style={{fontSize:12,color:T.muted}}>Faça a pesagem inicial e novas pesagens do lote para o app calcular o ganho de peso diário e relacionar com ração e suplemento no intervalo.</div> : <div style={{display:"grid",gap:8}}>
                      {nutritionAudit.gainWindows.map((window, idx)=><div key={idx} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:10,padding:10,display:"grid",gap:6,fontSize:12}}>
                        <div style={{fontWeight:700,color:T.text}}>{fmtDate(window.from)} → {fmtDate(window.to)}</div>
                        <div style={{color:T.muted}}>Peso médio por cabeça: <strong style={{color:getBovineWeightClass((window.fromCarcassWeight||0)/15).color}}>{fmtMeasure((window.fromCarcassWeight||0)/15,2)} @</strong> → <strong style={{color:getBovineWeightClass((window.toCarcassWeight||0)/15).color}}>{fmtMeasure((window.toCarcassWeight||0)/15,2)} @</strong> · ganho diário: <strong>{fmtMeasure(window.gainPerHeadDay/15,3)} @/cab/dia</strong></div>
                        <div style={{fontSize:11,color:T.muted}}>Balança: {fmtMeasure(window.fromWeight,1)} kg ({fmtMeasure(window.fromCarcassYieldPct,1)}%) → {fmtMeasure(window.toWeight,1)} kg ({fmtMeasure(window.toCarcassYieldPct,1)}%)</div>
                        <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr 1fr":"repeat(4,minmax(0,1fr))",gap:8}}>
                          <div style={{background:T.card2,borderRadius:8,padding:8}}><div style={{fontSize:11,color:T.muted}}>Ração</div><div style={{fontWeight:800,color:T.text}}>{fmtMeasure(window.nutrition.rationSacks)} saco(s)</div></div>
                          <div style={{background:T.card2,borderRadius:8,padding:8}}><div style={{fontSize:11,color:T.muted}}>Suplemento</div><div style={{fontWeight:800,color:T.text}}>{fmtMeasure(window.nutrition.supplementSacks)} saco(s)</div></div>
                          <div style={{background:T.card2,borderRadius:8,padding:8}}><div style={{fontSize:11,color:T.muted}}>Consumo médio planejado</div><div style={{fontWeight:800,color:T.text}}>{fmtMeasure(window.nutrition.avgPerHeadKgDay)} kg/cab/dia</div></div>
                          <div style={{background:T.card2,borderRadius:8,padding:8}}><div style={{fontSize:11,color:T.muted}}>Duração</div><div style={{fontWeight:800,color:T.text}}>{window.days} dia(s)</div></div>
                        </div>
                      </div>)}
                    </div>}
                  </div>}
                </div>
              </div>;
              })()}

              <div style={{gridColumn:"1/-1",display:"flex",gap:10,justifyContent:"space-between",paddingTop:8,borderTop:`1px solid ${T.border}`}}>
                <div>{!lotForm._new&&canEdit&&(
                  <Btn variant="danger" onClick={()=>{setLots(p=>p.filter(l=>l.id!==lotForm.id));setLotForm(null);show("🗑️ Lote excluído.");syncOnSave("animalLots");}}>🗑️ Excluir</Btn>
                )}</div>
                <div style={{display:"flex",gap:10}}>
                  <Btn variant="ghost" onClick={()=>setLotForm(null)}>Cancelar</Btn>
                  <Btn onClick={saveLot}>Salvar Lote</Btn>
                </div>
              </div>
            </div>}
          </InlineModal>


          {!isLotInlineFocused && <>
          {canEdit&&(
            <div style={{display:"flex",justifyContent:"flex-end",marginBottom:16}}>
              <Btn onClick={()=>setLotForm({...defLot, species:activeSpecies || defLot.species, costCenter:defaultCostCenterForSpecies(activeSpecies || defLot.species)})}>+ Novo Lote</Btn>
            </div>
          )}

          {/* Lots cards grid */}
          {speciesLots.length===0&&(
            <div style={{color:T.muted,textAlign:"center",padding:48}}>Nenhum lote cadastrado. Clique em "+ Novo Lote" para começar.</div>
          )}
          <div style={{display:"grid",gridTemplateColumns:(currentLivestockInlineLotId && ["Bovino","Ovino"].includes(String(activeSpecies||"")))?"1fr":"repeat(auto-fill,minmax(280px,1fr))",gap:14}}>
            {((currentLivestockInlineLotId && ["Bovino","Ovino"].includes(String(activeSpecies||""))) ? filteredLots.filter(item=>String(item.id)===String(currentLivestockInlineLotId)) : filteredLots).map(lot=>{
              const ageRange = getEffectiveLotAgeRange(lot);
              const indivCount = animals.filter(a=>a.lotId===lot.id).length;
              const currentAgeMonths = getLotCurrentAgeMonths(lot);
              const lotReadyForSlaughter = isLotReadyForSlaughter(lot);
              return(
                <div key={lot.id}
                  onClick={()=>openLivestockLotDetail(lot)}
                  style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,overflow:"hidden",cursor:"pointer",transition:"all .2s"}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=T.yellow;e.currentTarget.style.transform="translateY(-2px)";}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.transform="translateY(0)";}}>

                  {/* Card header */}
                  <div style={{background:`${T.yellow}18`,borderBottom:`1px solid ${T.yellow}33`,padding:"12px 16px",display:"flex",alignItems:"center",gap:10}}>
                    <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",minHeight:28}}>{ageRange?.icon || (animalSpeciesOf(lot)==="Ovino" ? <SheepHeadIcon size={30} /> : <BovineHeadIcon size={30} />)}</span>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,color:T.text,fontSize:15,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{lot.name}</div>
                      <div style={{fontSize:12,color:T.muted}}>{lot.species} · {lot.breed||"Sem raça"}</div>
                    </div>
                    {canEdit&&(
                      <Btn size="sm" variant={currentLivestockInlineLotId===String(lot.id) && !!lotForm ? "primary" : "ghost"} onClick={e=>{e.stopPropagation();openLotEditInline(lot);}}>✏️</Btn>
                    )}
                  </div>

                  {/* Card body */}
                  <div style={{padding:"12px 16px",display:"flex",flexDirection:"column",gap:8}}>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                      <div style={{background:T.card2,borderRadius:8,padding:"8px 10px",textAlign:"center"}}>
                        <div style={{fontSize:20,fontWeight:800,color:T.yellow}}>{lot.quantity||0}</div>
                        <div style={{fontSize:10,color:T.muted,textTransform:"uppercase",letterSpacing:.5}}>Animais</div>
                      </div>
                      <div style={{background:T.card2,borderRadius:8,padding:"8px 10px",textAlign:"center"}}>
                        <div style={{fontSize:20,fontWeight:800,color:getBovineWeightClass(getCarcassArrobas(Number(lot.avgWeight||0), lot)).color}}>{fmtMeasure(getCarcassArrobas(Number(lot.avgWeight||0), lot),2)} @</div>
                        <div style={{fontSize:10,color:T.muted,textTransform:"uppercase",letterSpacing:.5}}>Peso médio · {fmtMeasure(getCarcassWeightKg(Number(lot.avgWeight||0), lot),1)} kg</div>
                      </div>
                    </div>

                    <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                      {ageRange&&<Badge color={T.accent}>{ageRange.icon} {ageRange.label}</Badge>}
                      {currentAgeMonths > 0 && <Badge color={T.yellow}>🗓️ {fmtMeasure(currentAgeMonths,0)} mês(es)</Badge>}
                      <Badge color={lot.sex==="Macho"?T.accent:lot.sex==="Fêmea"?T.red:T.muted}>{lot.sex==="Macho"?"♂":lot.sex==="Fêmea"?"♀":"⚥"} {lot.sex}</Badge>
                      {lot.purpose&&<Badge color={T.purple}>{lot.purpose}</Badge>}
                      {!!lot.terminationLot && <Badge color={lotReadyForSlaughter ? T.green : T.red}>🏁 Animais em Terminação</Badge>}
                    </div>

                    {lotReadyForSlaughter && (
                      <div style={{fontSize:12,fontWeight:800,color:T.green,background:`${T.green}12`,border:`1px solid ${T.green}33`,borderRadius:10,padding:"8px 10px"}}>
                        ✅ Animais Prontos Para Abate!
                      </div>
                    )}

                    {lot.pastureId&&pastureMap[lot.pastureId]&&(
                      <div style={{fontSize:12,color:T.green,fontWeight:500}}>📍 {pastureMap[lot.pastureId]}</div>
                    )}
                    {lot.feedType&&(
                      <div style={{fontSize:11,color:T.green,display:"inline-flex",alignItems:"center",gap:6}}>{animalSpeciesOf(lot)==="Ovino" ? <GenericRationBagIcon size={16} /> : <BovineRationBagIcon size={16} />} {lot.feedType}{lot.dailyFeedKg>0?` · ${lot.dailyFeedKg}kg/animal/dia`:""}</div>
                    )}
                    {indivCount>0&&(
                      <div style={{fontSize:11,color:T.muted}}>🔗 {indivCount} animal(is) vinculado(s)</div>
                    )}
                    {(["Bovino","Ovino"].includes(String(activeSpecies||"")) && canEdit) && <div style={{display:"flex",gap:8,flexWrap:"wrap"}}><Btn size="sm" variant={currentLivestockInlineLotId===String(lot.id) && !!lotNutritionForm ? "primary" : "ghost"} onClick={e=>{e.stopPropagation(); openLotNutritionEditor(lot);}}>Nutrição</Btn><Btn size="sm" variant={currentLivestockInlineLotId===String(lot.id) && !!lotMedicationForm ? "primary" : "ghost"} onClick={e=>{e.stopPropagation(); openLotMedicationEditor(lot);}}>Medicamento</Btn></div>}
                    <div style={{fontSize:10,color:T.muted}}>Formado em {fmtDate(lot.formedAt)}</div>
                  </div>
                </div>
              );
            })}
          </div>

          </>}

          {/* Lot Detail + Movements Modal */}
          {detailLot&&<LotDetailModal lot={detailLot} pastureMap={pastureMap} canEdit={canEdit} inline
            exitReasonOptions={animalExitReasons}
            entryReasonOptions={animalEntryReasons}
            buyerOptions={animalBuyers||[]}
            onCreateBuyer={createAnimalBuyer}
            onClose={closeAllLivestockInlinePanels}
            onEdit={()=>openLotEditInline(detailLot)}
            onAddMovement={addMovement}
            onIndividualize={()=>openLotIndividualization(detailLot)}
            onToggleTermination={(checked)=>{
              setLots(prev=>dedupeById((prev||[]).map(item=>String(item.id)===String(detailLot.id)?{...item, terminationLot:checked, ...makeUpdateMeta(session?.name||"")}:item)));
              setDetailLot(prev=>prev?{...prev, terminationLot:checked}:prev);
              show(checked ? "✅ Lote marcado como animais em terminação." : "✅ Lote removido de animais em terminação.");
              syncOnSave("animalLots");
            }}
            linkedAnimals={animals.filter(a=>a.lotId===detailLot.id)}
          />}
        </>
      )}

      <InlineModal theme={T} tone="warning" open={!!individualizeForm} onClose={()=>setIndividualizeForm(null)} title="Individualizar animais do lote">
        {individualizeForm && (()=>{
          const lot = getLivestockLotById(individualizeForm.lotId);
          const available = Number(lot?.quantity || 0);
          return <div style={{display:"grid",gap:12}}>
            <div style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:12,padding:12,fontSize:12,color:T.muted}}>
              <div style={{fontWeight:800,color:T.text}}>{lot?.name || 'Lote'}</div>
              <div style={{marginTop:4}}>Esta ação cria animais individuais e reduz a quantidade do lote na mesma proporção, evitando dupla contagem operacional.</div>
              <div style={{marginTop:6}}>Disponível no lote: <strong>{available}</strong> animal(is).</div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:10}}>
              <Field label="Quantidade a individualizar"><Input type="number" min="1" max={String(available||1)} value={individualizeForm.qty||1} onChange={e=>setIndividualizeForm(f=>({...f,qty:e.target.value}))}/></Field>
              <Field label="Peso inicial por animal (kg)"><Input type="number" min="0" step="0.1" value={individualizeForm.avgWeight||''} onChange={e=>setIndividualizeForm(f=>({...f,avgWeight:e.target.value}))}/></Field>
              <Field label="Prefixo do nome"><Input value={individualizeForm.namePrefix||''} onChange={e=>setIndividualizeForm(f=>({...f,namePrefix:e.target.value}))}/></Field>
              <Field label="Prefixo do brinco"><Input value={individualizeForm.tagPrefix||''} onChange={e=>setIndividualizeForm(f=>({...f,tagPrefix:e.target.value}))}/></Field>
            </div>
            <Field label="Observação"><Textarea rows={2} value={individualizeForm.notes||''} onChange={e=>setIndividualizeForm(f=>({...f,notes:e.target.value}))} placeholder="Opcional"/></Field>
            <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
              <Btn size="sm" variant="ghost" onClick={()=>setIndividualizeForm(null)}>Cancelar</Btn>
              <Btn size="sm" onClick={confirmLotIndividualization}>Individualizar</Btn>
            </div>
          </div>;
        })()}
      </InlineModal>

      <InlineModal theme={T} tone="success" open={!!lotNutritionForm} onClose={closeAllLivestockInlinePanels} title="Nutrição do lote">
        {lotNutritionForm && (()=>{ 
          const lot=getLivestockLotById(lotNutritionForm.lotId); 
          const selectedItem=bovineNutritionStockItems.find(item=>String(item.id)===String(lotNutritionForm.stockId||"")); 
          const plan=getBovineNutritionPlan(lotNutritionForm); 
          const ageDef=bovineAgeRanges.find(item=>item.id===String(lotNutritionForm.ageRange||getEffectiveLotAgeRangeId(lot))); 
          const suggestedPerHead = Number(plan?.suggestion?.profile?.dose || 0);
          const categories = bovineCategoriesForLot({ ...(lot||{}), ageRange:lotNutritionForm?.ageRange || getEffectiveLotAgeRangeId(lot), sex:lotNutritionForm?.sex || lot?.sex, purpose:lotNutritionForm?.purpose || lot?.purpose });
          const filteredItems = bovineNutritionStockItems.filter(item=>itemSupportsAnyCategory(item, "nutrition", "Bovino", categories));
          return <div style={{display:"grid",gap:14}}>
          <div style={{background:T.card2,borderRadius:12,padding:12,display:"grid",gap:4,fontSize:12}}>
            <div style={{fontWeight:800,color:T.text}}>{lot?.name || "Lote bovino"}</div>
            <div style={{color:T.muted}}>Faixa atual: <strong>{ageDef?.label || "—"}</strong> · Subcategoria liberada: <strong>{categories.join(" / ") || "—"}</strong> · Animais: <strong>{lotNutritionForm.animalCount||lot?.quantity||0}</strong> · Peso médio: <strong>{lotNutritionForm.avgWeightKg||lot?.avgWeight||0}</strong> kg</div>
          </div>
          <Field label="Ração / suplementação (Pecuária, conforme faixa etária)"><Select value={lotNutritionForm.stockId||""} onChange={e=>setLotNutritionForm(f=>{ const next={...f,stockId:e.target.value}; const nextPlan=getBovineNutritionPlan(next); if(Number(nextPlan?.suggestion?.profile?.dose||0)>0 && !String(f.consumptionPerHeadKgDay||"").trim()) next.consumptionPerHeadKgDay=String(nextPlan.suggestion.profile.dose); if(nextPlan?.qtyToLaunch!=null) next.qty=String(nextPlan.qtyToLaunch); return next; })}><option value="">Selecione</option>{filteredItems.map(item=><option key={item.id} value={item.id}>{item.name} · {item.category} · saldo {item.quantity}</option>)}</Select></Field>
          <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr 1fr",gap:10}}>
            <Field label="Faixa etária"><Select value={lotNutritionForm.ageRange||getEffectiveLotAgeRangeId(lot)} onChange={e=>setLotNutritionForm(f=>{ const next={...f,ageRange:e.target.value}; const nextPlan=getBovineNutritionPlan(next); if(nextPlan?.qtyToLaunch!=null) next.qty=String(nextPlan.qtyToLaunch); return next; })}>{bovineAgeRanges.map(item=><option key={item.id} value={item.id}>{item.label}</option>)}</Select></Field>
            <Field label="Qtd. de animais"><Input type="number" min="0" value={lotNutritionForm.animalCount||""} onChange={e=>setLotNutritionForm(f=>{ const next={...f,animalCount:e.target.value}; const nextPlan=getBovineNutritionPlan(next); if(nextPlan?.qtyToLaunch!=null) next.qty=String(nextPlan.qtyToLaunch); return next; })}/></Field>
            <Field label="Peso médio (kg)"><Input type="number" min="0" step="0.01" value={lotNutritionForm.avgWeightKg||""} onChange={e=>setLotNutritionForm(f=>({...f,avgWeightKg:e.target.value}))}/></Field>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr",gap:10}}>
            <Field label="Consumo em kg/dia por cabeça"><Input type="number" min="0" step="0.001" value={lotNutritionForm.consumptionPerHeadKgDay||""} onChange={e=>setLotNutritionForm(f=>{ const next={...f,consumptionPerHeadKgDay:e.target.value}; const nextPlan=getBovineNutritionPlan(next); if(nextPlan?.qtyToLaunch!=null) next.qty=String(nextPlan.qtyToLaunch); return next; })}/></Field>
          </div>
          {(suggestedPerHead > 0 || plan?.item) && <div style={{background:`${T.green}12`,border:`1px solid ${T.green}33`,borderRadius:12,padding:12,display:"grid",gap:8,fontSize:12}}>
            <div style={{fontWeight:700,color:T.green}}>Plano automático de consumo</div>
            {suggestedPerHead > 0 && <div style={{color:T.text}}>Sugestão base do sistema/produto: <strong>{fmtMeasure(suggestedPerHead)}</strong> kg/dia por cabeça.</div>}
            {plan?.suggestion?.formula && <div style={{color:T.muted}}>{plan.suggestion.formula}</div>}
            <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr 1fr":"repeat(4,minmax(0,1fr))",gap:8}}>
              <div style={{background:T.card,borderRadius:10,padding:10}}><div style={{fontSize:11,color:T.muted}}>Lote / dia</div><div style={{fontWeight:800,color:T.text}}>{fmtMeasure(plan?.dailyKg)} kg</div></div>
              <div style={{background:T.card,borderRadius:10,padding:10}}><div style={{fontSize:11,color:T.muted}}>Lote / semana</div><div style={{fontWeight:800,color:T.text}}>{fmtMeasure(plan?.weeklyKg)} kg</div></div>
              <div style={{background:T.card,borderRadius:10,padding:10}}><div style={{fontSize:11,color:T.muted}}>Lote / mês</div><div style={{fontWeight:800,color:T.text}}>{fmtMeasure(plan?.monthlyKg)} kg</div></div>
              <div style={{background:T.card,borderRadius:10,padding:10}}><div style={{fontSize:11,color:T.muted}}>Baixa diária arred.</div><div style={{fontWeight:800,color:T.text}}>{fmtMeasure(plan?.qtyToLaunch)} {selectedItem?.unit||"un"}</div></div>
            </div>
            <div style={{color:T.muted}}>
              {plan?.packKg ? <>Embalagem considerada: <strong>{fmtMeasure(plan.packKg)} kg</strong> por {selectedItem?.unit||"unidade"}. A baixa automática considera sempre o consumo <strong>diário</strong> do lote, arredondando por saco/unidade. </> : <>Sem referência de embalagem em kg cadastrada; a baixa será na unidade do produto. </>}
              {plan?.stockDays!=null && <>No ritmo atual, o estoque dura cerca de <strong>{fmtMeasure(plan.stockDays,1)} dias</strong>.</>}
            </div>
            {suggestedPerHead > 0 && <div><Btn size="sm" variant="ghost" onClick={()=>setLotNutritionForm(f=>{ const next={...f,consumptionPerHeadKgDay:String(suggestedPerHead)}; const nextPlan=getBovineNutritionPlan(next); return {...next, qty:String(nextPlan?.qtyToLaunch||"")}; })}>Usar consumo sugerido</Btn></div>}
          </div>}
          <Field label="Quant. a Baixar do Estoque por dia. (EDITÁVEL)"><Input type="number" min="0" step="0.001" value={lotNutritionForm.qty||""} onChange={e=>setLotNutritionForm(f=>({...f,qty:e.target.value}))}/></Field>
          {selectedItem && <div style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:12,padding:12,display:"grid",gap:8,fontSize:12}}><div style={{fontWeight:700,color:T.text}}>Pausa por item</div>{(lot?.pausedFeedItems||{})[selectedItem.id]?.paused ? <><div style={{color:T.red}}>Fornecimento pausado para este item. Motivo: <strong>{(lot?.pausedFeedItems||{})[selectedItem.id]?.reason||"—"}</strong></div><Btn size="sm" variant="ghost" onClick={()=>{ const paused={...(lot?.pausedFeedItems||{})}; delete paused[selectedItem.id]; setLots(prev=>dedupeById((prev||[]).map(entry=>String(entry.id)===String(lot.id)?{...entry,pausedFeedItems:paused,...makeUpdateMeta(session?.name||"")}:entry))); setDetailLot(prev=>prev&&String(prev.id)===String(lot.id)?{...prev,pausedFeedItems:paused}:prev); show("✅ Fornecimento reativado para este item."); syncOnSave("animalLots"); }}>Retomar fornecimento</Btn></> : <><div style={{color:T.muted}}>Você pode pausar o fornecimento deste item sem perder o plano automático.</div><div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:10}}><Field label="Motivo da pausa"><Select value={lotNutritionForm.pauseReason||""} onChange={e=>setLotNutritionForm(f=>({...f,pauseReason:e.target.value}))}><option value="">Selecione</option>{feedPauseReasons.map(reason=><option key={reason} value={reason}>{reason}</option>)}</Select></Field><Field label="Observação complementar"><Input value={lotNutritionForm.pauseNotes||""} onChange={e=>setLotNutritionForm(f=>({...f,pauseNotes:e.target.value}))} placeholder="Opcional"/></Field></div><div><Btn size="sm" variant="ghost" onClick={()=>{ if(!String(lotNutritionForm.pauseReason||"").trim()){ show("Selecione a justificativa da pausa.", "error"); return; } const paused={...(lot?.pausedFeedItems||{}), [selectedItem.id]:{ paused:true, reason:lotNutritionForm.pauseReason, notes:lotNutritionForm.pauseNotes||"", ts:new Date().toISOString(), by:session?.name||"" }}; setLots(prev=>dedupeById((prev||[]).map(entry=>String(entry.id)===String(lot.id)?{...entry,pausedFeedItems:paused,...makeUpdateMeta(session?.name||"")}:entry))); setDetailLot(prev=>prev&&String(prev.id)===String(lot.id)?{...prev,pausedFeedItems:paused}:prev); show("✅ Fornecimento pausado para este item."); syncOnSave("animalLots"); }}>Pausar fornecimento deste item</Btn></div></>}</div>}
          <Field label="Observações"><Textarea rows={2} value={lotNutritionForm.notes||""} onChange={e=>setLotNutritionForm(f=>({...f,notes:e.target.value}))}/></Field>
          <div style={{fontSize:12,color:T.muted}}>O app mantém o plano automático de consumo, mas a baixa sugerida do estoque é sempre diária e arredondada por saco/unidade. Se você alterar o consumo sugerido ou a quantidade a baixar, o sistema registra revisão pelo usuário.</div>
          <div style={{display:"flex",justifyContent:"flex-end",gap:10}}><Btn variant="ghost" onClick={closeAllLivestockInlinePanels}>Cancelar</Btn><Btn onClick={registerLotNutrition}>Salvar</Btn></div>
        </div>; })()}
      </InlineModal>

      <InlineModal theme={T} tone="warning" open={!!lotMedicationForm} onClose={closeAllLivestockInlinePanels} title="Medicamento do lote">
        {lotMedicationForm && (()=>{ 
          const lot=getLivestockLotById(lotMedicationForm.lotId); 
          const selectedItem=bovineMedicationStockItems.find(item=>String(item.id)===String(lotMedicationForm.stockId||"")); 
          const ageDef=bovineAgeRanges.find(item=>item.id===String(lotMedicationForm.ageRange||getEffectiveLotAgeRangeId(lot))); 
          const categories = bovineCategoriesForLot({ ...(lot||{}), ageRange:lotMedicationForm?.ageRange || getEffectiveLotAgeRangeId(lot), sex:lotMedicationForm?.sex || lot?.sex, purpose:lotMedicationForm?.purpose || lot?.purpose });
          const weightGuide=resolveMedicationWeightGuide({ species:"Bovino", category:categories[0]||"", sex:String(lotMedicationForm?.sex || lot?.sex || ""), lotLike:lot, typedWeightKg:lotMedicationForm?.avgWeightKg });
          const effectiveWeightKg=getMedicationEffectiveWeightKg(lotMedicationForm?.avgWeightKg, weightGuide);
          const suggestion=getBovineMedicationSuggestion({ ...lotMedicationForm, avgWeightKg:effectiveWeightKg }); 
          const filteredItems = bovineMedicationStockItems.filter(item=>itemSupportsAnyCategory(item, "medication", "Bovino", categories));
          const intervalDays = lotMedicationForm.reapplyEnabled ? Number(lotMedicationForm.reapplyPreset === "custom" ? lotMedicationForm.customIntervalDays : lotMedicationForm.reapplyPreset || 0) : 0;
          const reviewCard=buildMedicationInlineReview({ suggestion, item:selectedItem, targetLabel:"no lote", extraLines:[weightGuide?.message, weightGuide?.latestRecordedAt ? `${weightGuide.latestSource}: ${fmtMeasure(weightGuide.latestWeightKg,1)} kg em ${fmtDate(weightGuide.latestRecordedAt)}` : "", weightGuide?.averageWeightKg ? `Média da fazenda para o perfil: ${fmtMeasure(weightGuide.averageWeightKg,1)} kg` : "", categories.length ? `Perfis verificados: ${categories.join(' / ')}` : ""] });
          return <div style={{display:"grid",gap:14}}>
          <div style={{background:T.card2,borderRadius:12,padding:12,display:"grid",gap:4,fontSize:12}}>
            <div style={{fontWeight:800,color:T.text}}>{lot?.name || "Lote bovino"}</div>
            <div style={{color:T.muted}}>Faixa atual: <strong>{ageDef?.label || "—"}</strong> · Subcategoria liberada: <strong>{categories.join(" / ") || "—"}</strong> · Animais: <strong>{lotMedicationForm.animalCount||lot?.quantity||0}</strong> · Peso médio: <strong>{effectiveWeightKg||lot?.avgWeight||0}</strong> kg</div>
          </div>
          <Field label="Medicamento (Pecuária)"><Select value={lotMedicationForm.stockId||""} onChange={e=>setLotMedicationForm(f=>{ const next={...f,stockId:e.target.value}; const result=getBovineMedicationSuggestion({ ...next, avgWeightKg:getMedicationEffectiveWeightKg(next?.avgWeightKg, weightGuide) }); if(result?.qty!=null) next.qty=String(result.qty); if(result?.profile?.notes) next.dosageUsed=result.profile.notes; return next; })}><option value="">Selecione</option>{filteredItems.map(item=><option key={item.id} value={item.id}>{item.name} · saldo {item.quantity}</option>)}</Select></Field>
          <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr 1fr",gap:10}}>
            <Field label="Faixa etária"><Select value={lotMedicationForm.ageRange||getEffectiveLotAgeRangeId(lot)} onChange={e=>setLotMedicationForm(f=>{ const next={...f,ageRange:e.target.value}; const result=getBovineMedicationSuggestion({ ...next, avgWeightKg:getMedicationEffectiveWeightKg(next?.avgWeightKg, weightGuide) }); if(result?.qty!=null) next.qty=String(result.qty); if(result?.profile?.notes && !String(f.dosageUsed||"").trim()) next.dosageUsed=result.profile.notes; return next; })}>{bovineAgeRanges.map(item=><option key={item.id} value={item.id}>{item.label}</option>)}</Select></Field>
            <Field label="Qtd. de animais"><Input type="number" min="0" value={lotMedicationForm.animalCount||""} onChange={e=>setLotMedicationForm(f=>{ const next={...f,animalCount:e.target.value}; const result=getBovineMedicationSuggestion({ ...next, avgWeightKg:getMedicationEffectiveWeightKg(next?.avgWeightKg, weightGuide) }); if(result?.qty!=null) next.qty=String(result.qty); return next; })}/></Field>
            <Field label="Peso médio usado no cálculo (kg)"><Input type="number" min="0" step="0.01" value={getMedicationWeightInputValue(lotMedicationForm.avgWeightKg, weightGuide)} onChange={e=>setLotMedicationForm(f=>{ const next={...f,avgWeightKg:e.target.value}; const result=getBovineMedicationSuggestion(next); if(result?.qty!=null) next.qty=String(result.qty); return next; })}/></Field>
          </div>
          {reviewCard && <div style={{background:`${T.purple}12`,border:`2px solid ${T.purple}`,borderRadius:12,padding:12,display:"grid",gap:6,fontSize:12}}>
            <div style={{fontWeight:800,color:T.purple}}>{reviewCard.title}</div>
            <div style={{color:T.text}}>Resultado: <strong>{reviewCard.amountText}</strong></div>
            <div style={{color:T.muted}}>{reviewCard.formula}</div>
            {reviewCard.extraLines.map((line, idx)=><div key={idx} style={{color:T.muted}}>{line}</div>)}
            {reviewCard.notes && <div style={{color:T.muted}}>{reviewCard.notes}</div>}
            {reviewCard.warning && <div style={{color:T.red,fontWeight:700}}>{reviewCard.warning} Você pode confirmar o uso mesmo assim; o prontuário ficará marcado em destaque.</div>}
            <div><Btn size="sm" variant="ghost" onClick={()=>setLotMedicationForm(f=>({...f,avgWeightKg:String(effectiveWeightKg||f.avgWeightKg||""), qty:String(suggestion.qty||""), dosageUsed:suggestion?.profile?.notes||f.dosageUsed||""}))}>Usar cálculo do app</Btn></div>
          </div>}
          <Field label="Posologia utilizada"><Textarea rows={2} value={lotMedicationForm.dosageUsed||""} placeholder="Ex.: 1 mL por 10 kg, dose única" onChange={e=>setLotMedicationForm(f=>({...f,dosageUsed:e.target.value}))}/></Field>
          <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:10}}>
            <Field label="Quantidade total a baixar do estoque"><Input type="number" min="0" step="0.001" value={lotMedicationForm.qty||""} onChange={e=>setLotMedicationForm(f=>({...f,qty:e.target.value}))}/></Field>
            <Field label="Reaplicação"><label style={{display:"flex",alignItems:"center",gap:8,height:42}}><input type="checkbox" checked={!!lotMedicationForm.reapplyEnabled} onChange={e=>setLotMedicationForm(f=>({...f,reapplyEnabled:e.target.checked}))}/> Programar reaplicação</label></Field>
          </div>
          {lotMedicationForm.reapplyEnabled && <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:10}}>
            <Field label="Intervalo comum"><Select value={lotMedicationForm.reapplyPreset||"7"} onChange={e=>setLotMedicationForm(f=>({...f,reapplyPreset:e.target.value}))}>{commonBovineMedicationIntervals.map(item=><option key={item.value} value={item.value}>{item.label}</option>)}<option value="custom">Personalizado</option></Select></Field>
            {lotMedicationForm.reapplyPreset === "custom" ? <Field label="Intervalo personalizado (dias)"><Input type="number" min="1" step="1" value={lotMedicationForm.customIntervalDays||""} onChange={e=>setLotMedicationForm(f=>({...f,customIntervalDays:e.target.value}))}/></Field> : <Field label="Próxima aplicação"><Input value={intervalDays > 0 ? new Date(Date.now() + intervalDays*86400000).toLocaleDateString("pt-BR") : ""} readOnly/></Field>}
          </div>}
          <Field label="Observações"><Textarea rows={2} value={lotMedicationForm.notes||""} onChange={e=>setLotMedicationForm(f=>({...f,notes:e.target.value}))}/></Field>
          <div style={{fontSize:12,color:T.muted}}>O app destaca a dosagem calculada até você confirmar. Depois, o lançamento gera um balão com o medicamento e a quantidade aplicada no lote.</div>
          <div style={{display:"flex",justifyContent:"flex-end",gap:10}}><Btn variant="ghost" onClick={closeAllLivestockInlinePanels}>Cancelar</Btn><Btn onClick={registerLotMedication}>Confirmar uso</Btn></div>
        </div>; })()}
      </InlineModal>

      <InlineModal theme={T} tone="success" open={!!foalForm} onClose={()=>setFoalForm(null)} title={foalForm ? (foalForm._new ? "Novo Potro" : `Editar Potro · ${foalForm.name||""}`) : ""}>
        {foalForm && <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14}}>
          <Field label="Nome *"><Input value={foalForm.name||""} onChange={e=>setFoalForm(f=>({...f,name:e.target.value}))}/></Field>
          <Field label="Sexo"><Select value={foalForm.sex||"Macho"} onChange={e=>setFoalForm(f=>({...f,sex:e.target.value}))}>{ANIMAL_SEX.map(item=><option key={item}>{item}</option>)}</Select></Field>
          <Field label="Raça"><Input value={foalForm.breed||""} onChange={e=>setFoalForm(f=>({...f,breed:e.target.value}))} placeholder="Ex.: Quarto de Milha"/></Field>
          <Field label="Faixa etária"><Select value={foalForm.foalAgeRange||"0-6"} onChange={e=>setFoalForm(f=>({...f,foalAgeRange:e.target.value}))}>{foalAgeRanges.map(item=><option key={item.id} value={item.id}>{item.label}</option>)}</Select></Field>
          <Field label="Pai (plantel Haras)"><Select value={foalForm.fatherHarasId||""} onChange={e=>setFoalForm(f=>({...f,fatherHarasId:e.target.value}))}><option value="">Selecione</option>{foalParentOptions.map(item=><option key={item.id} value={item.id}>{item.name} {item.tag?`· ${item.tag}`:""}</option>)}</Select></Field>
          <Field label="Origem da mãe"><Select value={foalForm.motherSource||"haras"} onChange={e=>setFoalForm(f=>({...f,motherSource:e.target.value, motherHarasId:e.target.value==="haras"?f.motherHarasId||"":"", motherNameManual:e.target.value==="manual"?f.motherNameManual||"":""}))}><option value="haras">Plantel Haras</option><option value="manual">Informar manualmente</option></Select></Field>
          {String(foalForm.motherSource||"haras") === "haras" ? <Field label="Mãe (plantel Haras)"><Select value={foalForm.motherHarasId||""} onChange={e=>setFoalForm(f=>({...f,motherHarasId:e.target.value}))}><option value="">Selecione</option>{foalParentOptions.map(item=><option key={item.id} value={item.id}>{item.name} {item.tag?`· ${item.tag}`:""}</option>)}</Select></Field> : <Field label="Nome da mãe"><Input value={foalForm.motherNameManual||""} onChange={e=>setFoalForm(f=>({...f,motherNameManual:e.target.value}))}/></Field>}
          <Field label="Nascimento"><Input type="date" value={foalForm.born||""} onChange={e=>setFoalForm(f=>({...f,born:e.target.value}))}/></Field>
          <Field label="Observações" col="1/-1"><Textarea rows={3} value={foalForm.notes||""} onChange={e=>setFoalForm(f=>({...f,notes:e.target.value}))}/></Field>
          <div style={{gridColumn:"1/-1",display:"flex",justifyContent:"flex-end",gap:10}}><Btn variant="ghost" onClick={()=>setFoalForm(null)}>Cancelar</Btn><Btn onClick={saveFoal}>Salvar</Btn></div>
        </div>}
      </InlineModal>

      <InlineModal theme={T} tone="success" open={!!serviceAnimalForm} onClose={()=>setServiceAnimalForm(null)} title={serviceAnimalForm ? (serviceAnimalForm._new ? "Animal de Serviço" : `Editar Animal · ${serviceAnimalForm.name||""}`) : ""}>
        {serviceAnimalForm && <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14}}>
          <Field label="Modo"><Select value={serviceAnimalForm.mode||"manual"} onChange={e=>setServiceAnimalForm(f=>({...f,mode:e.target.value}))}><option value="manual">Cadastro manual</option><option value="haras">Transferir do Haras</option></Select></Field>
          <Field label="Código de serviço"><Input value={serviceAnimalForm.serviceCode||""} onChange={e=>setServiceAnimalForm(f=>({...f,serviceCode:e.target.value}))}/></Field>
          {String(serviceAnimalForm.mode||"manual") === "haras" ? <Field label="Equino do Haras *" col="1/-1"><Select value={serviceAnimalForm.harasEquineId||""} onChange={e=>{ const selected = availableHarasEquines.find(item=>String(item.id)===String(e.target.value)); setServiceAnimalForm(f=>({...f,harasEquineId:e.target.value, name:selected?.name||f.name||"", sex:selected?.sex||f.sex||"Macho", breed:selected?.breed||f.breed||"", status:selected?.status||f.status||"Ativo", tag:selected?.tag||f.tag||"" })); }}><option value="">Selecione</option>{availableHarasEquines.map(item=><option key={item.id} value={item.id}>{item.name} {item.tag?`· ${item.tag}`:""}</option>)}</Select></Field> : <Field label="Nome *" col="1/-1"><Input value={serviceAnimalForm.name||""} onChange={e=>setServiceAnimalForm(f=>({...f,name:e.target.value}))}/></Field>}
          <Field label="Sexo"><Select value={serviceAnimalForm.sex||"Macho"} onChange={e=>setServiceAnimalForm(f=>({...f,sex:e.target.value}))}>{ANIMAL_SEX.map(item=><option key={item}>{item}</option>)}</Select></Field>
          <Field label="Raça"><Input value={serviceAnimalForm.breed||""} onChange={e=>setServiceAnimalForm(f=>({...f,breed:e.target.value}))}/></Field>
          <Field label="Status"><Select value={serviceAnimalForm.status||"Ativo"} onChange={e=>setServiceAnimalForm(f=>({...f,status:e.target.value}))}>{ANIMAL_STATUS.map(item=><option key={item}>{item}</option>)}</Select></Field>
          <Field label="Brinco / Identificação"><Input value={serviceAnimalForm.tag||""} onChange={e=>setServiceAnimalForm(f=>({...f,tag:e.target.value}))}/></Field>
          <Field label="Entrar na média de nutrição?"><label style={{display:"flex",alignItems:"center",gap:8,height:42}}><input type="checkbox" checked={serviceAnimalForm.rationConsumer !== false} onChange={e=>setServiceAnimalForm(f=>({...f,rationConsumer:e.target.checked}))}/> Sim</label></Field>
          <Field label="Observações" col="1/-1"><Textarea rows={3} value={serviceAnimalForm.notes||""} onChange={e=>setServiceAnimalForm(f=>({...f,notes:e.target.value}))}/></Field>
          <div style={{gridColumn:"1/-1",display:"flex",justifyContent:"flex-end",gap:10}}><Btn variant="ghost" onClick={()=>setServiceAnimalForm(null)}>Cancelar</Btn><Btn onClick={saveServiceAnimal}>Salvar</Btn></div>
        </div>}
      </InlineModal>

      <InlineModal theme={T} tone="warning" open={!!foalMedicationForm} onClose={()=>setFoalMedicationForm(null)} title="Medicamento do potro">
        {foalMedicationForm && (()=>{ 
          const animal=foalAnimals.find(item=>String(item.id)===String(foalMedicationForm.animalId));
          const weightGuide=resolveMedicationWeightGuide({ species:"Equino", category:"Potros", sex:String(foalMedicationForm?.sex || animal?.sex || ""), animalLike:animal, typedWeightKg:foalMedicationForm?.avgWeightKg });
          const effectiveWeightKg=getMedicationEffectiveWeightKg(foalMedicationForm?.avgWeightKg, weightGuide);
          const suggestion=getEquineMedicationSuggestion({ ...foalMedicationForm, avgWeightKg:effectiveWeightKg, sex:foalMedicationForm?.sex || animal?.sex || "" }, "Potros");
          const selectedItem=equineMedicationStockItems.find(entry=>String(entry.id)===String(foalMedicationForm.stockId));
          const reviewCard=buildMedicationInlineReview({ suggestion, item:selectedItem, targetLabel:"por animal", extraLines:[weightGuide?.message, weightGuide?.latestRecordedAt ? `${weightGuide.latestSource}: ${fmtMeasure(weightGuide.latestWeightKg,1)} kg em ${fmtDate(weightGuide.latestRecordedAt)}` : "", weightGuide?.averageWeightKg ? `Média da fazenda para o perfil: ${fmtMeasure(weightGuide.averageWeightKg,1)} kg` : ""] });
          return <div style={{display:"grid",gap:14}}>
            <Field label="Item de estoque"><Select value={foalMedicationForm.stockId||""} onChange={e=>setFoalMedicationForm(f=>{ const next={...f,stockId:e.target.value}; const result=getEquineMedicationSuggestion({ ...next, avgWeightKg:getMedicationEffectiveWeightKg(next?.avgWeightKg, weightGuide), sex:next?.sex || animal?.sex || "" }, "Potros"); if(result?.qty!=null) next.qty=String(result.qty); if(result?.profile?.notes && !next.dosageUsed) next.dosageUsed=result.profile.notes; return next; })}><option value="">Selecione</option>{equineMedicationStockItems.filter(item=>itemSupportsCategory(item, "medication", "Equino", "Potros")).map(item=><option key={item.id} value={item.id}>{item.name} · {item.costCenter||"—"} · saldo {item.quantity}</option>)}</Select></Field>
            {selectedItem && <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:10,fontSize:12,background:T.card2,border:`1px solid ${T.border}`,borderRadius:12,padding:12}}><div>Categoria: <strong>{selectedItem.category||"—"}</strong></div><div>Centro origem: <strong>{selectedItem.costCenter||"—"}</strong></div><div>Unidade: <strong>{selectedItem.unit||"—"}</strong></div><div>Saldo: <strong>{selectedItem.quantity}</strong></div></div>}
            <Field label="Peso usado no cálculo (kg)"><Input type="number" min="0" step="0.01" value={getMedicationWeightInputValue(foalMedicationForm.avgWeightKg, weightGuide)} onChange={e=>setFoalMedicationForm(f=>({...f,avgWeightKg:e.target.value}))}/></Field>
            {reviewCard && <div style={{background:`${T.purple}12`,border:`2px solid ${T.purple}`,borderRadius:12,padding:12,display:"grid",gap:6,fontSize:12}}><div style={{fontWeight:800,color:T.purple}}>{reviewCard.title}</div><div style={{color:T.text}}>Resultado: <strong>{reviewCard.amountText}</strong></div><div style={{color:T.muted}}>{reviewCard.formula}</div>{reviewCard.extraLines.map((line, idx)=><div key={idx} style={{color:T.muted}}>{line}</div>)}{reviewCard.notes && <div style={{color:T.muted}}>{reviewCard.notes}</div>}{reviewCard.warning && <div style={{color:T.red,fontWeight:700}}>{reviewCard.warning} Você pode confirmar o uso mesmo assim; o prontuário ficará marcado em destaque.</div>}<div><Btn size="sm" variant="ghost" onClick={()=>setFoalMedicationForm(f=>({...f,avgWeightKg:String(effectiveWeightKg||f.avgWeightKg||""), qty:String(suggestion.qty||""), dosageUsed:suggestion?.profile?.notes||f.dosageUsed||""}))}>Usar cálculo do app</Btn></div></div>}
            <Field label="Quantidade usada"><Input type="number" min="0" step="0.01" value={foalMedicationForm.qty||""} onChange={e=>setFoalMedicationForm(f=>({...f,qty:e.target.value}))}/></Field>
            <Field label="Dosagem utilizada"><Input value={foalMedicationForm.dosageUsed||""} placeholder="Ex: 10 mL IM, 1 sachê, 0,5 mg/kg" onChange={e=>setFoalMedicationForm(f=>({...f,dosageUsed:e.target.value}))}/></Field>
            <Field label="Reaplicar?"><div style={{display:"flex",alignItems:"center",gap:10,paddingTop:8}}><input type="checkbox" checked={!!foalMedicationForm.reapplyEnabled} onChange={e=>setFoalMedicationForm(f=>({...f,reapplyEnabled:e.target.checked}))}/><span style={{fontSize:13,color:T.text}}>Gravar reaplicação com intervalo.</span></div></Field>
            {foalMedicationForm.reapplyEnabled && <><Field label="Intervalo comum"><Select value={foalMedicationForm.reapplyPreset||"7"} onChange={e=>setFoalMedicationForm(f=>({...f,reapplyPreset:e.target.value}))}>{commonEquineReapplyIntervals.map(item=><option key={item.value} value={item.value}>{item.label}</option>)}</Select></Field>{foalMedicationForm.reapplyPreset === "custom" && <Field label="Outro intervalo (dias)"><Input type="number" min="1" value={foalMedicationForm.customIntervalDays||""} onChange={e=>setFoalMedicationForm(f=>({...f,customIntervalDays:e.target.value}))}/></Field>}</>}
            <Field label="Observações"><Textarea rows={2} value={foalMedicationForm.notes||""} onChange={e=>setFoalMedicationForm(f=>({...f,notes:e.target.value}))}/></Field>
            <div style={{fontSize:12,color:T.muted}}>A dosagem fica destacada no fluxo até você confirmar. Depois, o prontuário mostra um balão com o medicamento e a quantidade usada por animal.</div>
            <div style={{display:"flex",justifyContent:"flex-end",gap:10}}><Btn variant="ghost" onClick={()=>setFoalMedicationForm(null)}>Cancelar</Btn><Btn onClick={registerFoalMedication}>Confirmar uso</Btn></div>
          </div>; })()}
      </InlineModal>

      <InlineModal theme={T} tone="warning" open={!!serviceMedicationForm} onClose={()=>setServiceMedicationForm(null)} title="Medicamento individual">
        {serviceMedicationForm && (()=>{ 
          const animal=serviceAnimals.find(item=>String(item.id)===String(serviceMedicationForm.animalId));
          const weightGuide=resolveMedicationWeightGuide({ species:"Equino", category:"Equinos Adultos", sex:String(serviceMedicationForm?.sex || animal?.sex || ""), animalLike:animal, typedWeightKg:serviceMedicationForm?.avgWeightKg });
          const effectiveWeightKg=getMedicationEffectiveWeightKg(serviceMedicationForm?.avgWeightKg, weightGuide);
          const suggestion=getEquineMedicationSuggestion({ ...serviceMedicationForm, avgWeightKg:effectiveWeightKg, sex:serviceMedicationForm?.sex || animal?.sex || "" }, "Equinos Adultos");
          const selectedItem=equineMedicationStockItems.find(entry=>String(entry.id)===String(serviceMedicationForm.stockId));
          const reviewCard=buildMedicationInlineReview({ suggestion, item:selectedItem, targetLabel:"por animal", extraLines:[weightGuide?.message, weightGuide?.latestRecordedAt ? `${weightGuide.latestSource}: ${fmtMeasure(weightGuide.latestWeightKg,1)} kg em ${fmtDate(weightGuide.latestRecordedAt)}` : "", weightGuide?.averageWeightKg ? `Média da fazenda para o perfil: ${fmtMeasure(weightGuide.averageWeightKg,1)} kg` : ""] });
          return <div style={{display:"grid",gap:14}}>
            <Field label="Item de estoque"><Select value={serviceMedicationForm.stockId||""} onChange={e=>setServiceMedicationForm(f=>{ const next={...f,stockId:e.target.value}; const result=getEquineMedicationSuggestion({ ...next, avgWeightKg:getMedicationEffectiveWeightKg(next?.avgWeightKg, weightGuide), sex:next?.sex || animal?.sex || "" }, "Equinos Adultos"); if(result?.qty!=null) next.qty=String(result.qty); if(result?.profile?.notes && !next.dosageUsed) next.dosageUsed=result.profile.notes; return next; })}><option value="">Selecione</option>{equineMedicationStockItems.filter(item=>itemSupportsCategory(item, "medication", "Equino", "Equinos Adultos")).map(item=><option key={item.id} value={item.id}>{item.name} · {item.costCenter||"—"} · saldo {item.quantity}</option>)}</Select></Field>
            {selectedItem && <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:10,fontSize:12,background:T.card2,border:`1px solid ${T.border}`,borderRadius:12,padding:12}}><div>Categoria: <strong>{selectedItem.category||"—"}</strong></div><div>Centro origem: <strong>{selectedItem.costCenter||"—"}</strong></div><div>Unidade: <strong>{selectedItem.unit||"—"}</strong></div><div>Saldo: <strong>{selectedItem.quantity}</strong></div></div>}
            <Field label="Peso usado no cálculo (kg)"><Input type="number" min="0" step="0.01" value={getMedicationWeightInputValue(serviceMedicationForm.avgWeightKg, weightGuide)} onChange={e=>setServiceMedicationForm(f=>({...f,avgWeightKg:e.target.value}))}/></Field>
            {reviewCard && <div style={{background:`${T.purple}12`,border:`2px solid ${T.purple}`,borderRadius:12,padding:12,display:"grid",gap:6,fontSize:12}}><div style={{fontWeight:800,color:T.purple}}>{reviewCard.title}</div><div style={{color:T.text}}>Resultado: <strong>{reviewCard.amountText}</strong></div><div style={{color:T.muted}}>{reviewCard.formula}</div>{reviewCard.extraLines.map((line, idx)=><div key={idx} style={{color:T.muted}}>{line}</div>)}{reviewCard.notes && <div style={{color:T.muted}}>{reviewCard.notes}</div>}{reviewCard.warning && <div style={{color:T.red,fontWeight:700}}>{reviewCard.warning} Você pode confirmar o uso mesmo assim; o prontuário ficará marcado em destaque.</div>}<div><Btn size="sm" variant="ghost" onClick={()=>setServiceMedicationForm(f=>({...f,avgWeightKg:String(effectiveWeightKg||f.avgWeightKg||""), qty:String(suggestion.qty||""), dosageUsed:suggestion?.profile?.notes||f.dosageUsed||""}))}>Usar cálculo do app</Btn></div></div>}
            <Field label="Quantidade usada"><Input type="number" min="0" step="0.01" value={serviceMedicationForm.qty||""} onChange={e=>setServiceMedicationForm(f=>({...f,qty:e.target.value}))}/></Field>
            <Field label="Dosagem utilizada"><Input value={serviceMedicationForm.dosageUsed||""} placeholder="Ex: 10 mL IM, 1 sachê, 0,5 mg/kg" onChange={e=>setServiceMedicationForm(f=>({...f,dosageUsed:e.target.value}))}/></Field>
            <Field label="Reaplicar?"><div style={{display:"flex",alignItems:"center",gap:10,paddingTop:8}}><input type="checkbox" checked={!!serviceMedicationForm.reapplyEnabled} onChange={e=>setServiceMedicationForm(f=>({...f,reapplyEnabled:e.target.checked}))}/><span style={{fontSize:13,color:T.text}}>Gravar reaplicação com intervalo.</span></div></Field>
            {serviceMedicationForm.reapplyEnabled && <><Field label="Intervalo comum"><Select value={serviceMedicationForm.reapplyPreset||"7"} onChange={e=>setServiceMedicationForm(f=>({...f,reapplyPreset:e.target.value}))}>{commonEquineReapplyIntervals.map(item=><option key={item.value} value={item.value}>{item.label}</option>)}</Select></Field>{serviceMedicationForm.reapplyPreset === "custom" && <Field label="Outro intervalo (dias)"><Input type="number" min="1" value={serviceMedicationForm.customIntervalDays||""} onChange={e=>setServiceMedicationForm(f=>({...f,customIntervalDays:e.target.value}))}/></Field>}</>}
            <Field label="Observações"><Textarea rows={2} value={serviceMedicationForm.notes||""} onChange={e=>setServiceMedicationForm(f=>({...f,notes:e.target.value}))}/></Field>
            <div style={{fontSize:12,color:T.muted}}>A dosagem fica destacada no fluxo até você confirmar. Depois, o prontuário mostra um balão com o medicamento e a quantidade usada por animal.</div>
            <div style={{display:"flex",justifyContent:"flex-end",gap:10}}><Btn variant="ghost" onClick={()=>setServiceMedicationForm(null)}>Cancelar</Btn><Btn onClick={registerServiceMedication}>Confirmar uso</Btn></div>
          </div>; })()}
      </InlineModal>

      <InlineModal theme={T} tone="success" open={!!foalNutritionForm} onClose={()=>setFoalNutritionForm(null)} title="Nutrição geral dos potros">
        {foalNutritionForm && <div style={{display:"grid",gap:14}}>
          <div style={{fontSize:13,color:T.muted}}>Potros cadastrados: <strong style={{color:T.text}}>{foalAnimals.length}</strong></div>
          <Field label="Ração do estoque (Haras)"><Select value={foalNutritionForm.stockId||""} onChange={e=>setFoalNutritionForm(f=>{ const next={...f,stockId:e.target.value}; const suggestion=getEquineNutritionSuggestion(next, "Potros", foalAnimals.length); if(suggestion?.qty!=null) next.qty=String(suggestion.qty); return next; })}><option value="">Selecione</option>{equineRationStockItems.filter(item=>itemSupportsCategory(item, "nutrition", "Equino", "Potros")).map(item=><option key={item.id} value={item.id}>{item.name} · saldo {item.quantity} · {item.costCenter||"—"}</option>)}</Select></Field>
          <Field label="Periodicidade"><Select value={foalNutritionForm.period||"Diário"} onChange={e=>setFoalNutritionForm(f=>({...f,period:e.target.value}))}>{equineNutritionPeriods.map(item=><option key={item.value} value={item.value}>{item.label}</option>)}</Select></Field>
          <Field label="Quantidade de sacos"><Input type="number" min="0" step="0.01" value={foalNutritionForm.qty||""} onChange={e=>setFoalNutritionForm(f=>({...f,qty:e.target.value}))}/></Field>
          <Field label="Observações"><Textarea rows={2} value={foalNutritionForm.notes||""} onChange={e=>setFoalNutritionForm(f=>({...f,notes:e.target.value}))}/></Field>
          <div style={{fontSize:12,color:T.muted}}>A saída do estoque usa o item do centro Haras, mas o lançamento operacional fica no centro de custo Pecuária.</div>
          <div style={{display:"flex",justifyContent:"flex-end",gap:10}}><Btn variant="ghost" onClick={()=>setFoalNutritionForm(null)}>Cancelar</Btn><Btn onClick={registerFoalNutrition}>Salvar</Btn></div>
        </div>}
      </InlineModal>

      <InlineModal theme={T} tone="success" open={!!serviceNutritionForm} onClose={()=>setServiceNutritionForm(null)} title="Nutrição geral dos animais de serviço">
        {serviceNutritionForm && <div style={{display:"grid",gap:14}}>
          <div style={{fontSize:13,color:T.muted}}>Animais marcados na média de nutrição: <strong style={{color:T.text}}>{serviceNutritionEnabledCount}</strong></div>
          <Field label="Ração do estoque (Haras)"><Select value={serviceNutritionForm.stockId||""} onChange={e=>setServiceNutritionForm(f=>{ const next={...f,stockId:e.target.value}; const suggestion=getEquineNutritionSuggestion(next, "Equinos Adultos", serviceNutritionEnabledCount); if(suggestion?.qty!=null) next.qty=String(suggestion.qty); return next; })}><option value="">Selecione</option>{equineRationStockItems.filter(item=>itemSupportsCategory(item, "nutrition", "Equino", "Equinos Adultos")).map(item=><option key={item.id} value={item.id}>{item.name} · saldo {item.quantity} · {item.costCenter||"—"}</option>)}</Select></Field>
          <Field label="Periodicidade"><Select value={serviceNutritionForm.period||"Diário"} onChange={e=>setServiceNutritionForm(f=>({...f,period:e.target.value}))}>{equineNutritionPeriods.map(item=><option key={item.value} value={item.value}>{item.label}</option>)}</Select></Field>
          <Field label="Quantidade de sacos"><Input type="number" min="0" step="0.01" value={serviceNutritionForm.qty||""} onChange={e=>setServiceNutritionForm(f=>({...f,qty:e.target.value}))}/></Field>
          <Field label="Observações"><Textarea rows={2} value={serviceNutritionForm.notes||""} onChange={e=>setServiceNutritionForm(f=>({...f,notes:e.target.value}))}/></Field>
          <div style={{fontSize:12,color:T.muted}}>A saída do estoque usa o item do centro Haras, mas o lançamento operacional fica no centro de custo Pecuária.</div>
          <div style={{display:"flex",justifyContent:"flex-end",gap:10}}><Btn variant="ghost" onClick={()=>setServiceNutritionForm(null)}>Cancelar</Btn><Btn onClick={registerServiceNutrition}>Salvar</Btn></div>
        </div>}
      </InlineModal>


    </div>
  );
}
}
